<template>
	<!-- 与 activity3-1 一致：根节点用原生 div，避免 view 在纯 H5 中为未知标签 -->
	<div
		class="page-container"
		:class="{
			'page-container--act004-mp-scroll': !showLoading && useMultiPageScroll,
			'page-container--hotspot-debug': hotspotDebugVisible
		}"
	>
		<!-- 盖章层打开时隐藏主舞台，避免关层瞬间露出源页（探索任务等） -->
		<div v-show="!showcheckInBox" class="act004-main-stage">
		<!-- 多页纵向滑动：H5 用原生 div（scroll-view 在浏览器中非组件，易导致白屏/不可滚动） -->
		<div
			v-if="!showLoading && useMultiPageScroll"
			ref="mpScrollRoot"
			class="act004-mp-scroll act004-mp-scroll--h5"
			:style="act004MpScrollStyle"
			@scroll.passive="onAct004MultiPageScroll"
			@touchend="onAct004MultiPageTouchEnd"
			@touchcancel="onAct004MultiPageTouchEnd"
		>
			<!-- 步骤1：主滚动区用原生 div（与 activity3-1 一致），逻辑同小程序 -->
			<div
				v-for="ctx in navPageBlocks"
				:key="ctx.originalIndex"
				:id="'mp-' + ctx.originalIndex"
				class="act004-mp-section"
				:style="{ minHeight: (windowHeightPx || 600) + 'px' }"
			>
				<div v-if="resolvePageBackgroundSignMode(ctx) === 'composite'" class="composite-page-root">
					<img
						v-if="resolvePageSignCompositeBanner(ctx)"
						:src="getImageUrl(resolvePageSignCompositeBanner(ctx))"
						class="composite-banner-img"
						:class="{ 'hero-image-visible': signCompositeBannerLoaded }"
						@load="onCompositeBannerLoad"
						@error="onCompositeBannerError"
					/>
					<div v-if="resolvePageSignCompositeRuleHtml(ctx)" class="composite-rules-wrap">
						<u-parse
							:content="resolvePageSignCompositeRuleHtml(ctx)"
							:scroll-table="true"
							:tag-style="richTextTagStyle"
						/>
					</div>
					<div class="composite-checkin-module">
						<div class="composite-divider-row">
							<div class="composite-divider-line"></div>
							<span class="composite-divider-text">Clock in</span>
							<div class="composite-divider-line"></div>
						</div>
						<div
							class="background-wrapper composite-sign-layer"
							:style="{ minHeight: rpxPx(compositeLayerHeightRpxForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)) }"
						>
							<img
								v-if="resolvePageCheckinBackground(ctx.page)"
								:src="getImageUrl(resolvePageCheckinBackground(ctx.page))"
								class="composite-sign-layer__bg"
								alt=""
							/>
							<div class="act004-bg-overlay-layer">
								<img
									v-for="(item, bi) in visibleBgOverlayLayersForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
									:key="'bg-ovl-' + ctx.originalIndex + '-' + bi"
									:src="getImageUrl(item.src)"
									class="act004-bg-overlay-img"
									:style="getBgOverlayLayerStyle(item.hotspot)"
									alt=""
								/>
							</div>
							<div class="act004-progress-bar-layer">
								<div
									v-for="(item, pbi) in visibleProgressBarLayersForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
									:key="'pb-' + ctx.originalIndex + '-' + pbi"
									class="act004-progress-bar-host"
									:style="getProgressBarLayerStyle(item.hotspot)"
								>
									<act004-progress-bar-layer
										:bg-src="getImageUrl(item.bgSrc)"
										:fill-src="item.fillSrc ? getImageUrl(item.fillSrc) : ''"
										:segments="item.segments"
										:progress-percent="act004ProgressBarPercentByKey[item.key] != null ? act004ProgressBarPercentByKey[item.key] : 0"
										:fill-tile-width-rpx="item.fillTileWidthRpx"
										:fill-tile-height-rpx="item.fillTileHeightRpx"
										:fill-tile-gap-rpx="item.fillTileGapRpx"
										:window-width-px="item.windowWidthPx"
										:track-inset-left="item.trackInsetLeft"
										:track-inset-top="item.trackInsetTop"
										:track-inset-right="item.trackInsetRight"
										:track-inset-bottom="item.trackInsetBottom"
									/>
								</div>
							</div>
							<div
								v-for="(hotspot, index) in sortedHotspotsForAct004Render(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
								:key="'h-' + ctx.originalIndex + '-' + index"
								class="hotspot-circle"
								:class="{
									'hotspot-circle--full-image-hotzone': isImageFullHotZoneHotspot(hotspot),
									'hotspot-circle--data': isDataHotspot(hotspot),
									'hotspot-circle--act004-img-float': isAct004ImageHotspotFloatEnabled(hotspot),
									'hotspot-circle--blocked-by-link-zone': isCheckinHotspotBlockedByLinkZone(hotspot, ctx.originalIndex)
								}"
								:style="{
									left: rpxPx((hotspot.left / 100 * 750)),
									top: rpxPx((hotspot.top / 100 * 1334)),
									width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
									height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
									borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
								}"
								@click="onAct004HotspotTap(hotspot, index, ctx.originalIndex)"
							>
								<div
									v-if="isUserFormInputHotspot(hotspot)"
									class="hotspot-user-form-input"
									@click.stop
								>
									<img
										v-if="getUserFormInputBgUrl(hotspot)"
										:src="getUserFormInputBgUrl(hotspot)"
										class="hotspot-image hotspot-user-form-input__bg"
										:style="getHotspotUserFormInputBgStyle(hotspot)"
									/>
									<input
										class="hotspot-user-form-input__control"
										:value="getUserFormDraftValue(hotspot)"
										:placeholder="userFormInputPlaceholder(hotspot)"
										:type="userFormInputType(hotspot)"
										:maxlength="userFormInputMaxLength(hotspot)"
										:style="getUserFormInputControlStyle(hotspot)"
										@input="onUserFormDraftInput(hotspot, $event)"
									/>
								</div>
								<div
									v-else-if="getHotspotImage(hotspot) && isAct004ImageHotspotFloatEnabled(hotspot)"
									class="act004-img-float-host"
									:style="getAct004ImageFloatWrapStyle(hotspot)"
								>
									<img
										:src="getHotspotImage(hotspot)"
										class="hotspot-image"
										:style="{
											width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
											height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
											borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
										}"
									/>
								</div>
								<img
									v-else-if="getHotspotImage(hotspot)"
									:src="getHotspotImage(hotspot)"
									class="hotspot-image"
									:style="{
										width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
										height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
										borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
									}"
								/>
								<div
									v-else-if="isImageFullHotZoneHotspot(hotspot)"
									class="hotspot-full-image-hit"
								/>
								<div
									v-else-if="isDataHotspot(hotspot)"
									class="hotspot-data-text"
									:style="getDataHotspotTextStyle(hotspot)"
								>
									<span class="hotspot-data-text__inner" :style="getDataHotspotTextStyle(hotspot)">{{ getDataHotspotText(hotspot) }}</span>
								</div>
								<div
									v-if="shouldShowCheckedCornerBadge(hotspot)"
									class="hotspot-checked-badge-wrap"
									:style="getHotspotCheckedBadgeBoxStyle(hotspot)"
								>
									<img
										:src="hotspotAssetUrl(hotspot.checkedBadgeImg)"
										class="hotspot-checked-badge-img"
										alt=""
									/>
								</div>
							</div>
							<div class="act004-shake-lottery-verify-layer">
								<div
									v-for="hs in getShakeLotteryPendingVerifyHotspotsForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
									:key="'shake-verify-seal-' + getShakeHotspotKey(hs)"
									class="shake-lottery-verify-seal-host"
									:style="getShakeLotteryVerifySealLayerStyle(hs)"
								>
									<div
										class="shake-lottery-verify-seal-zone"
										:id="'shakeLotteryVerifyTouchArea-' + getShakeHotspotKey(hs)"
										@touchstart.stop="handleShakeLotteryVerifyTouchStart($event, hs)"
										@touchmove.stop.prevent="handleShakeLotteryVerifyTouchMove($event, hs)"
										@touchend.stop="handleShakeLotteryVerifyTouchEnd($event)"
										@touchcancel.stop="handleShakeLotteryVerifyTouchEnd($event)"
									></div>
								</div>
							</div>
							<div class="act004-link-checkin-zone-layer">
								<div
									v-for="(zone, zi) in sortedLinkCheckinZonesForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
									:key="'link-checkin-zone-' + getAct004LinkCheckinZoneKey(zone, ctx.originalIndex, zi)"
									class="link-checkin-zone-seal-host"
									:style="getLinkCheckinZoneLayerStyle(zone)"
								>
									<div
										class="link-checkin-zone-touch-capture"
										:id="'linkCheckinZoneTouchArea-' + getAct004LinkCheckinZoneKey(zone, ctx.originalIndex, zi)"
										:data-page-index="ctx.originalIndex"
										:data-zone-zi="zi"
										@touchstart.stop="handleLinkCheckinZoneTouchStart($event, zone, ctx.originalIndex, zi)"
										@touchmove.stop.prevent="handleLinkCheckinZoneTouchMove($event, zone, ctx.originalIndex, zi)"
										@touchend.stop="handleLinkCheckinZoneTouchEnd($event, zone, ctx.originalIndex, zi)"
										@touchcancel.stop="handleLinkCheckinZoneTouchEnd($event, zone, ctx.originalIndex, zi)"
										@click.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, ctx.originalIndex, zi)"
										@mousedown.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, ctx.originalIndex, zi)"
									></div>
								</div>
							</div>
							<div
								v-if="shouldRenderQuizStudyHotspot(ctx.originalIndex)"
								class="act004-quiz-overlay-host act004-quiz-study-overlay-host"
							>
								<div
									class="act004-quiz-study-hit"
									:style="getQuizStudyHotspotStyle()"
									@click.stop="onAct004QuizStudyStart"
								/>
							</div>
							<div v-if="shouldShowQuizPageOverlayOnPage(ctx.originalIndex)" class="act004-quiz-overlay-host">
								<act004-quiz-page-overlay
									inline-anchored
									:visible="true"
									:question="act004QuizPageCurrentQuestion"
									:layout-config="act004QuizPageLayoutConfig"
									:selected-option-ids="act004QuizPageSelectedIds"
									:submitting="act004QuizPageSubmitting"
									:window-width-px="windowWidthPx"
									@toggle-option="onAct004QuizPageToggleOption"
									@submit="onAct004QuizPageSubmit"
								/>
							</div>
							<div v-if="shouldShowQuizImageOverlayOnPage(ctx.originalIndex)" class="act004-quiz-overlay-host">
								<act004-quiz-image-option-overlay
									inline-anchored
									:visible="true"
									:question="act004QuizPageCurrentQuestion"
									:layout-config="act004QuizPageLayoutConfig"
									:selected-option-ids="act004QuizPageSelectedIds"
									:submitting="act004QuizPageSubmitting"
									:window-width-px="windowWidthPx"
									@toggle-option="onAct004QuizPageToggleOption"
									@submit="onAct004QuizPageSubmit"
								/>
							</div>
						</div>
					</div>
				</div>
				<div v-else class="background-wrapper">
					<img
						:src="getImageUrl((ctx.page && ctx.page.background) || backgroundSign || '')"
						class="background-image"
						:class="{ 'hero-image-visible': signSingleBgLoaded }"
						@load="onSignSingleBackgroundLoad"
					@error="onSignSingleBackgroundError"
				/>
				<div class="act004-bg-overlay-layer">
					<img
						v-for="(item, bi) in visibleBgOverlayLayersForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
						:key="'bg-ovl-s-' + ctx.originalIndex + '-' + bi"
						:src="getImageUrl(item.src)"
						class="act004-bg-overlay-img"
						:style="getBgOverlayLayerStyle(item.hotspot)"
						alt=""
					/>
				</div>
				<div class="act004-progress-bar-layer">
					<div
						v-for="(item, pbi) in visibleProgressBarLayersForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
						:key="'pb-s-' + ctx.originalIndex + '-' + pbi"
						class="act004-progress-bar-host"
						:style="getProgressBarLayerStyle(item.hotspot)"
					>
						<act004-progress-bar-layer
							:bg-src="getImageUrl(item.bgSrc)"
							:fill-src="item.fillSrc ? getImageUrl(item.fillSrc) : ''"
							:segments="item.segments"
							:progress-percent="act004ProgressBarPercentByKey[item.key] != null ? act004ProgressBarPercentByKey[item.key] : 0"
							:fill-tile-width-rpx="item.fillTileWidthRpx"
							:fill-tile-height-rpx="item.fillTileHeightRpx"
							:fill-tile-gap-rpx="item.fillTileGapRpx"
							:window-width-px="item.windowWidthPx"
							:track-inset-left="item.trackInsetLeft"
							:track-inset-top="item.trackInsetTop"
							:track-inset-right="item.trackInsetRight"
							:track-inset-bottom="item.trackInsetBottom"
						/>
					</div>
				</div>
				<div
					v-for="(hotspot, index) in sortedHotspotsForAct004Render(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
					:key="'s-' + ctx.originalIndex + '-' + index"
						class="hotspot-circle"
						:class="{
							'hotspot-circle--full-image-hotzone': isImageFullHotZoneHotspot(hotspot),
							'hotspot-circle--data': isDataHotspot(hotspot),
							'hotspot-circle--act004-img-float': isAct004ImageHotspotFloatEnabled(hotspot),
							'hotspot-circle--blocked-by-link-zone': isCheckinHotspotBlockedByLinkZone(hotspot, ctx.originalIndex)
						}"
						:style="{
							left: rpxPx((hotspot.left / 100 * 750)),
							top: rpxPx((hotspot.top / 100 * 1334)),
							width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
							height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
							borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
						}"
						@click="onAct004HotspotTap(hotspot, index, ctx.originalIndex)"
					>
						<div
							v-if="isUserFormInputHotspot(hotspot)"
							class="hotspot-user-form-input"
							@click.stop
						>
							<img
								v-if="getUserFormInputBgUrl(hotspot)"
								:src="getUserFormInputBgUrl(hotspot)"
								class="hotspot-image hotspot-user-form-input__bg"
								:style="getHotspotUserFormInputBgStyle(hotspot)"
							/>
							<input
								class="hotspot-user-form-input__control"
								:value="getUserFormDraftValue(hotspot)"
								:placeholder="userFormInputPlaceholder(hotspot)"
								:type="userFormInputType(hotspot)"
								:maxlength="userFormInputMaxLength(hotspot)"
								:style="getUserFormInputControlStyle(hotspot)"
								@input="onUserFormDraftInput(hotspot, $event)"
							/>
						</div>
						<div
							v-else-if="getHotspotImage(hotspot) && isAct004ImageHotspotFloatEnabled(hotspot)"
							class="act004-img-float-host"
							:style="getAct004ImageFloatWrapStyle(hotspot)"
						>
							<img
								:src="getHotspotImage(hotspot)"
								class="hotspot-image"
								:style="{
									width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
									height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
									borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
								}"
							/>
						</div>
						<img
							v-else-if="getHotspotImage(hotspot)"
							:src="getHotspotImage(hotspot)"
							class="hotspot-image"
							:style="{
								width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
								height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
								borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
							}"
						/>
						<div
							v-else-if="isImageFullHotZoneHotspot(hotspot)"
							class="hotspot-full-image-hit"
						/>
						<div
							v-else-if="isDataHotspot(hotspot)"
							class="hotspot-data-text"
							:style="getDataHotspotTextStyle(hotspot)"
						>
							<span class="hotspot-data-text__inner" :style="getDataHotspotTextStyle(hotspot)">{{ getDataHotspotText(hotspot) }}</span>
						</div>
						<div
							v-if="shouldShowCheckedCornerBadge(hotspot)"
							class="hotspot-checked-badge-wrap"
							:style="getHotspotCheckedBadgeBoxStyle(hotspot)"
						>
							<img
								:src="hotspotAssetUrl(hotspot.checkedBadgeImg)"
								class="hotspot-checked-badge-img"
								alt=""
							/>
						</div>
					</div>
					<div class="act004-shake-lottery-verify-layer">
						<div
							v-for="hs in getShakeLotteryPendingVerifyHotspotsForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
							:key="'shake-verify-seal-s-' + getShakeHotspotKey(hs)"
							class="shake-lottery-verify-seal-host"
							:style="getShakeLotteryVerifySealLayerStyle(hs)"
						>
							<div
								class="shake-lottery-verify-seal-zone"
								:id="'shakeLotteryVerifyTouchArea-' + getShakeHotspotKey(hs)"
								@touchstart.stop="handleShakeLotteryVerifyTouchStart($event, hs)"
								@touchmove.stop.prevent="handleShakeLotteryVerifyTouchMove($event, hs)"
								@touchend.stop="handleShakeLotteryVerifyTouchEnd($event)"
								@touchcancel.stop="handleShakeLotteryVerifyTouchEnd($event)"
							></div>
						</div>
					</div>
					<div class="act004-link-checkin-zone-layer">
						<div
							v-for="(zone, zi) in sortedLinkCheckinZonesForPage(ctx.page && ctx.page.hotspots, ctx.originalIndex)"
							:key="'link-checkin-zone-s-' + getAct004LinkCheckinZoneKey(zone, ctx.originalIndex, zi)"
							class="link-checkin-zone-seal-host"
							:style="getLinkCheckinZoneLayerStyle(zone)"
						>
							<div
								class="link-checkin-zone-touch-capture"
								:id="'linkCheckinZoneTouchArea-' + getAct004LinkCheckinZoneKey(zone, ctx.originalIndex, zi)"
								:data-page-index="ctx.originalIndex"
								:data-zone-zi="zi"
								@touchstart.stop="handleLinkCheckinZoneTouchStart($event, zone, ctx.originalIndex, zi)"
								@touchmove.stop.prevent="handleLinkCheckinZoneTouchMove($event, zone, ctx.originalIndex, zi)"
								@touchend.stop="handleLinkCheckinZoneTouchEnd($event, zone, ctx.originalIndex, zi)"
								@touchcancel.stop="handleLinkCheckinZoneTouchEnd($event, zone, ctx.originalIndex, zi)"
								@click.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, ctx.originalIndex, zi)"
								@mousedown.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, ctx.originalIndex, zi)"
							></div>
						</div>
					</div>
					<div
						v-if="shouldRenderQuizStudyHotspot(ctx.originalIndex)"
						class="act004-quiz-overlay-host act004-quiz-study-overlay-host"
					>
						<div
							class="act004-quiz-study-hit"
							:style="getQuizStudyHotspotStyle()"
							@click.stop="onAct004QuizStudyStart"
						/>
					</div>
					<div v-if="shouldShowQuizPageOverlayOnPage(ctx.originalIndex)" class="act004-quiz-overlay-host">
						<act004-quiz-page-overlay
							inline-anchored
							:visible="true"
							:question="act004QuizPageCurrentQuestion"
							:layout-config="act004QuizPageLayoutConfig"
							:selected-option-ids="act004QuizPageSelectedIds"
							:submitting="act004QuizPageSubmitting"
							:window-width-px="windowWidthPx"
							@toggle-option="onAct004QuizPageToggleOption"
							@submit="onAct004QuizPageSubmit"
						/>
					</div>
					<div v-if="shouldShowQuizImageOverlayOnPage(ctx.originalIndex)" class="act004-quiz-overlay-host">
						<act004-quiz-image-option-overlay
							inline-anchored
							:visible="true"
							:question="act004QuizPageCurrentQuestion"
							:layout-config="act004QuizPageLayoutConfig"
							:selected-option-ids="act004QuizPageSelectedIds"
							:submitting="act004QuizPageSubmitting"
							:window-width-px="windowWidthPx"
							@toggle-option="onAct004QuizPageToggleOption"
							@submit="onAct004QuizPageSubmit"
						/>
					</div>
				</div>
			</div>
		</div>
		<!-- 单页：无纵向多节 或 从顺序流跳转到无 displayOrder 的页 -->
		<template v-else-if="!showLoading">
			<div v-if="resolvePageBackgroundSignMode(effectiveAct004SignCtx) === 'composite'" class="composite-page-root">
				<img
					v-if="resolvePageSignCompositeBanner(effectiveAct004SignCtx)"
					:src="getImageUrl(resolvePageSignCompositeBanner(effectiveAct004SignCtx))"
					class="composite-banner-img"
					:class="{ 'hero-image-visible': signCompositeBannerLoaded }"
					@load="onCompositeBannerLoad"
					@error="onCompositeBannerError"
				/>
				<div v-if="resolvePageSignCompositeRuleHtml(effectiveAct004SignCtx)" class="composite-rules-wrap">
					<u-parse
						:content="resolvePageSignCompositeRuleHtml(effectiveAct004SignCtx)"
						:scroll-table="true"
						:tag-style="richTextTagStyle"
					/>
				</div>
				<div class="composite-checkin-module">
					<div class="composite-divider-row">
						<div class="composite-divider-line"></div>
						<span class="composite-divider-text">Clock in</span>
						<div class="composite-divider-line"></div>
					</div>
					<div
						class="background-wrapper composite-sign-layer"
						:style="{ minHeight: rpxPx(compositeCheckinLayerHeightRpx) }"
					>
						<img
							v-if="resolvePageCheckinBackground(effectiveAct004SignCtx && effectiveAct004SignCtx.page)"
							:src="getImageUrl(resolvePageCheckinBackground(effectiveAct004SignCtx && effectiveAct004SignCtx.page))"
							class="composite-sign-layer__bg"
							alt=""
						/>
						<div class="act004-bg-overlay-layer">
							<img
								v-for="(item, bi) in visibleBgOverlayLayersForPage(pages[currentPageIndex] && pages[currentPageIndex].hotspots, currentPageIndex)"
								:key="'bg-ovl-c-' + currentPageIndex + '-' + bi"
								:src="getImageUrl(item.src)"
								class="act004-bg-overlay-img"
								:style="getBgOverlayLayerStyle(item.hotspot)"
								alt=""
							/>
						</div>
						<div class="act004-progress-bar-layer">
							<div
								v-for="(item, pbi) in visibleProgressBarLayersForPage(pages[currentPageIndex] && pages[currentPageIndex].hotspots, currentPageIndex)"
								:key="'pb-c-' + currentPageIndex + '-' + pbi"
								class="act004-progress-bar-host"
								:style="getProgressBarLayerStyle(item.hotspot)"
							>
								<act004-progress-bar-layer
									:bg-src="getImageUrl(item.bgSrc)"
									:fill-src="item.fillSrc ? getImageUrl(item.fillSrc) : ''"
									:segments="item.segments"
									:progress-percent="act004ProgressBarPercentByKey[item.key] != null ? act004ProgressBarPercentByKey[item.key] : 0"
									:fill-tile-width-rpx="item.fillTileWidthRpx"
									:fill-tile-height-rpx="item.fillTileHeightRpx"
									:fill-tile-gap-rpx="item.fillTileGapRpx"
									:window-width-px="item.windowWidthPx"
									:track-inset-left="item.trackInsetLeft"
									:track-inset-top="item.trackInsetTop"
									:track-inset-right="item.trackInsetRight"
									:track-inset-bottom="item.trackInsetBottom"
								/>
							</div>
						</div>
						<div
							v-for="(hotspot, index) in hotspots"
							:key="index"
							class="hotspot-circle"
							:class="{
								'hotspot-circle--full-image-hotzone': isImageFullHotZoneHotspot(hotspot),
								'hotspot-circle--data': isDataHotspot(hotspot),
								'hotspot-circle--act004-img-float': isAct004ImageHotspotFloatEnabled(hotspot),
								'hotspot-circle--blocked-by-link-zone': isCheckinHotspotBlockedByLinkZone(hotspot, currentPageIndex)
							}"
							:style="{
								left: rpxPx((hotspot.left / 100 * 750)),
								top: rpxPx((hotspot.top / 100 * 1334)),
								width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
								height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
								borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
							}"
							@click="onAct004HotspotTap(hotspot, index)"
						>
							<div
								v-if="isUserFormInputHotspot(hotspot)"
								class="hotspot-user-form-input"
								@click.stop
							>
								<img
									v-if="getUserFormInputBgUrl(hotspot)"
									:src="getUserFormInputBgUrl(hotspot)"
									class="hotspot-image hotspot-user-form-input__bg"
									:style="getHotspotUserFormInputBgStyle(hotspot)"
								/>
								<input
									class="hotspot-user-form-input__control"
									:value="getUserFormDraftValue(hotspot)"
									:placeholder="userFormInputPlaceholder(hotspot)"
									:type="userFormInputType(hotspot)"
									:maxlength="userFormInputMaxLength(hotspot)"
									:style="getUserFormInputControlStyle(hotspot)"
									@input="onUserFormDraftInput(hotspot, $event)"
								/>
							</div>
							<div
								v-else-if="getHotspotImage(hotspot) && isAct004ImageHotspotFloatEnabled(hotspot)"
								class="act004-img-float-host"
								:style="getAct004ImageFloatWrapStyle(hotspot)"
							>
								<img
									:src="getHotspotImage(hotspot)"
									class="hotspot-image"
									:style="{
										width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
										height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
										borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
									}"
								/>
							</div>
							<img
								v-else-if="getHotspotImage(hotspot)"
								:src="getHotspotImage(hotspot)"
								class="hotspot-image"
								:style="{
									width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
									height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
									borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
								}"
							/>
							<div
								v-else-if="isImageFullHotZoneHotspot(hotspot)"
								class="hotspot-full-image-hit"
							/>
							<div
								v-else-if="isDataHotspot(hotspot)"
								class="hotspot-data-text"
								:style="getDataHotspotTextStyle(hotspot)"
							>
								<span class="hotspot-data-text__inner" :style="getDataHotspotTextStyle(hotspot)">{{ getDataHotspotText(hotspot) }}</span>
							</div>
							<div
								v-if="shouldShowCheckedCornerBadge(hotspot)"
								class="hotspot-checked-badge-wrap"
								:style="getHotspotCheckedBadgeBoxStyle(hotspot)"
							>
								<img
									:src="hotspotAssetUrl(hotspot.checkedBadgeImg)"
									class="hotspot-checked-badge-img"
									alt=""
								/>
							</div>
						</div>
						<div class="act004-shake-lottery-verify-layer">
							<div
								v-for="hs in getShakeLotteryPendingVerifyHotspotsForPage(hotspots, currentPageIndex)"
								:key="'shake-verify-seal-c-' + getShakeHotspotKey(hs)"
								class="shake-lottery-verify-seal-host"
								:style="getShakeLotteryVerifySealLayerStyle(hs)"
							>
								<div
									class="shake-lottery-verify-seal-zone"
									:id="'shakeLotteryVerifyTouchArea-' + getShakeHotspotKey(hs)"
									@touchstart.stop="handleShakeLotteryVerifyTouchStart($event, hs)"
									@touchmove.stop.prevent="handleShakeLotteryVerifyTouchMove($event, hs)"
									@touchend.stop="handleShakeLotteryVerifyTouchEnd($event)"
									@touchcancel.stop="handleShakeLotteryVerifyTouchEnd($event)"
								></div>
							</div>
						</div>
						<div class="act004-link-checkin-zone-layer">
							<div
								v-for="(zone, zi) in sortedLinkCheckinZonesForPage(pages[currentPageIndex] && pages[currentPageIndex].hotspots, currentPageIndex)"
								:key="'link-checkin-zone-c-' + getAct004LinkCheckinZoneKey(zone, currentPageIndex, zi)"
								class="link-checkin-zone-seal-host"
								:style="getLinkCheckinZoneLayerStyle(zone)"
							>
								<div
									class="link-checkin-zone-touch-capture"
									:id="'linkCheckinZoneTouchArea-' + getAct004LinkCheckinZoneKey(zone, currentPageIndex, zi)"
									:data-page-index="currentPageIndex"
									:data-zone-zi="zi"
									@touchstart.stop="handleLinkCheckinZoneTouchStart($event, zone, currentPageIndex, zi)"
									@touchmove.stop.prevent="handleLinkCheckinZoneTouchMove($event, zone, currentPageIndex, zi)"
									@touchend.stop="handleLinkCheckinZoneTouchEnd($event, zone, currentPageIndex, zi)"
									@touchcancel.stop="handleLinkCheckinZoneTouchEnd($event, zone, currentPageIndex, zi)"
									@click.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, currentPageIndex, zi)"
									@mousedown.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, currentPageIndex, zi)"
								></div>
							</div>
						</div>
						<div
							v-if="shouldRenderQuizStudyHotspot(currentPageIndex)"
							class="act004-quiz-overlay-host act004-quiz-study-overlay-host"
						>
							<div
								class="act004-quiz-study-hit"
								:style="getQuizStudyHotspotStyle()"
								@click.stop="onAct004QuizStudyStart"
							/>
						</div>
						<div v-if="shouldShowQuizPageOverlayOnPage(currentPageIndex)" class="act004-quiz-overlay-host">
							<act004-quiz-page-overlay
								inline-anchored
								:visible="true"
								:question="act004QuizPageCurrentQuestion"
								:layout-config="act004QuizPageLayoutConfig"
								:selected-option-ids="act004QuizPageSelectedIds"
								:submitting="act004QuizPageSubmitting"
								:window-width-px="windowWidthPx"
								@toggle-option="onAct004QuizPageToggleOption"
								@submit="onAct004QuizPageSubmit"
							/>
						</div>
						<div v-if="shouldShowQuizImageOverlayOnPage(currentPageIndex)" class="act004-quiz-overlay-host">
							<act004-quiz-image-option-overlay
								inline-anchored
								:visible="true"
								:question="act004QuizPageCurrentQuestion"
								:layout-config="act004QuizPageLayoutConfig"
								:selected-option-ids="act004QuizPageSelectedIds"
								:submitting="act004QuizPageSubmitting"
								:window-width-px="windowWidthPx"
								@toggle-option="onAct004QuizPageToggleOption"
								@submit="onAct004QuizPageSubmit"
							/>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="background-wrapper">
				<img
					:src="getImageUrl(backgroundSign || '')"
					class="background-image"
					:class="{ 'hero-image-visible': signSingleBgLoaded }"
					@load="onSignSingleBackgroundLoad"
					@error="onSignSingleBackgroundError"
				/>
				<div class="act004-bg-overlay-layer">
					<img
						v-for="(item, bi) in visibleBgOverlayLayersForPage((pages[currentPageIndex] && pages[currentPageIndex].hotspots) || hotspots, currentPageIndex)"
						:key="'bg-ovl-p-' + currentPageIndex + '-' + bi"
						:src="getImageUrl(item.src)"
						class="act004-bg-overlay-img"
						:style="getBgOverlayLayerStyle(item.hotspot)"
						alt=""
					/>
				</div>
				<div class="act004-progress-bar-layer">
					<div
						v-for="(item, pbi) in visibleProgressBarLayersForPage((pages[currentPageIndex] && pages[currentPageIndex].hotspots) || hotspots, currentPageIndex)"
						:key="'pb-p-' + currentPageIndex + '-' + pbi"
						class="act004-progress-bar-host"
						:style="getProgressBarLayerStyle(item.hotspot)"
					>
						<act004-progress-bar-layer
							:bg-src="getImageUrl(item.bgSrc)"
							:fill-src="item.fillSrc ? getImageUrl(item.fillSrc) : ''"
							:segments="item.segments"
							:progress-percent="act004ProgressBarPercentByKey[item.key] != null ? act004ProgressBarPercentByKey[item.key] : 0"
							:fill-tile-width-rpx="item.fillTileWidthRpx"
							:fill-tile-height-rpx="item.fillTileHeightRpx"
							:fill-tile-gap-rpx="item.fillTileGapRpx"
							:window-width-px="item.windowWidthPx"
							:track-inset-left="item.trackInsetLeft"
							:track-inset-top="item.trackInsetTop"
							:track-inset-right="item.trackInsetRight"
							:track-inset-bottom="item.trackInsetBottom"
						/>
					</div>
				</div>
				<div
					v-for="(hotspot, index) in hotspots"
					:key="index"
					class="hotspot-circle"
							:class="{
								'hotspot-circle--full-image-hotzone': isImageFullHotZoneHotspot(hotspot),
								'hotspot-circle--data': isDataHotspot(hotspot),
								'hotspot-circle--act004-img-float': isAct004ImageHotspotFloatEnabled(hotspot),
								'hotspot-circle--blocked-by-link-zone': isCheckinHotspotBlockedByLinkZone(hotspot, currentPageIndex)
							}"
					:style="{
						left: rpxPx((hotspot.left / 100 * 750)),
						top: rpxPx((hotspot.top / 100 * 1334)),
						width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
						height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
						borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
					}"
					@click="onAct004HotspotTap(hotspot, index)"
				>
					<div
						v-if="isUserFormInputHotspot(hotspot)"
						class="hotspot-user-form-input"
						@click.stop
					>
						<img
							v-if="getUserFormInputBgUrl(hotspot)"
							:src="getUserFormInputBgUrl(hotspot)"
							class="hotspot-image hotspot-user-form-input__bg"
							:style="getHotspotUserFormInputBgStyle(hotspot)"
						/>
						<input
							class="hotspot-user-form-input__control"
							:value="getUserFormDraftValue(hotspot)"
							:placeholder="userFormInputPlaceholder(hotspot)"
							:type="userFormInputType(hotspot)"
							:maxlength="userFormInputMaxLength(hotspot)"
							:style="getUserFormInputControlStyle(hotspot)"
							@input="onUserFormDraftInput(hotspot, $event)"
						/>
					</div>
					<div
						v-else-if="getHotspotImage(hotspot) && isAct004ImageHotspotFloatEnabled(hotspot)"
						class="act004-img-float-host"
						:style="getAct004ImageFloatWrapStyle(hotspot)"
					>
						<img
							:src="getHotspotImage(hotspot)"
							class="hotspot-image"
							:style="{
								width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
								height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
								borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
							}"
						/>
					</div>
					<img
						v-else-if="getHotspotImage(hotspot)"
						:src="getHotspotImage(hotspot)"
						class="hotspot-image"
						:style="{
							width: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size) : (hotspot.radius || hotspot.size))),
							height: rpxPx((hotspot.shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size) : (hotspot.radius || hotspot.size))),
							borderRadius: hotspot.shape === 'rectangle' ? '0' : '50%'
						}"
					/>
					<div
						v-else-if="isImageFullHotZoneHotspot(hotspot)"
						class="hotspot-full-image-hit"
					/>
					<div
						v-else-if="isDataHotspot(hotspot)"
						class="hotspot-data-text"
						:style="getDataHotspotTextStyle(hotspot)"
					>
								<span class="hotspot-data-text__inner" :style="getDataHotspotTextStyle(hotspot)">{{ getDataHotspotText(hotspot) }}</span>
					</div>
					<div
						v-if="shouldShowCheckedCornerBadge(hotspot)"
						class="hotspot-checked-badge-wrap"
						:style="getHotspotCheckedBadgeBoxStyle(hotspot)"
					>
						<img
							:src="hotspotAssetUrl(hotspot.checkedBadgeImg)"
							class="hotspot-checked-badge-img"
							alt=""
						/>
					</div>
				</div>
				<div class="act004-shake-lottery-verify-layer">
					<div
						v-for="hs in getShakeLotteryPendingVerifyHotspotsForPage(hotspots, currentPageIndex)"
						:key="'shake-verify-seal-sp-' + getShakeHotspotKey(hs)"
						class="shake-lottery-verify-seal-host"
						:style="getShakeLotteryVerifySealLayerStyle(hs)"
					>
						<div
							class="shake-lottery-verify-seal-zone"
							:id="'shakeLotteryVerifyTouchArea-' + getShakeHotspotKey(hs)"
							@touchstart.stop="handleShakeLotteryVerifyTouchStart($event, hs)"
							@touchmove.stop.prevent="handleShakeLotteryVerifyTouchMove($event, hs)"
							@touchend.stop="handleShakeLotteryVerifyTouchEnd($event)"
							@touchcancel.stop="handleShakeLotteryVerifyTouchEnd($event)"
						></div>
					</div>
				</div>
				<div class="act004-link-checkin-zone-layer">
					<div
						v-for="(zone, zi) in sortedLinkCheckinZonesForPage(pages[currentPageIndex] && pages[currentPageIndex].hotspots, currentPageIndex)"
						:key="'link-checkin-zone-sp-' + getAct004LinkCheckinZoneKey(zone, currentPageIndex, zi)"
						class="link-checkin-zone-seal-host"
						:style="getLinkCheckinZoneLayerStyle(zone)"
					>
						<div
									class="link-checkin-zone-touch-capture"
							:id="'linkCheckinZoneTouchArea-' + getAct004LinkCheckinZoneKey(zone, currentPageIndex, zi)"
							:data-page-index="currentPageIndex"
							:data-zone-zi="zi"
							@touchstart.stop="handleLinkCheckinZoneTouchStart($event, zone, currentPageIndex, zi)"
							@touchmove.stop.prevent="handleLinkCheckinZoneTouchMove($event, zone, currentPageIndex, zi)"
							@touchend.stop="handleLinkCheckinZoneTouchEnd($event, zone, currentPageIndex, zi)"
							@touchcancel.stop="handleLinkCheckinZoneTouchEnd($event, zone, currentPageIndex, zi)"
							@click.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, currentPageIndex, zi)"
							@mousedown.stop.prevent="onLinkCheckinZonePointerBlock($event, zone, currentPageIndex, zi)"
						></div>
					</div>
				</div>
				<div
					v-if="shouldRenderQuizStudyHotspot(currentPageIndex)"
					class="act004-quiz-overlay-host act004-quiz-study-overlay-host"
				>
					<div
						class="act004-quiz-study-hit"
						:style="getQuizStudyHotspotStyle()"
						@click.stop="onAct004QuizStudyStart"
					/>
				</div>
				<div v-if="shouldShowQuizPageOverlayOnPage(currentPageIndex)" class="act004-quiz-overlay-host">
					<act004-quiz-page-overlay
						inline-anchored
						:visible="true"
						:question="act004QuizPageCurrentQuestion"
						:layout-config="act004QuizPageLayoutConfig"
						:selected-option-ids="act004QuizPageSelectedIds"
						:submitting="act004QuizPageSubmitting"
						:window-width-px="windowWidthPx"
						@toggle-option="onAct004QuizPageToggleOption"
						@submit="onAct004QuizPageSubmit"
					/>
				</div>
				<div v-if="shouldShowQuizImageOverlayOnPage(currentPageIndex)" class="act004-quiz-overlay-host">
					<act004-quiz-image-option-overlay
						inline-anchored
						:visible="true"
						:question="act004QuizPageCurrentQuestion"
						:layout-config="act004QuizPageLayoutConfig"
						:selected-option-ids="act004QuizPageSelectedIds"
						:submitting="act004QuizPageSubmitting"
						:window-width-px="windowWidthPx"
						@toggle-option="onAct004QuizPageToggleOption"
						@submit="onAct004QuizPageSubmit"
					/>
				</div>
			</div>
		</template>
		</div>

    <!-- 步骤2：盖章 overlay — view→div，与 activity3-1 一致 -->
    <div
      v-if="showcheckInBox"
      class="checkin-overlay"
      :class="{ 'checkin-overlay--internal-page': currentCheckInIsInternalPage }"
      @click="handleOverlayClick"
    >
      <div v-if="currentCheckInIsInternalPage" class="checkin-internal-page" @click.stop>
        <div class="checkin-internal-page-body">
          <img
            v-if="currentCheckInBackgroundImg"
            :src="currentCheckInBackgroundImg"
            alt=""
            class="checkin-internal-page-bg"
            @load="measureCheckInBackgroundRatio(currentCheckInBackgroundImg)"
          />
          <div
            v-if="!checkInInternalSealLoading && checkInInternalPageHotspotItems.length"
            class="checkin-internal-page-hotspots"
          >
            <div
              v-for="item in checkInInternalPageHotspotItems"
              :key="'checkin-internal-hs-' + item.listIndex"
              class="hotspot-circle checkin-internal-page-hotspot"
              :style="getHotspotContainerStyle(item.hotspot)"
              @click.stop="onCheckInInternalPageHotspotClick(item.hotspot)"
            >
              <img
                v-if="getHotspotImage(item.hotspot)"
                :src="getHotspotImage(item.hotspot)"
                class="hotspot-image"
                :style="getHotspotImageStyle(item.hotspot)"
                alt=""
              />
              <div
                v-else
                class="hotspot-full-image-hit"
                :style="getHotspotImageStyle(item.hotspot)"
              ></div>
            </div>
          </div>
          <div class="checkin-internal-page-stage">
            <div
              class="canvas-wrapper canvas-wrapper--with-bg"
              id="checkinTouchArea"
              :style="checkInCanvasWrapperStyle"
            >
              <canvas
                ref="canvas"
                canvas-id="checkinCanvas"
                id="checkinCanvas"
                class="checkin-canvas"
                :style="checkinCanvasOuterStyle"
                :width="canvasPixelSize"
                :height="canvasPixelSize"
              ></canvas>
              <div
                v-if="!checkInInternalSealLoading"
                class="touch-capture-layer"
                @touchstart.stop="handleTouchStart"
                @touchmove.stop.prevent="handleTouchMove"
                @touchend.stop="handleTouchEnd"
                @touchcancel.stop="handleTouchEnd"
              ></div>
            </div>
            <div v-if="!checkInInternalSealLoading" class="checkin-hint checkin-hint--internal-page">
              <span>虚线框内为盖章区域</span>
              <span
                v-if="canNavigateCurrentHotspot"
                class="checkin-nav-link"
                @click.stop="openCurrentHotspotLocation"
              >导航</span>
            </div>
          </div>
          <div v-if="checkInInternalSealLoading" class="checkin-internal-seal-loading">
            <div class="loading-content" @click.stop>
              <div class="brand-logo fcc seal-loading">
                <div class="seal-circle-outer">
                  <img class="circle" :src="getImageUrl('8bg.png')" alt="" />
                </div>
                <div class="seal-logo-inner">
                  <img :src="currentSealLoadingCenterImg" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="fcc-h checkin-pop" @click.stop>
        <!-- 使用 div 捕获触摸（canvas 在部分机型触摸不触发） -->
        <div class="canvas-container" :class="{ 'canvas-container--with-bg': !!currentCheckInBackgroundImg }" :style="checkInPopupStyle">
          <img
            v-if="currentCheckInBackgroundImg"
            :src="currentCheckInBackgroundImg"
            alt=""
            class="checkin-popup-bg"
          />
          <div
            class="canvas-wrapper"
            :class="{ 'canvas-wrapper--with-bg': !!currentCheckInBackgroundImg }"
            id="checkinTouchArea"
            :style="checkInCanvasWrapperStyle"
          >
            <canvas
              ref="canvas"
              canvas-id="checkinCanvas"
              id="checkinCanvas"
              class="checkin-canvas"
              :style="checkinCanvasOuterStyle"
              :width="canvasPixelSize"
              :height="canvasPixelSize"
            ></canvas>
            <div
              class="touch-capture-layer"
              @touchstart.stop="handleTouchStart"
              @touchmove.stop.prevent="handleTouchMove"
              @touchend.stop="handleTouchEnd"
              @touchcancel.stop="handleTouchEnd"
            ></div>
          </div>
          <div class="checkin-hint">
            <span>{{ currentCheckInBackgroundImg ? '虚线框内为盖章区域' : '盖章区域' }}</span>
            <span
              v-if="canNavigateCurrentHotspot"
              class="checkin-nav-link"
              @click.stop="openCurrentHotspotLocation"
            >导航</span>
          </div>
        </div>
      </div>
    </div>
    <!-- 步骤3：加载 / 首屏挡层 / 盖章转圈 -->
    <div v-if="showLoading" class="loading-overlay">
      <div class="fcc-h loading-wrapper" style="height: 100vh;">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-logo">
            <img :src="currentPageLoadingCenterImg" alt="" />
          </div>
        </div>
        <div class="loading-text">加载中...</div>
      </div>
    </div>
    <div v-else-if="showBgHold" class="loading-overlay">
      <div class="fcc-h loading-wrapper" style="height: 100vh;">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-ring"></div>
          <div class="spinner-logo">
            <img :src="currentPageLoadingCenterImg" alt="" />
          </div>
        </div>
        <div class="loading-text">加载中...</div>
      </div>
    </div>

    <div v-if="load && !checkInInternalPageModeLocked" class="loading-overlay" @click="load = false">
      <div class="loading-content" @click.stop>
        <div class="brand-logo fcc seal-loading">
          <div class="seal-circle-outer">
            <img class="circle" :src="getImageUrl('8bg.png')" alt="" />
          </div>
          <div class="seal-logo-inner">
            <img :src="currentSealLoadingCenterImg" alt="" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showShakeLotteryDrawingHint" class="shake-lottery-drawing-hint">
      <span class="shake-lottery-drawing-hint__text">抽奖中...</span>
    </div>

    <div v-if="showHotspotImagePreview" class="hotspot-image-preview-overlay" @click="hideHotspotImagePreview">
      <div class="hotspot-image-preview-container" @click="hideHotspotImagePreview">
        <div
          class="hotspot-preview-image-wrapper"
          :class="{ 'hotspot-preview-image-wrapper--link-success': linkCheckinSuccessPopupActive }"
          :style="linkCheckinSuccessPopupBoxStyle"
          @click.stop
        >
          <img
            v-if="previewHotspotImage"
            :src="previewHotspotImage"
            class="hotspot-preview-image"
            :class="{ 'hotspot-preview-image--link-success': linkCheckinSuccessPopupActive }"
            alt=""
            @load="handleImageLoad"
            @error="handleImageError"
          />
          <div
            v-for="(nested, nestedIndex) in previewHotspotNestedHotspots"
            :key="'nested-' + nestedIndex"
            :class="['hotspot-preview-nested', { 'hotspot-preview-nested--checkinhotspot': String(nested && nested.type ? nested.type : '').toLowerCase() === 'checkinhotspot' }]"
            :style="{
              left: nested.left + '%',
              top: nested.top + '%',
              width: nested.width + '%',
              height: nested.height + '%'
            }"
            @click.stop="handlePreviewNestedHotspotClick(nested)"
          />
        </div>
        <div v-if="!previewHotspotImage" class="preview-placeholder-text">图片加载中...</div>
      </div>
    </div>
    <!-- 步骤4：富文本弹层、收货弹层 -->
    <div v-if="showHotspotRichTextDialog" class="hotspot-richtext-overlay" @click="closeHotspotRichTextDialog">
      <div class="hotspot-richtext-panel" @click.stop>
        <div class="hotspot-richtext-title">{{ hotspotRichTextTitle }}</div>
        <div class="hotspot-richtext-content-wrap hotspot-richtext-content-wrap--h5">
          <u-parse
            :content="hotspotRichTextContent"
            :scroll-table="true"
            :tag-style="richTextTagStyle"
          />
        </div>
        <div class="hotspot-richtext-actions">
          <button type="button" class="hotspot-richtext-btn" @click="closeHotspotRichTextDialog">我知道了</button>
        </div>
      </div>
    </div>
    <div v-if="showShippingDialog" class="shipping-dialog-overlay" @click="closeShippingDialog">
      <div class="shipping-dialog-panel" @click.stop>
        <div class="shipping-dialog-title">填写收货信息</div>
        <div class="shipping-dialog-desc">用于核销后奖品发放，请确保信息准确。</div>

        <input v-model="shippingForm.receiverName" :disabled="shippingReadonly" class="shipping-input" placeholder="姓名" />
        <input v-model="shippingForm.receiverPhone" :disabled="shippingReadonly" class="shipping-input" type="number" maxlength="11" placeholder="手机号" />
        <template v-if="!shippingReadonly">
          <div class="shipping-region-row">
            <input v-model="shippingForm.province" class="shipping-input shipping-region-part" placeholder="省" />
            <input v-model="shippingForm.city" class="shipping-input shipping-region-part" placeholder="市" />
            <input v-model="shippingForm.district" class="shipping-input shipping-region-part" placeholder="区" />
          </div>
        </template>
        <div v-else class="shipping-picker shipping-picker-disabled">{{ shippingRegionText }}</div>
        <textarea v-model="shippingForm.address" :disabled="shippingReadonly" class="shipping-textarea" maxlength="200" placeholder="详细地址" />
        <div v-if="shippingReadonly" class="shipping-readonly-tip">该核销点位收货信息已保存，不能修改。</div>

        <div class="shipping-actions">
          <button type="button" class="shipping-btn shipping-btn-cancel" @click="closeShippingDialog">暂不填写</button>
          <button
            type="button"
            class="shipping-btn shipping-btn-confirm"
            :class="{ 'shipping-btn-disabled': shippingReadonly || !isShippingFormComplete || shippingSaving }"
            :disabled="shippingReadonly || !isShippingFormComplete || shippingSaving"
            @click="submitShippingInfo"
          >
            {{ shippingSaving ? '保存中...' : '确认保存' }}
          </button>
        </div>
      </div>
    </div>
    <act004-quiz-dialog
      :visible="act004QuizDialogVisible"
      :activity-user-id="activityUserId"
      :activity-code="activityCode"
      :page-index="act004QuizSessionPageIndex"
      :point-index="act004QuizPointIndex"
      :quiz-payload="act004QuizPayload"
      @success="onAct004QuizSuccess"
      @close="onAct004QuizClose"
    />
    <authorization-confirmation
      ref="authConfirm"
      :require-phone="phoneAuthRequired"
      :require-nickname="nicknameAuthRequired"
      :require-staff="false"
      @registerSuccess="onAuthConfirmSuccess"
    />
    <div class="activity-multi-audio-offscreen" aria-hidden="true">
      <audio id="audioID" ref="audio" class="aud">
        <source src="http://cscec.warnny.com/duang.mp3" type="audio/mp3" />
      </audio>
    </div>
	</div>
</template>

<script>
import {
  getSignStoreActivityDetailByCode,
  getSignStoreActivityDetail,
  validateSeal,
  validateSealForAct004,
  getOrCreateUserRecord,
  getOrCreateUserRecordH5,
  h5AnonymousSilentLogin,
  getCheckedPoints,
  getShippingInfo,
  saveShippingInfo,
  getFirstTemplatePoints,
  h5ThirdPartSilentLogin,
  getAct004UserFormProfile,
  submitAct004UserForm,
  getAct004CheckinLimitStatusDetail,
  getAct004CheckinLimitStatusDetailBatch,
  startAct004QuizSession,
  submitAct004QuizAnswer,
  getActivityAvailablePoints,
  getCheckinPointRank,
  drawSignStoreLottery,
  listSignStoreLotteryDrawRecords,
  verifySignStoreLotterySeal
} from '@/api/api'
import Act004QuizDialog from '@/components/act004-quiz-dialog/act004-quiz-dialog.vue'
import Act004QuizPageOverlay from '@/components/act004-quiz-page-overlay/act004-quiz-page-overlay.vue'
import Act004QuizImageOptionOverlay from '@/components/act004-quiz-image-option-overlay/act004-quiz-image-option-overlay.vue'
import { parseImageOptionLayoutConfig } from '@/utils/activity-multi/quiz-image-option-layout'
import {
  isQuizHotspot,
  shouldOpenQuizDialog,
  isRandomQuestionOrder,
  getQuizDisplayMode,
  resolveQuizStudyGateFromSources,
  isQuizPassedFromPrefetch,
  isQuizPassedForPoint
} from '@/utils/activity-multi/quiz-condition.js'
import {
  showAct004QuizCorrectAnswerModal,
  showAct004QuizWrongAnswerModal,
  showAct004QuizAlreadyCorrectToast
} from '@/utils/activity-multi/act004-quiz-wrong-answer.js'
import { quizHotspotBoxStyle } from '@/utils/activity-multi/quiz-page-layout'
import { mergeHotspotDebugStyle, isHotspotDebugVisible } from '@/utils/hotspot-debug'
import {
  act004UserFormFieldLabel,
  act004UserFormDraftKey,
  act004InputTypeForField,
  readUserFormDraftByHotspot,
  normalizeConditionalTargetRules,
  hotspotNeedsProfileFieldsConditionalVerify,
  resolveConditionalTargetPageIndex
} from '@/utils/act004-user-form-fields'
import {
  normalizeCheckInSuccessConditionalRules,
  resolveCheckInSuccessTargetPageIndex
} from '@/utils/act004-checkin-success-jump'
import {
  normalizeVerifyConditionType,
  normalizeVerifyMinCheckinCount,
  evaluateVerifyCheckinCountCondition,
  shouldUseHotspotNotUnlockedImg,
  HOTSPOT_VERIFY_CONDITION_LOCKED_TOAST
} from '@/utils/act004-verify-condition'
import {
  normalizeBgOverlayConditionalRules,
  normalizeBgOverlayDisplayOrder,
  resolveBgOverlayImageUrl
} from '@/utils/act004-bg-overlay'
import {
  pickProgressBarFields,
  normalizeProgressBarDisplayOrder
} from '@/utils/act004-progress-bar'
import { readAllowClickCheckinValue } from '@/utils/act004-allow-click-checkin'
import {
  LINK_CHECKIN_ZONE_SIZE_RPX,
  normalizeLinkCheckinPointIndexes,
  pickLinkCheckinZoneFields,
  normalizeLinkCheckinSuccessDurationMs,
  normalizeLinkCheckinSuccessWidthRpx,
  normalizeLinkCheckinSuccessHeightRpx,
  isAct004LinkCheckinZoneHotspot,
  getAct004LinkCheckinZoneKey
} from '@/utils/act004-link-checkin-zone'
import Act004ProgressBarLayer from '@/components/act004/Act004ProgressBarLayer.vue'
import {
  normalizeAct004DataFontBold,
  normalizeAct004DataFontColor,
  normalizeAct004DataFontSize,
  resolveAct004DataHotspotFontFamilyCss,
  preloadAct004DataHotspotFontsForPages,
  pickAct004DataHotspotFields
} from '@/utils/act004-data-hotspot'
import moment from "moment";
import { getImageUrl, preloadImages, rpxToPx } from "@/utils";
import {
  shouldUseHotspotCallbackBeacon,
  isExternalHttpSuccessCallbackUrl,
  fireHotspotCallbackBeaconGet
} from '@/utils/hotspotCallbackBeacon'
import AuthorizationConfirmation from "@/components/authorization-confirmation/authorization-confirmation.vue";
import { ensureSignstoreH5Uid } from '@/utils/signstore-h5-uid'
import {
  markSignstoreH5Auth,
  canReuseSignstoreAnonymousToken,
  canReuseSignstoreThirdPartToken,
  clearIncompatibleSignstoreAuthForAnonymous,
  clearIncompatibleSignstoreAuthForThirdPart,
  clearSignstoreH5AuthSession,
  clearSignstoreH5UserInfo,
  ensureSignstoreAnonymousLoggedIn,
  getSignstoreUnauthorizedMessage,
  isSignstoreUnauthorizedResponse,
  isThirdPartUidLoginRejectedResponse,
  mergeActivityEntryQuery,
  persistSignstoreH5LoginPayload,
  resolveActivityThirdPartUid,
  shouldIgnoreUrlUidForActivity,
  signstoreActivityEntryAuth,
  signstoreAnonymousFreshLoginForActivityEntry,
  stripIgnoredUidFromBrowserUrl
} from '@/utils/signstore-h5-auth'

export default {
	components: {
		AuthorizationConfirmation,
		Act004QuizDialog,
		Act004QuizPageOverlay,
		Act004QuizImageOptionOverlay,
		Act004ProgressBarLayer
	},
	data() {
		return {
			activityId: 0,
			activityCode: null,
			/** H5 匿名用户标识（无第三方 uid 时自动注册） */
			h5Uid: null,
			/** H5 鉴权模式：anonymous | thirdPart | member（表单按手机号合并后） */
			h5AuthMode: null,
			/** 非法访问恢复仅执行一次，避免重复 replace */
			_signstoreUnauthorizedReentryAttempted: false,
			activityUserId: null, // 用户活动参与记录ID
			/** 数据热点 type=9：USER_ACTIVITY_POINTS 展示用 */
			userActivityAvailablePoints: 0,
			/** 数据热点 type=9：USER_LOTTERY_CHANCE（activity_user.lottery_chance_num） */
			userLotteryChanceNum: 0,
			/** 数据热点 CHECKIN_POINT_RANK：pointIndex -> 排行（未打卡为 0） */
			checkinPointRankByPointIndex: {},
			/** 数据热点请求去重（避免切页/滚动时重复打排行接口） */
			_dataHotspotRefreshKey: '',
			_dataHotspotRefreshAt: 0,
			_dataHotspotRefreshTimer: null,
			_dataHotspotRefreshPromise: null,
			ruleCode: null, // 印章规则编码
			backgroundSign: '', // 背景图（从 page_config_json.backgroundSign 获取，单背景模式）
			/** 打卡页背景：single 单图整页 | composite 组合（上 Banner + 中规则 + 下打卡区） */
			backgroundSignMode: 'single',
			signCompositeBanner: '',
			signCompositeRuleHtml: '',
			// 活动是否要求手机号授权（来自 page_config_json.phoneAuth）
			phoneAuthRequired: false,
			phoneAuthRaw: null, // 原始配置，便于调试
			// 活动是否要求昵称授权（来自 page_config_json.nicknameAuth）
			nicknameAuthRequired: false,
			nicknameAuthRaw: null,
			// 热点配置（从 page_config_json.pages[pageIndex].hotspots 获取）
			hotspots: [],
			/** ACT004 用户信息表单：热点 index+字段 -> 草稿值 */
			activityUserFormDraft: {},
			/** 服务端 yx_user 资料快照 */
			activityUserFormProfile: {},
			activityUserFormSubmitting: false,
			// 页面配置（从 page_config_json.pages 获取）
			pages: [],
			// 当前页面索引（对应 page_config_json.pages 下标）
			currentPageIndex: 0,
      /** 活动内页面跳转历史（仅 internal 跳转写入，back 优先回退该栈） */
      pageJumpHistory: [],
			/** 从带 displayOrder 的纵向浏览，经跳转进入「无顺序」页时为 true，仅单页展示 */
			jumpDetachedMode: false,
			/** 纵向滑动容器高度（px） */
			windowHeightPx: 0,
			/** 视口宽度（px），用于 rpx→px 内联样式 */
			windowWidthPx: 375,
			/** onLoad 传入的 pageIndex，在 queryActiveFun 里消费 */
			_launchPageIndex: null,
			/** onLoad / route query 传入的页面唯一 key（16位），优先于 pageIndex 定位页面 */
			_launchPageKey: '',
			/** 多页纵向：松手/滚动结束后吸附到整页，避免停在两页之间 */
			_mpSnapTimer: null,
			_mpSnapLockUntil: 0,
			_mpLastScrollTop: 0,
			_mpSectionTops: [],
			_mpSectionHeights: [],
			// 调用门店编码（必填）：对应活动 store_id 所关联 sign_store.store_code；活动详情 / URL 带参
			authStoreCode: '',
			/** H5 路由或 onLoad 传入的门店编码，优先于活动详情中的默认门店 */
			_authStoreCodeFromQuery: '',
			// 已打卡的点位序号列表
			checkedPointIndexes: [],
			/** 进度条热点动画进度 0–100，key = pageIndex-pointIndex */
			act004ProgressBarPercentByKey: {},
			/** 本会话已播放完成的进度条 key，避免重复动画 */
			act004ProgressBarPlayedKeys: {},
			/** 本次会话内刚打卡成功的点位：不立即展示「完成文案 / 已打卡角标 / 成功大图」，需用户再次点击该热点后展示 */
			postCheckInDeferredVisualIndexes: [],
			/** 摇一摇热点（type=21） */
			_shakeMotionHandler: null,
			_shakeUniHandler: null,
			_shakeUseUni: false,
			_shakeMotionLastX: null,
			_shakeMotionLastY: null,
			_shakeMotionLastZ: null,
			_shakeMotionLastTime: 0,
			_shakeLastDrawAt: 0,
			_shakeMotionPermissionGranted: false,
			shakeLotteryDrawing: false,
			showShakeLotteryDrawingHint: false,
			/** 活动参与用户 uid（getOrCreate 返回，供抽奖等接口） */
			signstoreUserUid: null,
			/** 摇一摇中奖后：热点 key -> lottery_show_image 原路径 */
			shakeWonPrizeImageByHotspotKey: {},
			/** 已抽奖成功的热点 key，避免重复触发 */
			shakeLotteryCompletedKeys: {},
			shakeLotteryVerifyStatusByHotspotKey: {},
			shakeLotteryDrawRecordIdByHotspotKey: {},
			shakeLotteryVerifyRuleCodeByHotspotKey: {},
			shakeVerifiedPrizeImageByHotspotKey: {},
			shakeLotteryVerifyChecking: false,
			_shakeLotteryVerifyGestureSubmitted: false,
			/** 联动打卡热区（type=22）四指提交中 */
			linkCheckinZoneChecking: false,
			_linkCheckinZoneGestureSubmitted: false,
			_linkCheckinSuccessPopupTimer: null,
			linkCheckinSuccessPopupActive: false,
			linkCheckinSuccessPopupBoxStyle: null,
			/** 与联动热区矩形相交、需禁用点击的打卡点 index 缓存（按页） */
			_linkZoneBlockedPointIndexesCachePage: null,
			_linkZoneBlockedPointIndexesCache: null,
			/** 联动热区屏幕矩形（document 触摸命中用） */
			_linkZoneHitTargets: [],
			_linkZoneDocTouchInstalled: false,
			_linkZoneDocTouchBound: null,
			_linkZoneSyncScrollTimer: null,
			_activityHasLinkCheckinZone: false,
			// 当前点击的热点索引（用于传递 pointIndex）
			currentHotspotIndex: null,
			completedCount: 0, // 已完成的活动数量（status = 3 和 4）
			verifyableCount: 0, // 可核销的活动数量（status = 3）
			isVerifying: false, // 是否为核销操作
      showLoading: false,
      signSingleBgLoaded: false,
      signCompositeBannerLoaded: false,
      _bgHoldSafetyTimer: null,
      checking: false,
      showcheckInBox: false,
      /** 打开盖章层时锁定的打卡区域 Y 偏移（rpx） */
      checkInAreaOffsetYLocked: null,
      /** 内部活动页打卡模式：转圈与跳转以打开时的页面为基准 */
      checkInInternalPageModeLocked: false,
      checkInInternalBackgroundImgLocked: '',
      /** 内部活动页模式：背景对应 pages 下标（用于渲染页内跳转等热点） */
      checkInInternalPageIndexLocked: null,
      checkInSourcePageIndexLocked: null,
      checkInInternalSealLoading: false,
      /** 页面跳转后短暂抑制底层 showBgHold 全屏 loading（无 UI，仅防闪） */
      _suppressBgHoldUntil: 0,
      _lastCheckedPointsRefreshPageIndex: null,
      _lastCheckedPointsRefreshAt: 0,
      _checkedPointsRefreshInflightByPage: null,
      load: false,
      showHotspotImagePreview: false, // 是否显示热点图片预览
      previewHotspotImage: '', // 预览的图片地址 
      previewHotspotNestedHotspots: [],
      imageScheduleAlarmTimers: [],
      imageScheduleAlarmTriggeredKeys: [],
      showHotspotRichTextDialog: false,
      hotspotRichTextContent: '',
      hotspotRichTextTitle: '活动详情',
      richTextTagStyle: {
        table: 'width:100%;border-collapse:collapse;display:table;',
        th: 'border:1px solid #d1d5db;padding:4px 5px;font-weight:600;',
        td: 'border:1px solid #d1d5db;padding:4px 5px;',
        p: 'margin:0 0 5px 0;line-height:1.7;',
        img: 'max-width:100%;height:auto;display:block;'
      },
      // 标准坐标系（用于跨设备兼容，与H5页面保持一致）
      standardWidth: 800,
      standardHeight: 600,
      // 最后一次触摸点数据（实时，抬指时可能被覆盖为 3/2 点）
      lastTouchPoints: null,
      // 最后一次有效的 4 点坐标（用于 touchend 兜底提交）
      lastValidFourPoints: null,
      // 本次手势内是否已提交（防止 touchstart/touchmove/touchend 多处触发重复请求）
      hasSubmittedThisGesture: false,
      // 触点数量（调试用：-1 表示未触摸）
      touchDebugCount: -1,
      // canvas 绘图尺寸（与 600rpx 对应的 px，用于绘图坐标系）
      canvasPixelSize: 300,
      // 音频播放器
      audioContext: null,
      showShippingDialog: false,
      shippingForm: {
        receiverName: '',
        receiverPhone: '',
        province: '',
        city: '',
        district: '',
        address: '',
        saved: false
      },
      shippingRegion: ['', '', ''],
      shippingHotspotIndex: null,
      shippingReadonly: false,
      shippingSaving: false,
      // 成功后转圈阶段锁定的热点配置，避免 currentHotspotIndex 清空后回退默认值
      sealLoadingCenterImgLocked: '',
      sealLoadingDurationMsLocked: null,
      // 页面级 loading（加载页）中心图标，独立于热点转圈中心图
      pageLoadingCenterImg: '',
      // 打卡弹框背景：pageConfigJson 根级默认，可被热点 checkInBackgroundImg 覆盖
      checkInBackgroundImgDefault: '',
      // 打卡背景高宽比（height / width），用于弹框自适应尺寸
      checkInBackgroundRatio: 0,
      /** 打卡限次（与 pageConfigJson 一致，供点击热点前预检） */
      checkinLimitPeriodType: 'DAILY',
      dailyCheckinLimitReachedHotspotIndex: null,
      pointCheckinMaxCountPerPoint: 1,
      pointCheckinLimitScopePointIndexes: [],
      dailyActivityTotalRoundsLimitReachedHotspotIndex: null,
      /** ACT004 打卡限次/问答详情缓存：pointIndex -> 接口 data */
      act004LimitDetailByPoint: {},
      act004QuizDialogVisible: false,
      act004QuizPointIndex: null,
      act004QuizPayload: null,
      act004QuizPendingResolve: null,
      act004QuizPageDisplayMode: 'PAGE_HOTSPOT',
      act004QuizPageCurrentQuestionCorrect: false,
      act004QuizPageQuizPassedPending: false,
      act004QuizPageOverlayVisible: false,
      act004QuizPageSessionId: '',
      act004QuizPageQuestions: [],
      act004QuizPageCurrentIndex: 0,
      act004QuizPageSelectedIds: [],
      act004QuizPageTextValue: '',
      act004QuizPageSubmitting: false,
      act004QuizPageBootstrapping: false,
      act004QuizPageWrongRetryMode: 'RESTART_FROM_FIRST',
      act004QuizPageReturnPageIndex: null,
      act004QuizStudyOverlayVisible: false,
      act004QuizStudyPageIndex: null,
      act004QuizStudyStartHotspot: null,
      act004QuizStudyGatePending: null,
      act004QuizStudyPrevJumpDetached: null
		}
	},
  computed: {
    act004QuizPageCurrentQuestion() {
      const list = this.act004QuizPageQuestions
      const i = this.act004QuizPageCurrentIndex
      return list && list[i] ? list[i] : null
    },
    act004QuizPageLayoutConfig() {
      const q = this.act004QuizPageCurrentQuestion
      return q && q.layoutConfigJson != null ? q.layoutConfigJson : ''
    },
    /** 问答会话 pageIndex：热点在 page_config 中的页码，与 batch 预取 hasPassed 一致（非当前浏览页） */
    act004QuizSessionPageIndex() {
      const pi = parseInt(this.act004QuizPointIndex, 10)
      if (!Number.isFinite(pi) || pi <= 0) return this.currentPageIndex
      const resolved = this.resolveHotspotPageIndexByPointIndex(pi)
      return resolved != null ? resolved : this.currentPageIndex
    },
    hotspotDebugVisible() {
      return isHotspotDebugVisible()
    },
    /** 当前浏览页是否存在联动打卡热区（type=22，含 visiblePageIndexes 跨页展示） */
    hasLinkCheckinZoneOnCurrentPage() {
      const pi = this.currentPageIndex
      const page = (this.pages || [])[pi]
      if (!page) return false
      return this.sortedLinkCheckinZonesForPage(page.hotspots, pi).length > 0
    },
    /**
     * 组合背景下打卡层高度：热点中心 Y = top%×1334（与 translate(-50%,-50%) 一致），下缘 = centerY + size/2，再加底边距。
     * 勿对打卡层使用 min-height:0 !important，否则会覆盖内联 minHeight，层高度塌陷导致绝对定位热点被裁切。
     */
    compositeCheckinLayerHeightRpx() {
      return this.compositeLayerHeightRpxForPage(this.hotspots, this.currentPageIndex)
    },
    isShippingFormComplete() {
      const f = this.shippingForm || {}
      return !!(
        String(f.receiverName || '').trim() &&
        /^1\d{10}$/.test(String(f.receiverPhone || '').trim()) &&
        String(f.province || '').trim() &&
        String(f.city || '').trim() &&
        String(f.district || '').trim() &&
        String(f.address || '').trim()
      )
    },
    shippingRegionText() {
      const f = this.shippingForm || {}
      if (String(f.province || '').trim() && String(f.city || '').trim() && String(f.district || '').trim()) {
        return `${f.province} ${f.city} ${f.district}`
      }
      return '请选择省 / 市 / 区'
    },
    currentHotspotData() {
      return this.findHotspotByPointIndex(this.currentHotspotIndex)
    },
    canNavigateCurrentHotspot() {
      const h = this.currentHotspotData
      if (!h || !h.showCheckinLocation) return false
      const lat = parseFloat(h.latitude)
      const lng = parseFloat(h.longitude)
      return Number.isFinite(lat) && Number.isFinite(lng)
    },
    currentCheckInBackgroundMode() {
      const hotspot = this.currentHotspotData || {}
      const m = String(hotspot.checkInBackgroundMode || 'popup').trim().toLowerCase()
      return m === 'internal_page' ? 'internal_page' : 'popup'
    },
    currentCheckInIsInternalPage() {
      return !!this.checkInInternalPageModeLocked && !!this.currentCheckInBackgroundImg
    },
    /** 打卡内部页背景上可点的热点（跳转等，与页内配置一致） */
    checkInInternalPageHotspotItems() {
      if (!this.currentCheckInIsInternalPage) return []
      const pageIdx = parseInt(this.checkInInternalPageIndexLocked, 10)
      if (!Number.isFinite(pageIdx) || pageIdx < 0) return []
      const pages = Array.isArray(this.pages) ? this.pages : []
      const page = pages[pageIdx]
      if (!page || !Array.isArray(page.hotspots)) return []
      const sorted = this.sortedHotspotsForAct004Render(page.hotspots, pageIdx)
      return sorted
        .map((hotspot, listIndex) => ({ hotspot, listIndex }))
        .filter(({ hotspot }) => this.isCheckInInternalPageInteractiveHotspot(hotspot))
    },
    currentCheckInAreaOffsetY() {
      if (this.checkInAreaOffsetYLocked != null) {
        const locked = parseInt(this.checkInAreaOffsetYLocked, 10)
        if (Number.isFinite(locked)) return locked
      }
      const hotspot = this.currentHotspotData || {}
      const n = parseInt(hotspot.checkInAreaOffsetY, 10)
      return Number.isFinite(n) ? n : 0
    },
    checkInCanvasWrapperStyle() {
      if (!this.currentCheckInBackgroundImg) return {}
      const offsetY = this.currentCheckInAreaOffsetY
      const vw = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
      const offsetPx = (offsetY * vw) / 750
      if (this.currentCheckInIsInternalPage) {
        return { marginTop: offsetPx + 'px' }
      }
      return { marginTop: offsetPx + 'px' }
    },
    currentCheckInBackgroundImg() {
      if (String(this.checkInInternalBackgroundImgLocked || '').trim()) {
        return String(this.checkInInternalBackgroundImgLocked).trim()
      }
      return this.resolveCheckInBackgroundImgForHotspot(this.currentHotspotData || {})
    },
    currentSealLoadingCenterImg() {
      const locked = String(this.sealLoadingCenterImgLocked || '').trim()
      if (locked) return this.getImageUrl(locked)
      const hotspot = this.currentHotspotData || {}
      const custom = String(hotspot.checkInLoadingCenterImg || '').trim()
      return custom ? this.getImageUrl(custom) : this.getImageUrl('logoicon.png')
    },
    currentSealLoadingDurationMs() {
      if (this.sealLoadingDurationMsLocked != null) {
        const locked = parseInt(this.sealLoadingDurationMsLocked, 10)
        if (Number.isFinite(locked) && locked >= 0) return locked
      }
      return this.resolveCheckInLoadingDurationMs(this.currentHotspotData, 2000)
    },
    currentPageLoadingCenterImg() {
      const custom = String(this.pageLoadingCenterImg || '').trim()
      return custom ? this.getImageUrl(custom) : this.getImageUrl('logoicon.png')
    },
    checkInPopupStyle() {
      if (!this.currentCheckInBackgroundImg) return {}
      const vw = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
      const vh = this.windowHeightPx || (typeof window !== 'undefined' ? window.innerHeight : 667)
      const maxW = Math.max(280, Math.floor(vw * 0.9))
      const maxH = Math.max(360, Math.floor(vh * 0.82))
      const ratio = this.checkInBackgroundRatio > 0 ? this.checkInBackgroundRatio : (1467 / 1100)
      let popupW = maxW
      let popupH = Math.round(popupW * ratio)
      if (popupH > maxH) {
        popupH = maxH
        popupW = Math.round(popupH / ratio)
      }
      return { width: popupW + 'px' }
    },
    /** 接口已结束，但仍等大图 @load，避免真机上 JPEG 自上而下渐进显示 */
    showBgHold() {
      if (this.showLoading) return false
      // H5：背景 @load 不可靠，页内切换（如进入打卡地图）不挡全屏 loading
      if (typeof window !== 'undefined') return false
      if (this.showcheckInBox) return false
      if (Date.now() < (this._suppressBgHoldUntil || 0)) return false
      // 页内问答/学习门禁：勿用全屏挡层盖住答题热点（挡层 z-index 高于页内 overlay）
      if (this.act004QuizPageOverlayVisible || this.act004QuizDialogVisible || this.act004QuizStudyOverlayVisible) {
        return false
      }
      // 多页纵向：各节可能为单背景或组合背景，不在此用全局 backgroundSignMode 阻塞（与 pages[].backgroundSignMode 一致）
      if (this.useMultiPageScroll) {
        return false
      }
      const signCtx = this.effectiveAct004SignCtx
      if (this.resolvePageBackgroundSignMode(signCtx) === 'composite') {
        const b = this.resolvePageSignCompositeBanner(signCtx)
        return !!(b && !this.signCompositeBannerLoaded)
      }
      return !!(this.backgroundSign && !this.signSingleBgLoaded)
    },
    /** 单页/跳转脱离模式：当前页面对象，供 resolvePage* 与根级配置组合（与控制台 pages[] 一致） */
    effectiveAct004SignCtx() {
      const idx = Math.max(0, parseInt(this.currentPageIndex, 10) || 0)
      const page = (this.pages && this.pages[idx]) || {}
      return { originalIndex: idx, page }
    },
    /** 只要存在有效 displayOrder（1 起）即启用严格顺序模式 */
    hasStrictDisplayOrder() {
      return this.countPagesWithDisplayOrder(this.pages) > 0
    },
    /** 纵向可浏览的页面序列：无 displayOrder 时按数组顺序全部可滑；有 displayOrder 时仅包含有顺序的页 */
    navPageBlocks() {
      return this.buildNavPageBlocks(this.pages || [])
    },
    /** 多节纵向滑动（至少 2 个可浏览节且非跳转脱离模式） */
    useMultiPageScroll() {
      return !this.jumpDetachedMode && this.navPageBlocks.length > 1
    },
    act004MpScrollStyle() {
      // 多页：高度交给 flex（见 .page-container--act004-mp-scroll），避免固定 px 小于 innerHeight 露出白底
      if (this.useMultiPageScroll) {
        return { width: '100%', boxSizing: 'border-box' }
      }
      const h = this.windowHeightPx || 667
      return { height: h + 'px', width: '100%', boxSizing: 'border-box' }
    },
    /** 盖章 canvas 外框：内联 rpx 在 H5 无效，按视口宽换算 px */
    checkinCanvasOuterStyle() {
      const w = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
      return {
        width: rpxToPx(600, w),
        height: rpxToPx(600, w)
      }
    }
  },
  watch: {
    currentCheckInBackgroundImg: {
      handler(url) {
        this.measureCheckInBackgroundRatio(url)
      },
      immediate: true
    },
    showcheckInBox(val) {
      if (!val) this.$nextTick(() => this.syncShakeLotteryListener())
    },
    showLoading(val) {
      if (!val) this.$nextTick(() => this.syncShakeLotteryListener())
    }
  },
	onLoad(options) {
		console.log('[activity-multi] onLoad', { keys: options ? Object.keys(options) : [], options })
		this.startActivityPage(options || {})
	},
	mounted() {
		const q = mergeActivityEntryQuery(this.$route)
		const activityCodeForStrip =
			(q.activity_code != null && String(q.activity_code).trim()) ||
			(q.activityCode != null && String(q.activityCode).trim()) ||
			''
		if (activityCodeForStrip) {
			stripIgnoredUidFromBrowserUrl(activityCodeForStrip, this.$router)
		}
		console.log('[activity-multi] mounted', {
			fullPath: this.$route && this.$route.fullPath,
			queryKeys: Object.keys(q),
			query: q
		})
		// 纯 Vue H5（vue-router）不会触发 uni-app 的 onLoad，必须从路由 query 启动
		this.startActivityPage(q)
		this.initH5ViewportResizeListener()
		// 首屏布局后 innerHeight 可能变化（地址栏收起），再取一次减少露底
		this.$nextTick(() => {
			this.refreshH5ViewportSize()
		})
	},
	onUnload() {
		this.clearBgHoldSafetyTimer()
		this.clearMultiPageSnapTimer()
      this.clearImageScheduleAlarmTimers()
		this.clearDataHotspotRefreshTimer()
		this.clearAct004ProgressBarTimers()
		this.destroyShakeLotteryListener()
		this.teardownLinkCheckinZoneDocumentTouch()
	},
	methods: {
    moment,
    getImageUrl,
    measureCheckInBackgroundRatio(url) {
      const u = String(url || '').trim()
      if (!u) {
        this.checkInBackgroundRatio = 0
        return
      }
      try {
        const img = new Image()
        img.onload = () => {
          const w = Number(img.naturalWidth || img.width || 0)
          const h = Number(img.naturalHeight || img.height || 0)
          if (w > 0 && h > 0) this.checkInBackgroundRatio = h / w
        }
        img.onerror = () => {
          this.checkInBackgroundRatio = 0
        }
        img.src = u
      } catch (e) {
        this.checkInBackgroundRatio = 0
      }
    },
    /** ACT004：纵向某一节的打卡背景模式，pages[i] 优先，缺省回退根级 */
    resolvePageBackgroundSignMode(ctx) {
      const p = ctx && ctx.page
      if (p && p.backgroundSignMode === 'composite') return 'composite'
      if (p && p.backgroundSignMode === 'single') return 'single'
      return this.backgroundSignMode === 'composite' ? 'composite' : 'single'
    },
    resolvePageSignCompositeBanner(ctx) {
      const p = ctx && ctx.page
      if (p && Object.prototype.hasOwnProperty.call(p, 'signCompositeBanner')) {
        return p.signCompositeBanner != null ? String(p.signCompositeBanner).trim() : ''
      }
      return String(this.signCompositeBanner || '').trim()
    },
    resolvePageSignCompositeRuleHtml(ctx) {
      const p = ctx && ctx.page
      if (p && Object.prototype.hasOwnProperty.call(p, 'signCompositeRuleHtml')) {
        return p.signCompositeRuleHtml != null ? String(p.signCompositeRuleHtml) : ''
      }
      return String(this.signCompositeRuleHtml || '')
    },
    /** 横竖屏/地址栏变化时刷新视口宽高，保证 rpxPx / 多页容器高度与小程序观感一致 */
    initH5ViewportResizeListener() {
      if (typeof window === 'undefined') return
      this._viewportResizeHandler = () => {
        this.refreshH5ViewportSize()
        this.syncLinkCheckinZoneHitTargets()
      }
      window.addEventListener('resize', this._viewportResizeHandler, { passive: true })
    },
    /** H5：uni.getSystemInfoSync 的 windowHeight 常小于浏览器 innerHeight，与 inner 取大，减少滚动区下方白条 */
    refreshH5ViewportSize() {
      if (typeof window === 'undefined') {
        try {
          const sys = uni.getSystemInfoSync()
          this.windowHeightPx = Math.max(400, Math.floor(sys.windowHeight || sys.screenHeight || 667))
          this.windowWidthPx = Math.max(320, Math.floor(sys.windowWidth || 375))
        } catch (e) {
          this.windowHeightPx = 667
          this.windowWidthPx = 375
        }
        return
      }
      try {
        const sys = uni.getSystemInfoSync()
        const sh = Math.floor(sys.windowHeight || sys.screenHeight || 667)
        const sw = Math.floor(sys.windowWidth || 375)
        const ih = Math.floor(window.innerHeight || sh)
        const iw = Math.floor(window.innerWidth || sw)
        this.windowHeightPx = Math.max(400, Math.max(sh, ih))
        this.windowWidthPx = Math.max(320, Math.max(sw, iw))
      } catch (e) {
        this.windowHeightPx = Math.max(400, Math.floor(window.innerHeight || 667))
        this.windowWidthPx = Math.max(320, Math.floor(window.innerWidth || 375))
      }
    },
    /** 模板内联样式：设计稿 rpx → 当前视口 px */
    rpxPx(n) {
      return rpxToPx(n, this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375))
    },
    /** rpxPx 的数值（用于几何计算）；勿对 rpxPx 返回值再拼 'px' */
    rpxToPxNum(n) {
      const s = this.rpxPx(n)
      const v = parseFloat(String(s).replace(/px$/i, '').trim())
      return Number.isFinite(v) ? v : 0
    },
    /**
     * 与 .hotspot-circle / 控制台 preview-hotspot 一致：left/top 为 750×1334 百分比中心点 + translate(-50%,-50%)
     */
    getAct004CenterHotspotBoxStyle(hotspot, widthRpx, heightRpx, extraStyle) {
      const leftPct = this.parseHotspotNumber(hotspot && hotspot.left, 50)
      const topPct = this.parseHotspotNumber(hotspot && hotspot.top, 50)
      const w = (() => {
        const n = parseInt(widthRpx, 10)
        return Number.isFinite(n) && n > 0 ? n : LINK_CHECKIN_ZONE_SIZE_RPX
      })()
      const h = (() => {
        const n = parseInt(heightRpx, 10)
        return Number.isFinite(n) && n > 0 ? n : w
      })()
      return Object.assign(
        {
          position: 'absolute',
          left: this.rpxPx((leftPct / 100) * 750),
          top: this.rpxPx((topPct / 100) * 1334),
          width: this.rpxPx(w),
          height: this.rpxPx(h),
          transform: 'translate(-50%, -50%)',
          boxSizing: 'border-box'
        },
        extraStyle || {}
      )
    },
    /** 调试：把本页关键状态打出来，复制控制台发给开发排查 */
    debugActivityMulti(tag, extra) {
      try {
        const payload = {
          tag,
          showLoading: this.showLoading,
          showBgHold: this.showBgHold,
          jumpDetachedMode: this.jumpDetachedMode,
          useMultiPageScroll: this.useMultiPageScroll,
          navPageBlocksLen: (this.navPageBlocks && this.navPageBlocks.length) || 0,
          pagesLen: (this.pages && this.pages.length) || 0,
          currentPageIndex: this.currentPageIndex,
          backgroundSignMode: this.backgroundSignMode,
          backgroundSignLen: (this.backgroundSign && String(this.backgroundSign).length) || 0,
          signSingleBgLoaded: this.signSingleBgLoaded,
          signCompositeBannerLoaded: this.signCompositeBannerLoaded,
          hasCompositeBanner: !!this.signCompositeBanner,
          activityId: this.activityId,
          activityCode: this.activityCode,
          activityUserId: this.activityUserId,
          windowHeightPx: this.windowHeightPx,
          __done: this.__activityBootstrapDone,
          __running: this.__activityBootstrapRunning
        }
        if (extra !== undefined) payload.extra = extra
        console.log('[activity-multi]', JSON.stringify(payload))
      } catch (e) {
        console.log('[activity-multi] debugActivityMulti error', tag, e && e.message)
      }
    },
    /** 从路由 / onLoad 读取 auth_store_code、authStoreCode、store_code、storeCode，供打卡接口 authStoreCode */
    applyAuthStoreCodeFromQueryOptions(options) {
      this._authStoreCodeFromQuery = ''
      const o = options || {}
      const raw =
        o.auth_store_code != null && o.auth_store_code !== ''
          ? o.auth_store_code
          : o.authStoreCode != null && o.authStoreCode !== ''
            ? o.authStoreCode
            : o.store_code != null && o.store_code !== ''
              ? o.store_code
              : o.storeCode
      if (raw != null && String(raw).trim() !== '') {
        this._authStoreCodeFromQuery = String(raw).trim()
      }
    },
    /** 印章比对 act004 必传 authStoreCode（活动 store → sign_store.store_code） */
    requireAuthStoreCodeForSeal() {
      const c = (this.authStoreCode && String(this.authStoreCode).trim()) || ''
      if (!c) {
        uni.showToast({
          title: '缺少门店编码，请重新进入活动',
          icon: 'none',
          duration: 2500
        })
        return false
      }
      return true
    },
    /** uni-app onLoad 在纯 Vue H5 不执行，须与 mounted 共用此入口（防重复拉数） */
    startActivityPage(options) {
      if (this.__activityBootstrapDone) {
        console.log('[activity-multi] startActivityPage skip: __activityBootstrapDone')
        return
      }
      if (this.__activityBootstrapRunning) {
        console.log('[activity-multi] startActivityPage skip: __activityBootstrapRunning')
        return
      }
      try {
        uni.setNavigationBarTitle({ title: '' })
      } catch (e) {
        /* ignore */
      }
      if (typeof document !== 'undefined') {
        document.title = ''
      }
      options = options || {}
      this.applyAuthStoreCodeFromQueryOptions(options)
      console.log('[activity-multi] startActivityPage enter', {
        keys: Object.keys(options),
        activity_code: options.activity_code,
        id: options.id,
        uid: options.uid
      })
      this.refreshH5ViewportSize()
      if (options.pageIndex != null && options.pageIndex !== '') {
        const pi = parseInt(options.pageIndex, 10)
        if (Number.isFinite(pi) && pi >= 0) {
          this._launchPageIndex = pi
        }
      }
      if (options.pageKey != null && String(options.pageKey).trim() !== '') {
        this._launchPageKey = String(options.pageKey).trim()
      }
      const activityCode = options.activity_code
      const activityId = options.id
      if (!activityCode && !activityId) {
        console.log('[activity-multi] startActivityPage abort: no activity_code / id')
        uni.showToast({
          title: '活动不存在或已结束',
          icon: 'none'
        })
        setTimeout(() => {
          uni.redirectTo({
            url: '/pages/index/index'
          })
        }, 1500)
        return
      }
      this.__activityBootstrapRunning = true
      if (activityCode) {
        this.activityCode = activityCode
      } else {
        this.activityId = activityId
      }
      this.audioContext = uni.createInnerAudioContext()
      this.audioContext.src = 'http://cscec.warnny.com/duang.mp3'
      this.audioContext.autoplay = false
      this.audioContext.onError(() => {})
      try {
        const sys2 = uni.getSystemInfoSync()
        this.canvasPixelSize = Math.round(600 * (sys2.windowWidth || 375) / 750)
      } catch (e2) {
        this.canvasPixelSize = 300
      }
      this.debugActivityMulti('after-startActivityPage-prep')
      this.ensureH5AuthThenLoadActivity(options)
    },
    /** H5：带 uid 则静默登录；无 uid 则 ensure h5_uuid 走匿名自动注册（与 activityRedirect 一致） */
    async ensureH5AuthThenLoadActivity(options) {
      console.log('[activity-multi] ensureH5AuthThenLoadActivity begin')
      try {
        options = options || {}
        const uidRaw = options.uid != null ? String(options.uid).trim() : ''
        const activityCodeForAuth =
          (options.activity_code || options.activityCode) ||
          this.activityCode ||
          ''
        const activityCodeStr = activityCodeForAuth != null ? String(activityCodeForAuth).trim() : ''
        if (typeof window !== 'undefined') {
          const auth = await signstoreActivityEntryAuth({
            activityCode: activityCodeStr,
            uidRaw,
            h5UidFromQuery: options.h5_uid,
            thirdPartLoginFn: h5ThirdPartSilentLogin,
            anonymousLoginFn: h5AnonymousSilentLogin,
            freshAnonymous: true
          })
          if (auth.mode === 'thirdPart' && auth.failed) {
            if (isSignstoreUnauthorizedResponse(auth.loginRes)) {
              if (await this.recoverFromSignstoreUnauthorized(auth.loginRes)) {
                await this.queryActiveFun()
                this.__activityBootstrapDone = true
                return
              }
            }
            throw new Error((auth.loginRes && auth.loginRes.msg) || '静默登录失败')
          }
          if (auth.mode === 'thirdPart') {
            this.h5AuthMode = 'thirdPart'
            if (!auth.reused && auth.loginRes && auth.loginRes.data) {
              persistSignstoreH5LoginPayload(auth.loginRes.data, 'thirdPart', auth.uid)
            }
            console.log('[activity-multi] thirdPart login ok', { uid: auth.uid, reused: !!auth.reused })
          } else {
            this.h5AuthMode = 'anonymous'
            this.h5Uid = auth.h5Uid
            console.log('[activity-multi] fresh anonymous login on entry', {
              h5Uid: this.h5Uid,
              fallbackFromThirdPart: !!auth.fallbackFromThirdPart
            })
          }
        } else {
          console.log('[activity-multi] skip silent login', { hasUid: !!uidRaw, hasWindow: typeof window !== 'undefined' })
        }
        console.log('[activity-multi] calling queryActiveFun…')
        await this.queryActiveFun()
        this.__activityBootstrapDone = true
        console.log('[activity-multi] bootstrap done')
        this.debugActivityMulti('after-queryActiveFun-success')
      } catch (err) {
        console.error('[activity-multi] ensureH5AuthThenLoadActivity error', err && err.message, err)
        this.debugActivityMulti('after-ensureH5Auth-error', { err: err && err.message })
        uni.showToast({
          title: (err && err.message) || '加载失败',
          icon: 'none',
          duration: 2500
        })
      } finally {
        this.__activityBootstrapRunning = false
        console.log('[activity-multi] ensureH5AuthThenLoadActivity finally, running=false')
      }
    },
    clearBgHoldSafetyTimer() {
      if (this._bgHoldSafetyTimer) {
        clearTimeout(this._bgHoldSafetyTimer)
        this._bgHoldSafetyTimer = null
      }
    },
    clearMultiPageSnapTimer() {
      if (this._mpSnapTimer) {
        clearTimeout(this._mpSnapTimer)
        this._mpSnapTimer = null
      }
    },
    /** 测量各节高度，用于吸附时按真实内容高度算「当前页」 */
    measureMultiPageSnapLayout(done) {
      if (!this.useMultiPageScroll) {
        if (typeof done === 'function') done(false)
        return
      }
      const blocks = this.navPageBlocks
      if (!blocks || blocks.length < 2) {
        if (typeof done === 'function') done(false)
        return
      }
      this.$nextTick(() => {
        const q = uni.createSelectorQuery().in(this)
        blocks.forEach((ctx) => {
          q.select('#mp-' + ctx.originalIndex).boundingClientRect()
        })
        q.exec((rects) => {
          if (!rects || !Array.isArray(rects) || rects.length < blocks.length) {
            if (typeof done === 'function') done(false)
            return
          }
          const hs = rects.map((r) => (r && r.height) ? r.height : 0)
          if (hs.some((h) => h <= 1)) {
            if (typeof done === 'function') done(false)
            return
          }
          const tops = []
          let acc = 0
          for (let i = 0; i < hs.length; i++) {
            tops.push(acc)
            acc += hs[i]
          }
          this._mpSectionHeights = hs
          this._mpSectionTops = tops
          if (typeof done === 'function') done(true)
        })
      })
    },
    /** 根据 scrollTop 得到当前应以哪一节为主（视口内可见面积最大） */
    getMultiPageBlockIndexByScrollTop(st) {
      const blocks = this.navPageBlocks
      const n = (blocks && blocks.length) ? blocks.length : 0
      if (!n) return 0
      const vh = this.windowHeightPx || 667
      const tops = this._mpSectionTops
      const heights = this._mpSectionHeights
      if (!tops || tops.length !== n || !heights || heights.length !== n) {
        const i = Math.floor(st / vh + 0.35)
        return Math.max(0, Math.min(n - 1, i))
      }
      let best = 0
      let maxOverlap = -1
      for (let i = 0; i < n; i++) {
        const top = tops[i]
        const bot = top + (heights[i] || vh)
        const overlap = Math.min(bot, st + vh) - Math.max(top, st)
        const o = overlap > 0 ? overlap : 0
        if (o > maxOverlap) {
          maxOverlap = o
          best = i
        }
      }
      if (maxOverlap <= 0) {
        let nearest = 0
        let nd = Infinity
        for (let i = 0; i < n; i++) {
          const d = Math.abs(st - tops[i])
          if (d < nd) {
            nd = d
            nearest = i
          }
        }
        return nearest
      }
      return best
    },
    syncMultiPageCurrentPageByScrollTop(st) {
      if (!this.useMultiPageScroll) return
      const blocks = this.navPageBlocks
      if (!blocks || !blocks.length) return
      const idx = this.getMultiPageBlockIndexByScrollTop(st)
      const nav = blocks[Math.max(0, Math.min(blocks.length - 1, idx))]
      if (!nav) return
      if (nav.originalIndex !== this.currentPageIndex) {
        this.currentPageIndex = nav.originalIndex
        this.applyPageAtIndex(nav.originalIndex)
        void this.refreshPageDataHotspots(nav.originalIndex)
      }
    },
    /** 将滚动位置吸附到当前主导节的顶部（整页感；不再在「已占满屏」时提前 return，避免可停在两页之间） */
    snapMultiPageScrollToNearest(scrollTop) {
      if (!this.useMultiPageScroll) return
      if (Date.now() < this._mpSnapLockUntil) return
      const blocks = this.navPageBlocks
      if (!blocks || blocks.length < 2) return
      if (!this._mpSectionTops || this._mpSectionTops.length !== blocks.length) {
        this.measureMultiPageSnapLayout((ok) => {
          if (ok) this.snapMultiPageScrollToNearest(this._mpLastScrollTop)
        })
        return
      }
      const tops = this._mpSectionTops
      const snapIdx = this.getMultiPageBlockIndexByScrollTop(scrollTop)
      const targetTop = tops[snapIdx]
      if (targetTop == null || !Number.isFinite(targetTop)) return
      if (Math.abs(scrollTop - targetTop) < 8) return

      const id = 'mp-' + blocks[snapIdx].originalIndex
      this._mpSnapLockUntil = Date.now() + 420
      this.$nextTick(() => {
        const root = this.$refs.mpScrollRoot
        if (root && typeof root.scrollTop === 'number') {
          try {
            root.scrollTop = targetTop
          } catch (e) {
            this.scrollMpSectionIntoView(id)
          }
        } else {
          this.scrollMpSectionIntoView(id)
        }
        const nav = blocks[snapIdx]
        if (nav && nav.originalIndex !== this.currentPageIndex) {
          this.currentPageIndex = nav.originalIndex
          this.applyPageAtIndex(nav.originalIndex)
        }
      })
    },
    parsePageDisplayOrder(page) {
      if (!page || page.displayOrder == null || page.displayOrder === '') return null
      const n = parseInt(page.displayOrder, 10)
      if (!Number.isFinite(n) || n < 1) return null
      return n
    },
    countPagesWithDisplayOrder(pages) {
      if (!Array.isArray(pages)) return 0
      let c = 0
      pages.forEach((p) => {
        if (this.parsePageDisplayOrder(p) != null) c += 1
      })
      return c
    },
    buildNavPageBlocks(pages) {
      const list = Array.isArray(pages) ? pages : []
      if (list.length === 0) return []
      const orderedCount = this.countPagesWithDisplayOrder(list)
      // 全部未配置顺序：默认只展示第一页，不开启纵向多页滚动
      if (orderedCount === 0) {
        return [{
          originalIndex: 0,
          page: list[0],
          displayOrder: 1
        }]
      }
      const out = []
      list.forEach((page, originalIndex) => {
        const o = this.parsePageDisplayOrder(page)
        if (o != null) {
          out.push({ originalIndex, page, displayOrder: o })
        }
      })
      out.sort((a, b) => (a.displayOrder - b.displayOrder) || (a.originalIndex - b.originalIndex))
      if (out.length === 0 && list.length) {
        return list.map((page, originalIndex) => ({
          originalIndex,
          page,
          displayOrder: originalIndex + 1
        }))
      }
      return out
    },
    mapRawHotspotsToRuntime(rawList) {
      if (!rawList || !Array.isArray(rawList) || rawList.length === 0) return []
      const base = rawList.map((h) => ({
        left: h.left || h.x || 0,
        top: h.top || h.y || 0,
        size: h.size || 80,
        index: (() => {
          const raw = h && h.index
          if (raw === undefined || raw === null || raw === '') return null
          const n = parseInt(raw, 10)
          return Number.isFinite(n) ? n : raw
        })(),
        checkType: h.checkType || 'CHECKIN',
        allowClickCheckin: readAllowClickCheckinValue(h),
        sealRuleCode: (() => {
          const s = h.sealRuleCode != null ? String(h.sealRuleCode).trim() : ''
          if (s) return s
          if (h.hotspotRuleCode != null) return String(h.hotspotRuleCode).trim()
          return ''
        })(),
        ...(String(h.type) === '22'
          ? {
            ...pickLinkCheckinZoneFields(h),
            shape: 'rectangle',
            sizeWidth: (() => {
              const n = parseInt(h.sizeWidth, 10)
              return Number.isFinite(n) && n > 0 ? n : LINK_CHECKIN_ZONE_SIZE_RPX
            })(),
            sizeHeight: (() => {
              const n = parseInt(h.sizeHeight, 10)
              return Number.isFinite(n) && n > 0 ? n : LINK_CHECKIN_ZONE_SIZE_RPX
            })()
          }
          : {}),
        needShippingInfo: h.needShippingInfo === 1 || h.needShippingInfo === '1' || h.needShippingInfo === true,
        showCheckinLocation: h.showCheckinLocation === 1 || h.showCheckinLocation === '1' || h.showCheckinLocation === true,
        longitude: h.longitude == null ? '' : String(h.longitude),
        latitude: h.latitude == null ? '' : String(h.latitude),
        locationAddress: h.locationAddress || '',
        doSignImg: h.doSignImg || '',
        notSignImg: h.notSignImg || '',
        notUnlockedImg: h.notUnlockedImg == null ? '' : String(h.notUnlockedImg).trim(),
        successCallbackUrl: h.successCallbackUrl || '',
        successCallbackMethod:
          String(h.successCallbackMethod == null ? '' : h.successCallbackMethod).trim().toUpperCase() === 'GET'
            ? 'GET'
            : 'POST',
        checkedBadgeImg: h.checkedBadgeImg || '',
        checkedBadgeSize: (() => {
          const n = parseInt(h.checkedBadgeSize, 10)
          return Number.isFinite(n) && n > 0 ? n : 0
        })(),
        checkInCompleteText: h.checkInCompleteText == null ? '' : String(h.checkInCompleteText).trim(),
        checkInBackgroundMode: (() => {
          const v = String(h.checkInBackgroundMode || 'popup').trim().toLowerCase()
          return v === 'internal_page' ? 'internal_page' : 'popup'
        })(),
        checkInBackgroundPageIndex: (() => {
          const n = parseInt(h.checkInBackgroundPageIndex, 10)
          return Number.isFinite(n) && n >= 0 ? n : null
        })(),
        checkInBackgroundImg: h.checkInBackgroundImg == null ? '' : String(h.checkInBackgroundImg).trim(),
        checkInAreaOffsetY: (() => {
          const n = parseInt(h.checkInAreaOffsetY, 10)
          return Number.isFinite(n) ? n : 0
        })(),
        checkInLoadingCenterImg: h.checkInLoadingCenterImg == null ? '' : String(h.checkInLoadingCenterImg).trim(),
        checkInLoadingDurationMs: this.resolveCheckInLoadingDurationMs(h, null),
        checkInSuccessJumpType: (() => {
          const v = String(h.checkInSuccessJumpType || 'none').trim().toLowerCase()
          return v === 'internal' || v === 'external' ? v : 'none'
        })(),
        checkInSuccessTargetPageIndex: (() => {
          const n = parseInt(h.checkInSuccessTargetPageIndex, 10)
          return Number.isFinite(n) && n >= 0 ? n : null
        })(),
        checkInSuccessJumpUrl: String(h.checkInSuccessJumpUrl || '').trim(),
        checkInSuccessConditionalRules: normalizeCheckInSuccessConditionalRules(
          h.checkInSuccessConditionalRules
        ),
        bgOverlayDefaultImage: String(h.bgOverlayDefaultImage || '').trim(),
        bgOverlayDisplayOrder: normalizeBgOverlayDisplayOrder(h.bgOverlayDisplayOrder),
        bgOverlayConditionalRules: normalizeBgOverlayConditionalRules(h.bgOverlayConditionalRules),
        ...pickProgressBarFields(h),
        type: h.type || '1',
        shape: h.shape || 'circle',
        radius: h.radius || h.size || 80,
        sizeWidth: h.sizeWidth || h.size || 100,
        sizeHeight: h.sizeHeight || h.size || 100,
        hasStock: h.hasStock === 1 || h.hasStock === '1' || h.hasStock === true,
        stockQuantity: h.stockQuantity || 0,
        usedStockQuantity: h.usedStockQuantity || 0,
        stockInsufficientImg: h.stockInsufficientImg || '',
        limitVerifyCount: h.limitVerifyCount === 1 || h.limitVerifyCount === '1' || h.limitVerifyCount === true,
        verifyCountLimit: h.verifyCountLimit || 1,
        notVerifyImg: h.notVerifyImg || '',
        jumpType: h.jumpType || 'external',
        conditionalTargetRules: normalizeConditionalTargetRules(h),
        targetPageIndex: h.targetPageIndex || 0,
        jumpButtonImg: h.jumpButtonImg || '',
        imageButtonImg: h.imageButtonImg || '',
        imageButtonImg2: h.imageButtonImg2 || '',
        imageButton2WhenCheckedIndexes: Array.isArray(h.imageButton2WhenCheckedIndexes)
          ? h.imageButton2WhenCheckedIndexes
            .map((v) => parseInt(v, 10))
            .filter((v) => Number.isFinite(v) && v > 0)
          : [],
        imageButtonWhenCheckedIndexes: Array.isArray(h.imageButtonWhenCheckedIndexes)
          ? h.imageButtonWhenCheckedIndexes
            .map((v) => parseInt(v, 10))
            .filter((v) => Number.isFinite(v) && v > 0)
          : [],
        // 根因修复：后台配置的是 url，但之前运行时映射未透传该字段，导致点击时 URL 变 undefined
        url: h.url || '',
        name: h.name || '',
        content: h.content || '',
        imageUrl: h.imageUrl || '',
        imageFloatEnabled: h.imageFloatEnabled === 1 || h.imageFloatEnabled === true || h.imageFloatEnabled === '1' ? 1 : 0,
        imageFloatDirection: (() => {
          const d = String(h.imageFloatDirection || 'VERTICAL').toUpperCase()
          return d === 'HORIZONTAL' || d === 'LR' || d === 'X' ? 'HORIZONTAL' : 'VERTICAL'
        })(),
        imageFloatRangeRpx: (() => {
          const n = parseInt(h.imageFloatRangeRpx, 10)
          return Number.isFinite(n) ? Math.max(1, Math.min(200, n)) : 24
        })(),
        imageFloatBlurBg: (() => {
          const raw = h.imageFloatBlurBg
          const n = parseInt(raw, 10)
          return raw === 1 || raw === true || raw === '1' || (Number.isFinite(n) && n === 1) ? 1 : 0
        })(),
        imageScheduleList: Array.isArray(h.imageScheduleList) ? h.imageScheduleList : [],
        imageNestedHotspots: Array.isArray(h.imageNestedHotspots) ? h.imageNestedHotspots : [],
        visiblePageIndexes: Array.isArray(h.visiblePageIndexes)
          ? h.visiblePageIndexes
            .map((v) => parseInt(v, 10))
            .filter((v) => Number.isFinite(v) && v >= 0)
          : [],
        verifyCondition: h.verifyCondition && typeof h.verifyCondition === 'object'
          ? {
            needValidate: !!h.verifyCondition.needValidate,
            conditionType: normalizeVerifyConditionType(h.verifyCondition.conditionType),
            quizPaperId: h.verifyCondition.quizPaperId != null ? h.verifyCondition.quizPaperId : null,
            quizPaperCode: h.verifyCondition.quizPaperCode || '',
            timeStart: h.verifyCondition.timeStart || '',
            timeEnd: h.verifyCondition.timeEnd || '',
            prerequisiteIndexes: Array.isArray(h.verifyCondition.prerequisiteIndexes) ? h.verifyCondition.prerequisiteIndexes : [],
            prerequisiteRelation: (() => {
              const r = h.verifyCondition.prerequisiteRelation
              if (r === 'OR' || String(r || '').toUpperCase() === 'OR') return 'OR'
              return 'AND'
            })(),
            minCheckinCount: (() => {
              const n = normalizeVerifyMinCheckinCount(h.verifyCondition.minCheckinCount)
              return n > 0 ? n : 1
            })()
          }
          : null,
        imageFullHotZone: h.imageFullHotZone === 1 || h.imageFullHotZone === true || h.imageFullHotZone === '1' ? 1 : 0,
        bindImageHotspotIndex: h.bindImageHotspotIndex,
        bindScheduleIndex: h.bindScheduleIndex,
        userFieldBind: h.userFieldBind == null ? '' : String(h.userFieldBind).trim(),
        formInputLabel: h.formInputLabel == null ? '' : String(h.formInputLabel).trim(),
        formInputPlaceholder: h.formInputPlaceholder == null ? '' : String(h.formInputPlaceholder).trim(),
        formInputButtonImg: h.formInputButtonImg == null ? '' : String(h.formInputButtonImg).trim(),
        formInputFontSize: (() => {
          const raw = h.formInputFontSize != null ? h.formInputFontSize : h.dataFontSize
          const n = parseInt(raw, 10)
          return Number.isFinite(n) ? Math.max(12, Math.min(120, n)) : 30
        })(),
        formInputPaddingLeft: (() => {
          const n = parseInt(h.formInputPaddingLeft, 10)
          return Number.isFinite(n) ? Math.max(0, Math.min(120, n)) : 24
        })(),
        formInputMaxLength: (() => {
          const n = parseInt(h.formInputMaxLength, 10)
          return Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : 30
        })(),
        formSubmitButtonImg: h.formSubmitButtonImg == null ? '' : String(h.formSubmitButtonImg).trim(),
        formSubmitSuccessText: h.formSubmitSuccessText == null ? '' : String(h.formSubmitSuccessText).trim(),
        formSubmitScope: h.formSubmitScope == null ? 'page' : String(h.formSubmitScope).trim(),
        formSubmitSuccessJumpType: (() => {
          const v = String(h.formSubmitSuccessJumpType || 'none').trim().toLowerCase()
          return v === 'internal' || v === 'external' ? v : 'none'
        })(),
        formSubmitSuccessTargetPageIndex: (() => {
          const n = parseInt(h.formSubmitSuccessTargetPageIndex, 10)
          return Number.isFinite(n) ? n : null
        })(),
        formSubmitSuccessJumpUrl: h.formSubmitSuccessJumpUrl == null ? '' : String(h.formSubmitSuccessJumpUrl).trim(),
        shakeBeforeImage: h.shakeBeforeImage == null ? '' : String(h.shakeBeforeImage).trim(),
        shakeAfterAction: (() => {
          const v = String(h.shakeAfterAction || 'lottery').trim().toLowerCase()
          return v === 'lottery' ? 'lottery' : 'lottery'
        })(),
        lotteryConfigId: (() => {
          const n = parseInt(h.lotteryConfigId, 10)
          return Number.isFinite(n) && n > 0 ? n : null
        })(),
        lotteryRuleName: h.lotteryRuleName == null ? '' : String(h.lotteryRuleName).trim(),
        lotteryBindActivityCode: h.lotteryBindActivityCode == null ? '' : String(h.lotteryBindActivityCode).trim(),
        shakeVerifyMode: (() => {
          const v = String(h.shakeVerifyMode || '').trim().toLowerCase()
          return v === 'checkin' ? 'checkin' : ''
        })(),
        shakeVerifyRuleId: (() => {
          const n = parseInt(h.shakeVerifyRuleId, 10)
          return Number.isFinite(n) && n > 0 ? n : null
        })(),
        shakeVerifyRuleCode: (() => {
          const s = h.shakeVerifyRuleCode != null ? String(h.shakeVerifyRuleCode).trim()
            : (h.shake_verify_rule_code != null ? String(h.shake_verify_rule_code).trim() : '')
          if (s) return s
          if (h.hotspotRuleCode != null) return String(h.hotspotRuleCode).trim()
          if (h.sealRuleCode != null) return String(h.sealRuleCode).trim()
          return ''
        })(),
        ...pickAct004DataHotspotFields(h)
      }))
      return this.mergeImageFullHotZoneGeometry(base, rawList)
    },
    mergeImageFullHotZoneGeometry(runtimeList, rawList) {
      if (!Array.isArray(runtimeList) || !Array.isArray(rawList)) return runtimeList || []
      const byIndex = {}
      rawList.forEach((h) => {
        const n = parseInt(h && h.index, 10)
        if (Number.isFinite(n)) byIndex[n] = h
      })
      return runtimeList.map((row, idx) => {
        const raw = rawList[idx]
        if (!raw || !(raw.imageFullHotZone === 1 || raw.imageFullHotZone === true || raw.imageFullHotZone === '1')) {
          return row
        }
        const parentIdx = parseInt(raw.bindImageHotspotIndex, 10)
        const parent = Number.isFinite(parentIdx) ? byIndex[parentIdx] : null
        if (!parent) return row
        return {
          ...row,
          left: parent.left != null ? parent.left : row.left,
          top: parent.top != null ? parent.top : row.top,
          shape: parent.shape || row.shape,
          radius: parent.radius || parent.size || row.radius,
          sizeWidth: parent.sizeWidth || parent.size || row.sizeWidth,
          sizeHeight: parent.sizeHeight || parent.size || row.sizeHeight,
          size: parent.size || row.size
        }
      })
    },
    /**
     * 当前时间命中的时段下标。多个时段时间重叠时取列表中最后一个命中的时段（后配置的通常更具体），
     * 避免 findIndex 只取第一项导致展示的时段图与「以图热区」bindScheduleIndex 不一致、无法打卡。
     */
    resolveImageScheduleHitIndexForNow(schedules) {
      const list = Array.isArray(schedules) ? schedules : []
      const now = Date.now()
      let lastHit = -1
      for (let i = 0; i < list.length; i++) {
        const item = list[i]
        if (!item || typeof item !== 'object') continue
        const startTs = this.parseDateTimeSafe(item.startTime)
        const endTs = this.parseDateTimeSafe(item.endTime)
        if (startTs == null || endTs == null) continue
        if (now >= startTs && now <= endTs) lastHit = i
      }
      return lastHit
    },
    /** 根据弹层实际展示图 URL 反推时段下标（时间与 bindScheduleIndex 不一致时的兜底） */
    inferImageScheduleIndexByPreviewUrl(parentHotspot, previewUrlResolved) {
      const schedules = Array.isArray(parentHotspot && parentHotspot.imageScheduleList)
        ? parentHotspot.imageScheduleList
        : []
      if (!schedules.length || !previewUrlResolved) return -1
      const target = String(previewUrlResolved).trim()
      for (let i = 0; i < schedules.length; i++) {
        const row = schedules[i]
        const u = row && row.imageUrl ? String(this.getImageUrl(row.imageUrl)).trim() : ''
        if (u && u === target) return i
      }
      return -1
    },
    resolveActiveImageScheduleSlotIndex(parentType3) {
      const schedules = Array.isArray(parentType3 && parentType3.imageScheduleList) ? parentType3.imageScheduleList : []
      return this.resolveImageScheduleHitIndexForNow(schedules)
    },
    shouldShowImageFullHotzoneHotspot(hotspot, rawPageHotspots) {
      if (!hotspot) return false
      // 以图为热区的打卡热点不在主画布直接渲染，只在图片弹窗内触发
      if (hotspot.imageFullHotZone === 1 || hotspot.imageFullHotZone === '1') return false
      return true
    },
    /**
     * 以图热区子热点未单独配置 visiblePageIndexes 时，跟随父级「图片」热点的跨页展示配置，
     * 否则在其它页打开时段图弹层时 collectRawHotspotsForDisplay 聚合不到子热点，无法注入打卡层。
     */
    inheritVisiblePageIndexesFromImageParentHotspot(h, sourcePageIndex) {
      if (!h || !(h.imageFullHotZone === 1 || h.imageFullHotZone === true || h.imageFullHotZone === '1')) {
        return []
      }
      const bi = parseInt(h.bindImageHotspotIndex, 10)
      if (!Number.isFinite(bi)) return []
      const pages = Array.isArray(this.pages) ? this.pages : []
      const pi = Number.isFinite(parseInt(sourcePageIndex, 10)) ? parseInt(sourcePageIndex, 10) : 0
      const hs = pages[pi] && Array.isArray(pages[pi].hotspots) ? pages[pi].hotspots : []
      const parent = hs.find((x) => {
        if (!x) return false
        if (parseInt(x.index, 10) !== bi) return false
        return String(x.type || '') === '3'
      })
      if (!parent) return []
      return Array.isArray(parent.visiblePageIndexes)
        ? parent.visiblePageIndexes
          .map((v) => parseInt(v, 10))
          .filter((v) => Number.isFinite(v) && v >= 0)
        : []
    },
    collectRawHotspotsForDisplay(rawList, pageIndex) {
      const base = Array.isArray(rawList) ? rawList : []
      const idx = Number.isFinite(parseInt(pageIndex, 10)) ? parseInt(pageIndex, 10) : this.currentPageIndex
      const pages = Array.isArray(this.pages) ? this.pages : []
      const dedup = {}
      const out = []
      const pushWithDedup = (h, fallbackKey) => {
        let k = fallbackKey
        if (String(h && h.type) === '22') {
          k = `type22-${fallbackKey}`
        } else {
          const n = parseInt(h && h.index, 10)
          if (Number.isFinite(n)) k = `idx-${n}`
        }
        if (dedup[k]) return
        dedup[k] = true
        out.push(h)
      }
      base.forEach((h, i) => pushWithDedup(h, `base-${i}`))
      pages.forEach((p, pi) => {
        const hs = p && Array.isArray(p.hotspots) ? p.hotspots : []
        hs.forEach((h, hi) => {
          let arr = Array.isArray(h && h.visiblePageIndexes)
            ? h.visiblePageIndexes
              .map((v) => parseInt(v, 10))
              .filter((v) => Number.isFinite(v) && v >= 0)
            : []
          if (!arr.length) {
            arr = this.inheritVisiblePageIndexesFromImageParentHotspot(h, pi)
          }
          if (!arr.includes(idx)) return
          pushWithDedup(h, `extra-${pi}-${hi}`)
        })
      })
      return out
    },
    sortedHotspotsForAct004Render(rawList, pageIndex) {
      const mergedRaw = this.collectRawHotspotsForDisplay(rawList, pageIndex)
      const merged = this.mapRawHotspotsToRuntime(mergedRaw)
      const visible = merged.filter(
        (h) =>
          this.shouldShowImageFullHotzoneHotspot(h, mergedRaw) &&
          String(h.type) !== '18' &&
          String(h.type) !== '19' &&
          String(h.type) !== '22'
      )
      const withOrder = visible.map((h, i) => ({ h, i, full: h.imageFullHotZone === 1 || h.imageFullHotZone === '1' }))
      withOrder.sort((a, b) => {
        if (a.full === b.full) return a.i - b.i
        return a.full ? 1 : -1
      })
      return withOrder.map((x) => x.h)
    },
    sortedLinkCheckinZonesForPage(rawList, pageIndex) {
      const mergedRaw = this.collectRawHotspotsForDisplay(rawList, pageIndex)
      return this.mapRawHotspotsToRuntime(mergedRaw).filter((h) => isAct004LinkCheckinZoneHotspot(h))
    },
    sortedBgOverlayHotspotsForPage(rawList, pageIndex) {
      const mergedRaw = this.collectRawHotspotsForDisplay(rawList, pageIndex)
      const merged = this.mapRawHotspotsToRuntime(mergedRaw).filter((h) => String(h.type) === '18')
      merged.sort(
        (a, b) =>
          (normalizeBgOverlayDisplayOrder(a.bgOverlayDisplayOrder) || 0) -
          (normalizeBgOverlayDisplayOrder(b.bgOverlayDisplayOrder) || 0)
      )
      return merged
    },
    visibleBgOverlayLayersForPage(rawList, pageIndex) {
      return this.sortedBgOverlayHotspotsForPage(rawList, pageIndex)
        .map((hotspot) => ({
          hotspot,
          src: this.resolveBgOverlayLayerImage(hotspot)
        }))
        .filter((item) => !!item.src)
    },
    resolveBgOverlayLayerImage(hotspot) {
      return resolveBgOverlayImageUrl(hotspot, {
        isCheckinPointDone: (idx) => this.isCheckinPointIndexDone(idx)
      })
    },
    getBgOverlayLayerStyle(hotspot) {
      const order = normalizeBgOverlayDisplayOrder(hotspot && hotspot.bgOverlayDisplayOrder)
      const shape = hotspot && hotspot.shape === 'rectangle' ? 'rectangle' : 'circle'
      const widthRpx = shape === 'rectangle' ? (hotspot.sizeWidth || hotspot.size || 750) : (hotspot.radius || hotspot.size || 80)
      const heightRpx = shape === 'rectangle' ? (hotspot.sizeHeight || hotspot.size || 1334) : (hotspot.radius || hotspot.size || 80)
      return {
        left: this.rpxPx((hotspot.left / 100 * 750)),
        top: this.rpxPx((hotspot.top / 100 * 1334)),
        width: this.rpxPx(widthRpx),
        height: this.rpxPx(heightRpx),
        zIndex: 1 + order
      }
    },
    sortedProgressBarHotspotsForPage(rawList, pageIndex) {
      const mergedRaw = this.collectRawHotspotsForDisplay(rawList, pageIndex)
      const merged = this.mapRawHotspotsToRuntime(mergedRaw).filter((h) => String(h.type) === '19')
      merged.sort(
        (a, b) =>
          (normalizeProgressBarDisplayOrder(a.progressBarDisplayOrder) || 0) -
          (normalizeProgressBarDisplayOrder(b.progressBarDisplayOrder) || 0)
      )
      return merged
    },
    act004ProgressBarLayerKey(hotspot, pageIndex) {
      const pi = parseInt(pageIndex, 10)
      const idx = hotspot && hotspot.index != null ? String(hotspot.index) : ''
      return `${Number.isFinite(pi) ? pi : 0}-${idx}`
    },
    visibleProgressBarLayersForPage(rawList, pageIndex) {
      return this.sortedProgressBarHotspotsForPage(rawList, pageIndex)
        .map((hotspot) => {
          const bg = String(hotspot.progressBarBgImage || '').trim()
          if (!bg) return null
          const key = this.act004ProgressBarLayerKey(hotspot, pageIndex)
          const picked = pickProgressBarFields(hotspot)
          return {
            hotspot,
            key,
            bgSrc: bg,
            fillSrc: String(picked.progressBarFillImage || '').trim(),
            segments: Array.isArray(picked.progressBarSegments) ? picked.progressBarSegments : [],
            fillTileWidthRpx: picked.progressBarFillTileWidth,
            fillTileHeightRpx: picked.progressBarFillTileHeight,
            fillTileGapRpx: picked.progressBarFillTileGap,
            windowWidthPx: this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375),
            trackInsetLeft: picked.progressBarTrackInsetLeft,
            trackInsetTop: picked.progressBarTrackInsetTop,
            trackInsetRight: picked.progressBarTrackInsetRight,
            trackInsetBottom: picked.progressBarTrackInsetBottom
          }
        })
        .filter(Boolean)
    },
    getProgressBarLayerStyle(hotspot) {
      const order = normalizeProgressBarDisplayOrder(hotspot && hotspot.progressBarDisplayOrder)
      const leftPct = this.parseHotspotNumber(hotspot && hotspot.left, 0)
      const topPct = this.parseHotspotNumber(hotspot && hotspot.top, 0)
      const widthRpx = this.parseHotspotNumber(hotspot && (hotspot.sizeWidth || hotspot.size), 684)
      const heightRpx = this.parseHotspotNumber(hotspot && (hotspot.sizeHeight || hotspot.size), 56)
      return {
        left: this.rpxPx((leftPct / 100) * 750),
        top: this.rpxPx((topPct / 100) * 1334),
        width: this.rpxPx(widthRpx),
        height: this.rpxPx(heightRpx),
        zIndex: 2 + order
      }
    },
    startAct004ProgressBarsForPage(pageIndex) {
      const pi = parseInt(pageIndex, 10)
      if (!Number.isFinite(pi) || pi < 0) return
      const page = this.pages && this.pages[pi]
      const list = page && page.hotspots
      const bars = this.sortedProgressBarHotspotsForPage(list, pi)
      bars.forEach((hotspot) => {
        const key = this.act004ProgressBarLayerKey(hotspot, pi)
        if (this.act004ProgressBarPlayedKeys && this.act004ProgressBarPlayedKeys[key]) {
          const cur = this.act004ProgressBarPercentByKey[key]
          if (cur == null || cur < 100) {
            this.$set(this.act004ProgressBarPercentByKey, key, 100)
          }
          return
        }
        this.runAct004ProgressBarAnimation(hotspot, pi, key)
      })
    },
    runAct004ProgressBarAnimation(hotspot, pageIndex, key) {
      if (!key) return
      const duration = parseInt(hotspot && hotspot.progressBarDurationMs, 10)
      const ms = Number.isFinite(duration) && duration >= 0 ? duration : 3000
      if (this._act004ProgressBarTimers && this._act004ProgressBarTimers[key]) {
        clearInterval(this._act004ProgressBarTimers[key])
      }
      if (!this._act004ProgressBarTimers) {
        this._act004ProgressBarTimers = {}
      }
      const finish = () => {
        this.$set(this.act004ProgressBarPercentByKey, key, 100)
        this.$set(this.act004ProgressBarPlayedKeys, key, true)
        if (this._act004ProgressBarTimers[key]) {
          clearInterval(this._act004ProgressBarTimers[key])
          delete this._act004ProgressBarTimers[key]
        }
        this.onAct004ProgressBarComplete(hotspot, pageIndex)
      }
      if (ms <= 0) {
        finish()
        return
      }
      const startAt = Date.now()
      this.$set(this.act004ProgressBarPercentByKey, key, 0)
      this._act004ProgressBarTimers[key] = setInterval(() => {
        const elapsed = Date.now() - startAt
        const pct = Math.min(100, Math.round((elapsed / ms) * 100))
        this.$set(this.act004ProgressBarPercentByKey, key, pct)
        if (pct >= 100) {
          finish()
        }
      }, 50)
    },
    onAct004ProgressBarComplete(hotspot, pageIndex) {
      const target = parseInt(hotspot && hotspot.progressBarCompleteTargetPageIndex, 10)
      if (!Number.isFinite(target) || target < 0) return
      this.handleHotspotJump(
        {
          jumpType: 'internal',
          targetPageIndex: target,
          conditionalTargetRules: []
        },
        { fromPageIndex: pageIndex }
      )
    },
    compositeLayerHeightRpxFromList(list) {
      const arr = list || []
      if (!arr.length) return 200
      const num = (v, fallback) => {
        const n = parseFloat(String(v == null ? '' : v).replace(/%/g, '').replace(/,/g, '').trim())
        return Number.isFinite(n) ? n : fallback
      }
      let maxBottom = 0
      arr.forEach((h) => {
        if (!h) return
        const centerY = num(h.top, 0) / 100 * 1334
        let half = num(h.size, 80) / 2
        if (h._quizPercentSize) {
          half = (num(h.sizeHeight, 8) / 100 * 1334) / 2
        } else if (h.shape === 'rectangle' && h.sizeHeight != null) {
          half = num(h.sizeHeight, h.size || 80) / 2
        }
        maxBottom = Math.max(maxBottom, centerY + half)
      })
      const bottomPad = 40
      const padded = Math.ceil(maxBottom) + bottomPad
      return Math.max(160, padded)
    },
    compositeLayerHeightRpxForPage(hotspots, pageOriginalIndex) {
      let list = Array.isArray(hotspots) ? hotspots.slice() : []
      if (
        this.act004QuizStudyOverlayVisible &&
        this.act004QuizStudyStartHotspot &&
        this.shouldRenderQuizStudyHotspot(pageOriginalIndex)
      ) {
        const h = this.act004QuizStudyStartHotspot
        list = list.concat([
          {
            top: h.top,
            sizeHeight: h.sizeHeight,
            _quizPercentSize: true
          }
        ])
      }
      return this.compositeLayerHeightRpxFromList(list)
    },
    resolvePageCheckinBackground(page) {
      if (!page) return ''
      return page.background != null ? String(page.background).trim() : ''
    },
    applyPageAtIndex(originalIndex, opts) {
      const options = opts && typeof opts === 'object' ? opts : {}
      const pages = this.pages || []
      const idx = Math.max(0, Math.min(pages.length - 1, originalIndex))
      const currentPage = pages[idx] || {}
      this.currentPageIndex = idx
      const skipBgHoldReset =
        options.skipBgHoldReset === true || typeof window !== 'undefined'
      if (!skipBgHoldReset) {
        this.signSingleBgLoaded = false
        this.signCompositeBannerLoaded = false
      }
      this.pageLoadingCenterImg = currentPage.loadingCenterImg == null ? '' : String(currentPage.loadingCenterImg).trim()
      this.backgroundSign = currentPage.background || ''
      if (currentPage.hotspots && Array.isArray(currentPage.hotspots) && currentPage.hotspots.length > 0) {
        this.hotspots = this.sortedHotspotsForAct004Render(currentPage.hotspots, idx)
      } else {
        this.hotspots = []
      }
      this.scheduleImageStartAlarmsForCurrentPage()
      this.syncShakeLotteryListener()
      this.invalidateLinkZoneBlockedPointIndexesCache()
      void this.refreshAct004CheckedPointsOnPageEnter(idx)
      this.$nextTick(() => {
        this.startAct004ProgressBarsForPage(idx)
        this.syncLinkCheckinZoneHitTargets()
        this.ensureLinkCheckinZoneDocumentTouch()
      })
    },
    /** H5：原生 div 滚动容器内，将 id 为 mp-* 的节滚到可视区顶部（替代 uni scroll-into-view） */
    scrollMpSectionIntoView(sectionId, immediate) {
      if (!sectionId || typeof window === 'undefined') return
      const run = () => {
        const root = this.$refs.mpScrollRoot
        if (!root) return
        const sid = String(sectionId).replace(/^#/, '')
        let el = null
        try {
          el = root.querySelector('#' + sid)
        } catch (err) {
          return
        }
        if (!el) return
        try {
          const rr = root.getBoundingClientRect()
          const er = el.getBoundingClientRect()
          root.scrollTop += er.top - rr.top
        } catch (e) {
          try {
            el.scrollIntoView({ block: 'start', behavior: 'auto' })
          } catch (e2) {}
        }
        try {
          this.syncMultiPageCurrentPageByScrollTop(root.scrollTop)
        } catch (e3) {}
      }
      if (immediate) {
        run()
        return
      }
      this.$nextTick(run)
    },
    /** 多页纵向：将指定节滚到容器顶部（绝对 scrollTop，避免关层后吸附回源页） */
    forceScrollMpSectionToTop(originalIndex) {
      const idx = parseInt(originalIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return false
      const root = this.$refs.mpScrollRoot
      if (!root || typeof window === 'undefined') return false
      const sid = 'mp-' + idx
      let el = null
      try {
        el = root.querySelector('#' + sid)
      } catch (err) {
        return false
      }
      if (!el) return false

      let top = 0
      let node = el
      while (node && node !== root) {
        top += node.offsetTop || 0
        node = node.offsetParent
      }
      if (node !== root) {
        try {
          const rr = root.getBoundingClientRect()
          const er = el.getBoundingClientRect()
          top = root.scrollTop + (er.top - rr.top)
        } catch (e2) {
          return false
        }
      }

      root.scrollTop = Math.max(0, Math.round(top))
      this._mpLastScrollTop = root.scrollTop
      this.clearMultiPageSnapTimer()
      const lockUntil = Date.now() + 1500
      if (lockUntil > this._mpSnapLockUntil) {
        this._mpSnapLockUntil = lockUntil
      }
      this.currentPageIndex = idx

      const si = this.navPageBlocks.findIndex((x) => x.originalIndex === idx)
      if (si >= 0 && Array.isArray(this._mpSectionTops)) {
        this.$set(this._mpSectionTops, si, root.scrollTop)
      }
      return true
    },
    async flushDomAfterPageJump() {
      await this.$nextTick()
      await new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        } else {
          setTimeout(resolve, 32)
        }
      })
    },
    preloadPageBackgroundAtIndex(originalIndex) {
      const idx = parseInt(originalIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return Promise.resolve()
      const pages = this.pages || []
      const page = pages[idx]
      const raw = page && page.background != null ? String(page.background).trim() : ''
      if (!raw) return Promise.resolve()
      const url = this.getImageUrl(raw)
      if (!url) return Promise.resolve()
      return new Promise((resolve) => {
        if (typeof Image === 'undefined') {
          resolve()
          return
        }
        const img = new Image()
        let done = false
        const finish = () => {
          if (done) return
          done = true
          resolve()
        }
        img.onload = finish
        img.onerror = finish
        img.src = url
        setTimeout(finish, 800)
      })
    },
    /** 跳转目标页：H5 不挡 loading，仅后台预加载背景 */
    markTargetPageBackgroundReady(originalIndex) {
      const idx = parseInt(originalIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return
      if (typeof window !== 'undefined') {
        this.signSingleBgLoaded = true
        this.signCompositeBannerLoaded = true
        void this.preloadPageBackgroundAtIndex(idx)
        return
      }
      const pages = this.pages || []
      const page = pages[idx] || {}
      const signCtx = { originalIndex: idx, page }
      if (this.resolvePageBackgroundSignMode(signCtx) === 'composite') {
        const banner = this.resolvePageSignCompositeBanner(signCtx)
        this.signCompositeBannerLoaded = !banner
      } else {
        const bg = page.background != null ? String(page.background).trim() : ''
        this.signSingleBgLoaded = !bg
      }
      this._suppressBgHoldUntil = Date.now() + 2000
      if (!this.signSingleBgLoaded || !this.signCompositeBannerLoaded) {
        this.preloadPageBackgroundAtIndex(idx).then(() => {
          this.signSingleBgLoaded = true
          this.signCompositeBannerLoaded = true
        })
      }
    },
    async finalizeCheckInInternalPageJump(targetPageIndex) {
      const idx = parseInt(targetPageIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return
      this.markTargetPageBackgroundReady(idx)
      await this.$nextTick()
      await this.flushDomAfterPageJump()
    },
    /**
     * 活动内跳转到指定页。
     * direct=true：内部页打卡背景点跳转，关层前强制切到目标页并锁定吸附（直达页面 A）
     */
    async applyActivityPageJumpTarget(targetPageIndex, opts) {
      const options = opts && typeof opts === 'object' ? opts : {}
      const idx = parseInt(targetPageIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return false
      const fromPageIndex =
        options.fromPageIndex != null && Number.isFinite(parseInt(options.fromPageIndex, 10))
          ? parseInt(options.fromPageIndex, 10)
          : this.currentPageIndex
      const useDirect = options.direct === true || options.waitScroll === true

      if (!useDirect && idx === fromPageIndex && idx === this.currentPageIndex) {
        uni.showToast({ title: '当前已在目标页面', icon: 'none' })
        return false
      }

      const blocks = this.navPageBlocks
      const strict = this.hasStrictDisplayOrder
      const inNav = blocks.some((b) => b.originalIndex === idx)
      const needApplyPage = useDirect || idx !== this.currentPageIndex

      if (needApplyPage) {
        if (idx !== this.currentPageIndex) {
          const last = this.pageJumpHistory.length ? this.pageJumpHistory[this.pageJumpHistory.length - 1] : null
          if (last !== fromPageIndex) {
            this.pageJumpHistory.push(fromPageIndex)
          }
        }
        this.jumpDetachedMode = strict && !inNav
        this.applyPageAtIndex(idx)
        await this.refreshAct004CheckedPointsOnPageEnter(idx)
        this.markTargetPageBackgroundReady(idx)
      }

      if (useDirect) {
        this.clearMultiPageSnapTimer()
        if (!this.jumpDetachedMode && this.useMultiPageScroll && this.navPageBlocks.length > 1 && inNav) {
          this.forceScrollMpSectionToTop(idx)
        }
        await this.$nextTick()
        await this.flushDomAfterPageJump()
        await this.refreshPageDataHotspots(idx, { force: true })
        this.preloadSignPageImages()
        return true
      }

      const doScroll = () => {
        if (!this.jumpDetachedMode && this.useMultiPageScroll && this.navPageBlocks.length > 1) {
          const si = this.navPageBlocks.findIndex((x) => x.originalIndex === idx)
          if (si >= 0) {
            this.scrollMpSectionIntoView('mp-' + idx, true)
          }
        }
        this.preloadSignPageImages()
        void this.refreshPageDataHotspots(idx, { force: true })
      }
      this.$nextTick(doScroll)
      return true
    },
    onAct004MultiPageScroll(e) {
      if (!this.useMultiPageScroll) return
      let st = 0
      if (e && e.detail && typeof e.detail.scrollTop === 'number') {
        st = e.detail.scrollTop
      } else if (e && e.target && typeof e.target.scrollTop === 'number') {
        st = e.target.scrollTop
      }
      this._mpLastScrollTop = st
      if (Date.now() < this._mpSnapLockUntil) return
      this.syncMultiPageCurrentPageByScrollTop(st)
      this.scheduleSyncLinkCheckinZoneHitTargets()
      this.clearMultiPageSnapTimer()
      this._mpSnapTimer = setTimeout(() => {
        this._mpSnapTimer = null
        this.snapMultiPageScrollToNearest(this._mpLastScrollTop)
        this.syncLinkCheckinZoneHitTargets()
      }, 48)
    },
    onAct004MultiPageTouchEnd() {
      if (!this.useMultiPageScroll) return
      if (Date.now() < this._mpSnapLockUntil) return
      this.clearMultiPageSnapTimer()
      // 略大于一帧，等惯性滚动写入 scrollTop 后再吸附到节顶
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.snapMultiPageScrollToNearest(this._mpLastScrollTop)
        })
      })
    },
    armBgHoldSafetyRelease() {
      this.clearBgHoldSafetyTimer()
      this._bgHoldSafetyTimer = setTimeout(() => {
        this._bgHoldSafetyTimer = null
        if (this.backgroundSign && !this.signSingleBgLoaded) this.signSingleBgLoaded = true
        if (this.signCompositeBanner && !this.signCompositeBannerLoaded) this.signCompositeBannerLoaded = true
      }, 30000)
    },
    onSignSingleBackgroundLoad() {
      this.clearBgHoldSafetyTimer()
      this.signSingleBgLoaded = true
      if (this.useMultiPageScroll) {
        this.$nextTick(() => this.measureMultiPageSnapLayout())
      }
    },
    onSignSingleBackgroundError() {
      this.clearBgHoldSafetyTimer()
      this.signSingleBgLoaded = true
    },
    onCompositeBannerLoad() {
      this.clearBgHoldSafetyTimer()
      this.signCompositeBannerLoaded = true
      if (this.useMultiPageScroll) {
        this.$nextTick(() => this.measureMultiPageSnapLayout())
      }
    },
    onCompositeBannerError() {
      this.clearBgHoldSafetyTimer()
      this.signCompositeBannerLoaded = true
    },
    /** 与模板中首屏 <img :src> 一致，预加载后再关 loading，避免大图渐进显示 */
    preloadSignPageImages() {
      const urls = []
      const pushU = (u) => {
        if (u) urls.push(u)
      }
      if (this.navPageBlocks && this.navPageBlocks.length > 1) {
        this.navPageBlocks.forEach((ctx) => {
          if (this.resolvePageBackgroundSignMode(ctx) === 'composite') {
            const b = this.resolvePageSignCompositeBanner(ctx)
            if (b) pushU(this.getImageUrl ? this.getImageUrl(b) : b)
          } else {
            const bg = (ctx.page && ctx.page.background) || this.backgroundSign
            if (bg) pushU(this.getImageUrl ? this.getImageUrl(bg) : bg)
          }
          const raw = ctx.page && ctx.page.hotspots
          this.sortedHotspotsForAct004Render(raw, ctx.originalIndex).forEach((h) => {
            const u = this.getHotspotImage(h)
            if (u) pushU(u)
          })
        })
      } else {
        const ctx = this.effectiveAct004SignCtx
        if (this.resolvePageBackgroundSignMode(ctx) === 'composite') {
          const b = this.resolvePageSignCompositeBanner(ctx)
          if (b) pushU(this.getImageUrl ? this.getImageUrl(b) : b)
        } else if (this.backgroundSign) {
          pushU(this.backgroundSign)
        }
      }
      if (this.hotspots && this.hotspots.length) {
        this.hotspots.forEach((h) => {
          const u = this.getHotspotImage(h)
          if (u) pushU(u)
        })
      }
      return preloadImages(urls)
    },
		async queryActiveFun() {
      let self = this
      console.log('[activity-multi] queryActiveFun start', {
        activityCode: self.activityCode,
        activityId: self.activityId
      })
      self.showLoading = true
      self.clearBgHoldSafetyTimer()
      self.signSingleBgLoaded = false
      self.signCompositeBannerLoaded = false
      self.debugActivityMulti('queryActiveFun-after-showLoading-true')

      try {
        let res
        if (self.activityCode) {
          // 通过 activityCode 查询
          const params = { activityCode: self.activityCode }
          res = await getSignStoreActivityDetailByCode(params)
        } else if (self.activityId) {
          // 通过 activityId 查询
          const params = { id: self.activityId }
          res = await getSignStoreActivityDetail(params)
        } else {
          throw new Error('活动ID或编码不存在')
        }
        
        // 检查返回数据结构
        if (!res) {
          throw new Error('API返回数据为空')
        }
        
        if (isSignstoreUnauthorizedResponse(res)) {
          const recovered = await self.recoverFromSignstoreUnauthorized(res)
          if (recovered) {
            self.showLoading = false
            return
          }
        }
        // 如果 status 不是 200，说明请求失败
        if (res.status !== 200) {
          throw new Error(res.msg || '获取活动详情失败')
        }
        
        // 获取实际的活动数据
        const activityData = res.data
        if (!activityData) {
          throw new Error('活动数据为空')
        }
        console.log('[activity-multi] detail ok', {
          id: activityData.id,
          code: activityData.activityCode,
          templateCode: activityData.templateCode,
          hasPageConfigJson: !!activityData.pageConfigJson
        })

        // 保存活动ID和编码
        self.activityId = activityData.id
        if (activityData.activityCode) {
          self.activityCode = activityData.activityCode
        }
        // 调用门店编码：接口返回 authStoreCode / store（detail、detailByCode 已带 storeId 解析结果）
        if (activityData.authStoreCode) {
          self.authStoreCode = String(activityData.authStoreCode).trim()
        } else if (activityData.store && activityData.store.storeCode) {
          self.authStoreCode = String(activityData.store.storeCode).trim()
        } else {
          self.authStoreCode = ''
        }
        // 链接上显式传入的门店编码优先（多店同活动、扫码带参）
        if (self._authStoreCodeFromQuery) {
          self.authStoreCode = self._authStoreCodeFromQuery
        }
        self.ruleCode = activityData.ruleCode || null
        
        // 解析 pageConfigJson
        let pageConfig = {}
        if (activityData.pageConfig) {
          pageConfig = activityData.pageConfig
        } else if (activityData.pageConfigJson) {
          try {
            pageConfig = typeof activityData.pageConfigJson === 'string' 
              ? JSON.parse(activityData.pageConfigJson) 
              : activityData.pageConfigJson
          } catch (e) {
            console.error('解析 pageConfigJson 失败:', e)
          }
        }                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                             
        
        // 处理多页面结构（displayOrder 决定纵向可浏览顺序，见 navPageBlocks）
        self.pages = pageConfig.pages || []
        preloadAct004DataHotspotFontsForPages(self.pages)
        self.pageJumpHistory = []
        const blocks = self.buildNavPageBlocks(self.pages)
        let startIdx = 0
        if (blocks.length) {
          startIdx = blocks[0].originalIndex
        } else if (self.pages.length) {
          startIdx = 0
        }
        self.jumpDetachedMode = false
        let launchTargetOriginalIndex = null
        const launchPageKey = String(self._launchPageKey || '').trim()
        const launch = self._launchPageIndex
        self._launchPageKey = ''
        self._launchPageIndex = null
        if (launchPageKey) {
          const strict = self.countPagesWithDisplayOrder(self.pages) > 0
          const target = self.pages
            .map((page, originalIndex) => ({ originalIndex, pageKey: String(page && page.pageKey || '').trim() }))
            .find((x) => x.pageKey === launchPageKey)
          if (target) {
            self.currentPageIndex = target.originalIndex
            launchTargetOriginalIndex = target.originalIndex
            const inNav = blocks.some((b) => b.originalIndex === target.originalIndex)
            self.jumpDetachedMode = strict && !inNav
          } else {
            self.currentPageIndex = startIdx
          }
        } else if (launch != null && launch >= 0 && launch < self.pages.length) {
          const strict = self.countPagesWithDisplayOrder(self.pages) > 0
          const inNav = blocks.some((b) => b.originalIndex === launch)
          self.currentPageIndex = launch
          self.jumpDetachedMode = strict && !inNav
        } else {
          self.currentPageIndex = startIdx
        }
        console.log('[activity-multi] page layout', {
          pagesLen: self.pages.length,
          navBlocksLen: blocks.length,
          currentPageIndex: self.currentPageIndex,
          jumpDetachedMode: self.jumpDetachedMode
        })

        // 打卡页背景模式：未配置时默认单背景，兼容旧数据
        self.backgroundSignMode = pageConfig.backgroundSignMode === 'composite' ? 'composite' : 'single'
        self.signCompositeBanner = pageConfig.signCompositeBanner || ''
        self.signCompositeRuleHtml = pageConfig.signCompositeRuleHtml || ''
        self.checkInBackgroundImgDefault = pageConfig.checkInBackgroundImg || ''
        // 是否需要授权手机号：与后台 page_config_json.phoneAuth 对齐
        self.phoneAuthRaw = pageConfig ? pageConfig.phoneAuth : undefined
        self.phoneAuthRequired = !!(pageConfig && (
          pageConfig.phoneAuth === 1 ||
          pageConfig.phoneAuth === '1' ||
          pageConfig.phoneAuth === true
        ))
        self.nicknameAuthRaw = pageConfig ? pageConfig.nicknameAuth : undefined
        self.nicknameAuthRequired = !!(pageConfig && (
          pageConfig.nicknameAuth === 1 ||
          pageConfig.nicknameAuth === '1' ||
          pageConfig.nicknameAuth === true
        ))
        self.checkinLimitPeriodType = String(pageConfig && pageConfig.checkinLimitPeriodType || '').trim().toUpperCase() || 'DAILY'
        if (self.checkinLimitPeriodType !== 'ALL' && self.checkinLimitPeriodType !== 'MONTHLY') {
          self.checkinLimitPeriodType = 'DAILY'
        }
        {
          const n = parseInt(pageConfig && pageConfig.dailyCheckinLimitReachedHotspotIndex, 10)
          self.dailyCheckinLimitReachedHotspotIndex = Number.isFinite(n) && n > 0 ? n : null
        }
        {
          const hasField = !!(pageConfig && Object.prototype.hasOwnProperty.call(pageConfig, 'pointCheckinMaxCountPerPoint'))
          const n = parseInt(pageConfig && pageConfig.pointCheckinMaxCountPerPoint, 10)
          self.pointCheckinMaxCountPerPoint = hasField && Number.isFinite(n) && n >= 0 ? n : 1
        }
        {
          self.pointCheckinLimitScopePointIndexes = Array.isArray(pageConfig && pageConfig.pointCheckinLimitScopePointIndexes)
            ? pageConfig.pointCheckinLimitScopePointIndexes
              .map((v) => parseInt(v, 10))
              .filter((v) => Number.isFinite(v) && v > 0)
            : []
        }
        {
          const n = parseInt(pageConfig && pageConfig.dailyActivityTotalRoundsLimitReachedHotspotIndex, 10)
          self.dailyActivityTotalRoundsLimitReachedHotspotIndex = Number.isFinite(n) && n > 0 ? n : null
        }

        {
          const rawNav = pageConfig && pageConfig.signPageNavTitle
          const navTitle =
            rawNav != null && String(rawNav).trim() !== ''
              ? String(rawNav).trim().slice(0, 32)
              : '活动详情'
          try {
            uni.setNavigationBarTitle({ title: navTitle })
          } catch (e) {
            /* ignore */
          }
          if (typeof document !== 'undefined' && document.title !== undefined) {
            document.title = navTitle
          }
        }

        const _signCtx0 = {
          originalIndex: self.currentPageIndex,
          page: self.pages && self.pages[self.currentPageIndex]
        }
        if (self.resolvePageBackgroundSignMode(_signCtx0) === 'composite') {
          if (!self.resolvePageSignCompositeBanner(_signCtx0)) self.signCompositeBannerLoaded = true
        }

        self.applyPageAtIndex(self.currentPageIndex)
        if (!self.backgroundSign && pageConfig.backgroundSign) {
          self.backgroundSign = pageConfig.backgroundSign
        }

        const _signCtx1 = {
          originalIndex: self.currentPageIndex,
          page: self.pages && self.pages[self.currentPageIndex]
        }
        if (self.resolvePageBackgroundSignMode(_signCtx1) !== 'composite') {
          const cur = self.pages[self.currentPageIndex] || {}
          const bg = String(self.backgroundSign || cur.background || '').trim()
          if (!bg) {
            uni.showToast({
              title: '未配置背景图',
              icon: 'none'
            })
            self.signSingleBgLoaded = true
          }
        }

        // 获取或创建用户参与记录
        await self.loadUserRecord()
        console.log('[activity-multi] after loadUserRecord', { activityUserId: self.activityUserId })

        // 加载已打卡的点位列表
        await self.loadCheckedPoints()
        console.log('[activity-multi] after loadCheckedPoints', {
          checkedLen: (self.checkedPointIndexes && self.checkedPointIndexes.length) || 0
        })

        await self.loadAct004UserFormProfile()
        await self.preloadSignPageImages()
        console.log('[activity-multi] after preloadSignPageImages')
        /**
         * 纯 H5：模板里 <img> 不是 uni-app 组件，@load 往往不触发，showBgHold 会一直为 true，
         * 全屏 loading-overlay 永不消失（表现为白屏）。预加载已完成，此处与小程序解耦直接放开挡层。
         */
        if (typeof window !== 'undefined') {
          const _signCtx2 = {
            originalIndex: self.currentPageIndex,
            page: self.pages && self.pages[self.currentPageIndex]
          }
          if (self.resolvePageBackgroundSignMode(_signCtx2) === 'composite') {
            self.signCompositeBannerLoaded = true
          } else {
            self.signSingleBgLoaded = true
          }
        }
        self.showLoading = false
        console.log('[activity-multi] queryActiveFun before nextTick', {
          showLoading: self.showLoading,
          showBgHold: self.showBgHold,
          useMultiPageScroll: self.useMultiPageScroll
        })
        self._activityHasLinkCheckinZone = self.detectActivityHasLinkCheckinZone()
        self.$nextTick(() => {
          self.debugActivityMulti('queryActiveFun-nextTick')
          self.ensureLinkCheckinZoneDocumentTouch()
          self.syncLinkCheckinZoneHitTargets()
          if (self.showBgHold) {
            console.log('[activity-multi] showBgHold still true, armBgHoldSafetyRelease')
            self.armBgHoldSafetyRelease()
          }
          if (self.useMultiPageScroll) {
            if (launchTargetOriginalIndex != null && !self.jumpDetachedMode) {
              self.scrollMpSectionIntoView('mp-' + launchTargetOriginalIndex)
            }
            setTimeout(() => self.measureMultiPageSnapLayout(), 120)
            setTimeout(() => self.measureMultiPageSnapLayout(), 450)
            setTimeout(() => self.measureMultiPageSnapLayout(), 900)
          }
        })
      } catch (error) {
        console.error('[activity-multi] queryActiveFun catch', error && error.message, error)
        self.clearBgHoldSafetyTimer()
        self.signSingleBgLoaded = true
        self.signCompositeBannerLoaded = true
        self.showLoading = false
        self.debugActivityMulti('queryActiveFun-catch', { err: error && error.message })
        uni.showToast({
          title: error.message || '加载活动失败',
          icon: 'none',
          duration: 2000
        })
      }
		},
    async loadUserRecord() {
      try {
        const params = {}
        if (this.activityId) {
          params.activityId = this.activityId
        } else if (this.activityCode) {
          params.activityCode = this.activityCode
        } else {
          return
        }

        const useH5Anonymous = this.h5AuthMode === 'anonymous' && this.h5Uid
        if (useH5Anonymous) {
          params.h5Uuid = this.h5Uid
        }

        const res = useH5Anonymous
          ? await getOrCreateUserRecordH5(params)
          : await getOrCreateUserRecord(params)

        if (isSignstoreUnauthorizedResponse(res)) {
          await this.recoverFromSignstoreUnauthorized(res)
          return
        }
        
        // 标准响应结构：{status: 200, data: {id: 4, ...}}
        if (res && res.status === 200 && res.data) {
          const userRecord = res.data
          
          // 设置用户打卡次数
          this.userFormData = userRecord.totalSealNum || 0
          // 设置已完成的活动数量（status = 3 和 4）
          this.completedCount = userRecord.completedCount || 0
          // 设置可核销的活动数量（status = 3）
          this.verifyableCount = userRecord.verifyableCount || 0
          // 保存用户活动参与记录ID
          this.activityUserId = userRecord.id || null
          const lotteryChanceRaw =
            userRecord.lotteryChanceNum != null
              ? userRecord.lotteryChanceNum
              : userRecord.lottery_chance_num
          const lotteryChance = parseInt(lotteryChanceRaw, 10)
          this.userLotteryChanceNum =
            Number.isFinite(lotteryChance) && lotteryChance > 0 ? lotteryChance : 0
          if (userRecord.uid != null) {
            this.signstoreUserUid = userRecord.uid
            this.mergeUidIntoH5UserInfo(userRecord.uid)
          }
          await this.loadActivityAvailablePoints()
          await this.loadCheckinPointRanks()
          if (this.activityUserId && Array.isArray(this.pages) && this.pages.length) {
            await this.prefetchAct004CheckinLimitBatch()
          }
          await this.loadShakeLotteryDrawRecords()
        }
      } catch (error) {
        // 失败时不影响页面显示，默认为0
        this.userFormData = 0
        this.completedCount = 0
        this.verifyableCount = 0
        this.activityUserId = null
        this.userActivityAvailablePoints = 0
        this.userLotteryChanceNum = 0
        this.checkinPointRankByPointIndex = {}
        this.clearAct004LimitDetailCache()
      }
    },
    collectDataHotspotRankPointIndexesFromHotspots(rawList) {
      const set = new Set()
      if (!Array.isArray(rawList)) return set
      rawList.forEach((h) => {
        if (!h || String(h.type) !== '9') return
        const dt = String(h.dataType || '').trim()
        if (dt !== 'CHECKIN_POINT_RANK') return
        const n = parseInt(h.dataBindPointIndex, 10)
        if (Number.isFinite(n) && n > 0) set.add(n)
      })
      return set
    },
    collectDataHotspotRankPointIndexes() {
      const set = new Set()
      if (Array.isArray(this.pages)) {
        this.pages.forEach((p) => {
          this.collectDataHotspotRankPointIndexesFromHotspots(p && p.hotspots).forEach((n) => set.add(n))
        })
      }
      this.collectDataHotspotRankPointIndexesFromHotspots(this.hotspots).forEach((n) => set.add(n))
      return Array.from(set)
    },
    collectDataHotspotRankPointIndexesForPage(pageOriginalIndex) {
      const pi = parseInt(pageOriginalIndex, 10)
      const pages = this.pages || []
      if (!Number.isFinite(pi) || pi < 0 || pi >= pages.length) return []
      return Array.from(this.collectDataHotspotRankPointIndexesFromHotspots(pages[pi] && pages[pi].hotspots))
    },
    pageHasUserActivityPointsDataHotspot(pageOriginalIndex) {
      const pi = parseInt(pageOriginalIndex, 10)
      const pages = this.pages || []
      if (!Number.isFinite(pi) || pi < 0 || pi >= pages.length) return false
      const list = pages[pi] && pages[pi].hotspots
      if (!Array.isArray(list)) return false
      return list.some(
        (h) => h && String(h.type) === '9' && String(h.dataType || '').trim() === 'USER_ACTIVITY_POINTS'
      )
    },
    buildDataHotspotRefreshKey(pageOriginalIndex, rankIndexes) {
      const pi = parseInt(pageOriginalIndex, 10)
      const au = this.activityUserId != null ? String(this.activityUserId) : '0'
      const idx = (Array.isArray(rankIndexes) ? rankIndexes : [])
        .map((n) => parseInt(n, 10))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join(',')
      return `${au}:${pi}:${idx}`
    },
    shouldSkipDataHotspotRefresh(key, force) {
      if (force) return false
      if (!key || key !== this._dataHotspotRefreshKey) return false
      const at = this._dataHotspotRefreshAt
      return at > 0 && Date.now() - at < 5000
    },
    clearDataHotspotRefreshTimer() {
      if (this._dataHotspotRefreshTimer) {
        clearTimeout(this._dataHotspotRefreshTimer)
        this._dataHotspotRefreshTimer = null
      }
    },
    clearAct004ProgressBarTimers() {
      const map = this._act004ProgressBarTimers
      if (!map || typeof map !== 'object') return
      Object.keys(map).forEach((k) => {
        if (map[k]) clearInterval(map[k])
      })
      this._act004ProgressBarTimers = {}
    },
    /** 进入页面时刷新本页数据热点（排行、可用积分）；防抖 + 去重，避免滚动时重复请求 */
    refreshPageDataHotspots(pageOriginalIndex, opts) {
      const options = opts && typeof opts === 'object' ? opts : {}
      const force = options.force === true
      const pi = parseInt(pageOriginalIndex, 10)
      if (!Number.isFinite(pi) || pi < 0) {
        return Promise.resolve()
      }
      const rankIndexes = this.collectDataHotspotRankPointIndexesForPage(pi)
      const needPoints = this.pageHasUserActivityPointsDataHotspot(pi)
      if (!rankIndexes.length && !needPoints) {
        return Promise.resolve()
      }
      const key = this.buildDataHotspotRefreshKey(pi, rankIndexes)
      if (this.shouldSkipDataHotspotRefresh(key, force)) {
        return Promise.resolve()
      }
      this.clearDataHotspotRefreshTimer()
      const delay = force ? 0 : 180
      return new Promise((resolve) => {
        this._dataHotspotRefreshTimer = setTimeout(() => {
          this._dataHotspotRefreshTimer = null
          if (this.shouldSkipDataHotspotRefresh(key, force)) {
            resolve()
            return
          }
          const run = async () => {
            const tasks = []
            if (rankIndexes.length) {
              tasks.push(this.loadCheckinPointRanks(rankIndexes, force))
            }
            if (needPoints) {
              tasks.push(this.loadActivityAvailablePoints())
            }
            if (tasks.length) {
              await Promise.all(tasks)
            }
            this._dataHotspotRefreshKey = key
            this._dataHotspotRefreshAt = Date.now()
          }
          if (this._dataHotspotRefreshPromise) {
            this._dataHotspotRefreshPromise
              .then(() => run())
              .then(() => resolve())
              .catch(() => resolve())
            return
          }
          this._dataHotspotRefreshPromise = run()
            .finally(() => {
              this._dataHotspotRefreshPromise = null
            })
          this._dataHotspotRefreshPromise.then(() => resolve()).catch(() => resolve())
        }, delay)
      })
    },
    async loadCheckinPointRanks(pointIndexes, force) {
      if (!this.activityUserId) {
        this.checkinPointRankByPointIndex = {}
        return
      }
      const indexes = (() => {
        if (Array.isArray(pointIndexes) && pointIndexes.length) {
          const out = []
          pointIndexes.forEach((raw) => {
            const n = parseInt(raw, 10)
            if (Number.isFinite(n) && n > 0 && !out.includes(n)) out.push(n)
          })
          return out
        }
        return this.collectDataHotspotRankPointIndexes()
      })()
      if (!indexes.length) {
        return
      }
      const prev = this.checkinPointRankByPointIndex || {}
      const next = { ...prev }
      let changed = false
      await Promise.all(indexes.map(async (pointIndex) => {
        if (!force && prev[pointIndex] != null) {
          return
        }
        try {
          const res = await getCheckinPointRank({
            activityUserId: this.activityUserId,
            pointIndex
          })
          if (res && res.status === 200 && res.data != null) {
            const n = parseInt(res.data, 10)
            const val = Number.isFinite(n) && n > 0 ? n : 0
            if (next[pointIndex] !== val) {
              next[pointIndex] = val
              changed = true
            }
          } else if (next[pointIndex] !== 0) {
            next[pointIndex] = 0
            changed = true
          }
        } catch (e) {
          if (next[pointIndex] !== 0) {
            next[pointIndex] = 0
            changed = true
          }
        }
      }))
      if (changed) {
        this.checkinPointRankByPointIndex = next
      }
    },
    async loadActivityAvailablePoints() {
      if (!this.activityUserId) {
        this.userActivityAvailablePoints = 0
        return
      }
      try {
        const res = await getActivityAvailablePoints({ activityUserId: this.activityUserId })
        if (res && res.status === 200 && res.data != null) {
          const n = parseInt(res.data, 10)
          const val = Number.isFinite(n) && n >= 0 ? n : 0
          if (this.userActivityAvailablePoints !== val) {
            this.userActivityAvailablePoints = val
          }
        } else if (this.userActivityAvailablePoints !== 0) {
          this.userActivityAvailablePoints = 0
        }
      } catch (e) {
        if (this.userActivityAvailablePoints !== 0) {
          this.userActivityAvailablePoints = 0
        }
      }
    },
    // 加载已打卡的点位列表
    async loadCheckedPoints() {
      if (!this.activityUserId) {
        return
      }
      try {
        const res = await getCheckedPoints({ activityUserId: this.activityUserId })
        if (res && res.status === 200 && res.data) {
          this.checkedPointIndexes = (Array.isArray(res.data) ? res.data : [])
            .map((v) => parseInt(v, 10))
            .filter((v) => Number.isFinite(v))
        }
      } catch (error) {
        console.error('加载已打卡点位列表失败:', error)
        this.checkedPointIndexes = []
      }
    },
    /** 页面是否含打卡热点（含图片嵌套打卡点），用于进入地图/打卡页时刷新已打卡列表 */
    pageHasAct004CheckinHotspots(pageIndex) {
      const idx = parseInt(pageIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) return false
      const page = (this.pages || [])[idx]
      const list = page && Array.isArray(page.hotspots) ? page.hotspots : []
      for (let i = 0; i < list.length; i++) {
        const h = list[i]
        if (!h) continue
        if (String(h.type) === '1') return true
        if (String(h.type) === '3') {
          const nested = h.imageNestedHotspotsForm || h.imageNestedHotspots || []
          if (!Array.isArray(nested)) continue
          for (let j = 0; j < nested.length; j++) {
            const checkin = nested[j] && (nested[j].checkinHotspot || nested[j].checkInHotspot)
            if (checkin && String(checkin.type) === '1') return true
          }
        }
      }
      return false
    },
    /**
     * 进入含打卡热点的活动页（如打卡地图）时拉取最新已打卡点位，避免跳转后状态与后台不一致
     */
    async refreshAct004CheckedPointsOnPageEnter(pageIndex) {
      const idx = parseInt(pageIndex, 10)
      if (!Number.isFinite(idx) || idx < 0 || !this.activityUserId) return
      if (!this.pageHasAct004CheckinHotspots(idx)) return

      if (!this._checkedPointsRefreshInflightByPage) {
        this._checkedPointsRefreshInflightByPage = {}
      }
      if (this._checkedPointsRefreshInflightByPage[idx]) {
        return this._checkedPointsRefreshInflightByPage[idx]
      }

      const now = Date.now()
      if (
        this._lastCheckedPointsRefreshPageIndex === idx &&
        now - (this._lastCheckedPointsRefreshAt || 0) < 600
      ) {
        return
      }

      const task = (async () => {
        await this.loadCheckedPoints()
        this._dataHotspotRefreshKey = ''
        await this.loadCheckinPointRanks(undefined, true)
        this._lastCheckedPointsRefreshPageIndex = idx
        this._lastCheckedPointsRefreshAt = Date.now()
      })()

      this._checkedPointsRefreshInflightByPage[idx] = task
      try {
        await task
      } finally {
        if (this._checkedPointsRefreshInflightByPage[idx] === task) {
          delete this._checkedPointsRefreshInflightByPage[idx]
        }
      }
    },
    /** 与接口返回的 number、配置里的 string 统一比较，避免 includes 误判 */
    isPointIndexChecked(pointIndex) {
      if (pointIndex == null || pointIndex === '') return false
      const n = parseInt(pointIndex, 10)
      if (!Number.isFinite(n)) return false
      return this.checkedPointIndexes.some((c) => parseInt(c, 10) === n)
    },
    /**
     * 解析热点「转圈时长(ms)」
     * @param {object} hotspot 映射后热点或 page_config 原始热点
     * @param {number|null} [fallback=2000] 传 null 表示未配置时不写入默认（映射用）
     */
    resolveCheckInLoadingDurationMs(hotspot, fallback) {
      const fb =
        arguments.length >= 2
          ? fallback
          : 2000
      if (!hotspot || typeof hotspot !== 'object') {
        return fb === null ? null : fb
      }
      const candidates = [
        hotspot.checkInLoadingDurationMs,
        hotspot.checkInLoadingDuration,
        hotspot.loadingDurationMs
      ]
      for (let i = 0; i < candidates.length; i++) {
        const v = candidates[i]
        if (v === undefined || v === null || v === '') continue
        if (typeof v === 'number' && Number.isFinite(v) && v >= 0) return Math.floor(v)
        const n = parseInt(v, 10)
        if (Number.isFinite(n) && n >= 0) return n
      }
      return fb === null ? null : fb
    },
    /** 从 page_config 原始热点（含图内嵌套打卡点）解析转圈时长 */
    resolveCheckInLoadingDurationMsFromRawHotspot(raw) {
      if (!raw || typeof raw !== 'object') return null
      let ms = this.resolveCheckInLoadingDurationMs(raw, null)
      if (ms != null) return ms
      const nested = raw.checkinHotspot || raw.checkInHotspot
      if (nested && typeof nested === 'object') {
        ms = this.resolveCheckInLoadingDurationMs(nested, null)
        if (ms != null) return ms
      }
      return null
    },
    /** 按点位 index 解析转圈时长：优先传入热点，其次 pages 原始配置 */
    resolveCheckInLoadingDurationMsForPoint(pointIndex, hintHotspot) {
      let ms = this.resolveCheckInLoadingDurationMs(hintHotspot, null)
      if (ms != null) return ms
      const pages = this.pages || []
      for (let pi = 0; pi < pages.length; pi++) {
        const rawList = pages[pi] && pages[pi].hotspots
        if (!Array.isArray(rawList)) continue
        for (let hi = 0; hi < rawList.length; hi++) {
          const raw = rawList[hi]
          if (String(raw && raw.type) === '3') {
            const nestedList =
              (raw.imageNestedHotspotsForm || raw.imageNestedHotspots || [])
            if (Array.isArray(nestedList)) {
              for (let ni = 0; ni < nestedList.length; ni++) {
                const nested = nestedList[ni]
                const checkin = nested && (nested.checkinHotspot || nested.checkInHotspot)
                const pt = checkin && checkin.index != null ? checkin.index : null
                if (String(pt) !== String(pointIndex)) continue
                ms = this.resolveCheckInLoadingDurationMsFromRawHotspot(checkin)
                if (ms != null) return ms
              }
            }
          }
          if (String(raw && raw.index) !== String(pointIndex)) continue
          ms = this.resolveCheckInLoadingDurationMsFromRawHotspot(raw)
          if (ms != null) return ms
        }
      }
      const hit = this.findHotspotWithPageByPointIndex(pointIndex)
      if (hit && hit.hotspot) {
        ms = this.resolveCheckInLoadingDurationMs(hit.hotspot, null)
        if (ms != null) return ms
      }
      return 2000
    },
    /** 打开打卡/核销会话时锁定转圈图与时长，避免后置阶段查库丢配置 */
    lockSealLoadingVisualFromHotspot(hotspot) {
      const hs = hotspot || this.currentHotspotData
      const pointIndex =
        hs && hs.index != null && hs.index !== ''
          ? hs.index
          : this.currentHotspotIndex
      this.sealLoadingDurationMsLocked = this.resolveCheckInLoadingDurationMsForPoint(pointIndex, hs)
      const img = hs ? String(hs.checkInLoadingCenterImg || '').trim() : ''
      this.sealLoadingCenterImgLocked = img
    },
    /** 转圈时长(ms) > 0 时才展示请求中 / 成功后的盖章转圈；配置 0 则全程不显示 */
    isAct004SealLoadingVisualEnabled(hotspot) {
      const hs = hotspot || this.currentHotspotData
      const pointIndex =
        this.currentHotspotIndex != null && this.currentHotspotIndex !== ''
          ? this.currentHotspotIndex
          : hs && hs.index != null && hs.index !== ''
            ? hs.index
            : null
      if (this.sealLoadingDurationMsLocked != null) {
        const locked = parseInt(this.sealLoadingDurationMsLocked, 10)
        if (Number.isFinite(locked) && locked >= 0) return locked > 0
      }
      const ms = this.resolveCheckInLoadingDurationMsForPoint(pointIndex, hs)
      return Number.isFinite(ms) && ms > 0
    },
    /** ACT004 印章比对接口是否打卡成功：matched === true */
    isAct004SealValidationMatched(res) {
      const raw = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
      return !!(raw && raw.matched === true)
    },
    /** 图片热点：是否展示「按钮图片（2）」 */
    act004ImageHotspotUseSecondButtonImg(hotspot) {
      if (!hotspot) return false
      const btn2 = String(hotspot.imageButtonImg2 || '').trim()
      if (!btn2) return false
      const raw = hotspot.imageButton2WhenCheckedIndexes
      if (!Array.isArray(raw) || !raw.length) return false
      const indexes = raw
        .map((v) => parseInt(v, 10))
        .filter((v) => Number.isFinite(v) && v > 0)
      if (!indexes.length) return false
      return indexes.every((n) => this.isPointIndexChecked(n))
    },
    act004ImageHotspotPrimaryButtonUnlocked(hotspot) {
      if (!hotspot) return true
      const raw = hotspot.imageButtonWhenCheckedIndexes
      if (!Array.isArray(raw) || !raw.length) return true
      const indexes = raw
        .map((v) => parseInt(v, 10))
        .filter((v) => Number.isFinite(v) && v > 0)
      if (!indexes.length) return true
      return indexes.every((n) => this.isPointIndexChecked(n))
    },
    parseHotspotNumber(value, fallback = 0) {
      const raw = String(value == null ? '' : value).replace(/%/g, '').replace(/,/g, '').trim()
      const num = parseFloat(raw)
      return Number.isFinite(num) ? num : fallback
    },
    getHotspotContainerStyle(hotspot) {
      const ww = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
      const left = this.parseHotspotNumber(hotspot && hotspot.left, 0) / 100 * 750
      const top = this.parseHotspotNumber(hotspot && hotspot.top, 0) / 100 * 1334
      const isCircle = !hotspot || hotspot.shape === 'circle'
      if (isCircle) {
        const diameter = this.parseHotspotNumber(hotspot && (hotspot.radius || hotspot.size), 80)
        return {
          left: rpxToPx(left, ww),
          top: rpxToPx(top, ww),
          width: rpxToPx(diameter, ww),
          height: rpxToPx(diameter, ww),
          borderRadius: '50%'
        }
      }
      const width = this.parseHotspotNumber(hotspot && (hotspot.sizeWidth || hotspot.size), 100)
      const height = this.parseHotspotNumber(hotspot && (hotspot.sizeHeight || hotspot.size), 100)
      return {
        left: rpxToPx(left, ww),
        top: rpxToPx(top, ww),
        width: rpxToPx(width, ww),
        height: rpxToPx(height, ww),
        borderRadius: '0'
      }
    },
    deferPostCheckInVisual(pointIndex) {
      if (pointIndex == null || pointIndex === '') return
      const key = String(pointIndex)
      if (!this.postCheckInDeferredVisualIndexes.includes(key)) {
        this.postCheckInDeferredVisualIndexes.push(key)
      }
    },
    clearDeferPostCheckInVisual(pointIndex) {
      const key = String(pointIndex)
      const arr = this.postCheckInDeferredVisualIndexes
      const i = arr.indexOf(key)
      if (i >= 0) arr.splice(i, 1)
    },
    isDeferredPostCheckInVisual(pointIndex) {
      return this.postCheckInDeferredVisualIndexes.includes(String(pointIndex))
    },
    /** 已打卡且配置了角标图时，在已打卡主图右下角叠加 */
    shouldShowCheckedCornerBadge(hotspot) {
      if (!hotspot || hotspot.index == null) return false
      if (!this.isPointIndexChecked(hotspot.index)) return false
      if (this.isDeferredPostCheckInVisual(hotspot.index)) return false
      return !!(String(hotspot.checkedBadgeImg || '').trim())
    },
    /** 角标容器：正方形边长 rpx，内图 object-fit: contain，不拉伸 */
    getHotspotCheckedBadgeBoxStyle(hotspot) {
      const n = parseInt(hotspot && hotspot.checkedBadgeSize, 10)
      const rpx = Number.isFinite(n) && n > 0 ? n : 64
      const px = this.rpxPx(rpx)
      return {
        width: px + 'px',
        height: px + 'px'
      }
    },
    getHotspotImageStyle(hotspot) {
      const ww = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
      const isCircle = !hotspot || hotspot.shape === 'circle'
      if (isCircle) {
        const diameter = this.parseHotspotNumber(hotspot && (hotspot.radius || hotspot.size), 80)
        return {
          width: rpxToPx(diameter, ww),
          height: rpxToPx(diameter, ww),
          borderRadius: '50%'
        }
      }
      const width = this.parseHotspotNumber(hotspot && (hotspot.sizeWidth || hotspot.size), 100)
      const height = this.parseHotspotNumber(hotspot && (hotspot.sizeHeight || hotspot.size), 100)
      return {
        width: rpxToPx(width, ww),
        height: rpxToPx(height, ww),
        borderRadius: '0'
      }
    },
    /** H5：配置里多为相对路径，与 activity3-1 一致走 CDN 前缀（小程序由运行时处理） */
    hotspotAssetUrl(u) {
      const s = u == null ? '' : String(u).trim()
      if (!s) return ''
      return this.getImageUrl(s)
    },
    isImageFullHotZoneHotspot(hotspot) {
      return !!(hotspot && (hotspot.imageFullHotZone === 1 || hotspot.imageFullHotZone === '1'))
    },
    isAct004ImageHotspotFloatEnabled(hotspot) {
      if (!hotspot) return false
      if (String(hotspot.type) !== '15') return false
      if (!(hotspot.imageFloatEnabled === 1 || hotspot.imageFloatEnabled === true || hotspot.imageFloatEnabled === '1')) return false
      const use2 = this.act004ImageHotspotUseSecondButtonImg(hotspot)
      if (use2 && String(hotspot.imageButtonImg2 || '').trim()) return true
      if (String(hotspot.imageButtonImg || '').trim() && this.act004ImageHotspotPrimaryButtonUnlocked(hotspot)) return true
      return false
    },
    clampAct004ImageFloatRangeRpx(hotspot) {
      const n = parseInt(hotspot && hotspot.imageFloatRangeRpx, 10)
      return Number.isFinite(n) ? Math.max(1, Math.min(200, n)) : 24
    },
    getAct004ImageFloatDirection(hotspot) {
      const d = String((hotspot && hotspot.imageFloatDirection) || 'VERTICAL').toUpperCase()
      return d === 'HORIZONTAL' || d === 'LR' || d === 'X' ? 'HORIZONTAL' : 'VERTICAL'
    },
    getAct004ImageFloatWrapStyle(hotspot) {
      if (!this.isAct004ImageHotspotFloatEnabled(hotspot)) return {}
      const r = this.clampAct004ImageFloatRangeRpx(hotspot)
      const px = this.rpxPx(r)
      const fr = (typeof px === 'number' && Number.isFinite(px) ? px : parseFloat(px)) + 'px'
      const horiz = this.getAct004ImageFloatDirection(hotspot) === 'HORIZONTAL'
      return {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '--act004-fr': fr,
        animation: (horiz ? 'act004FloatX' : 'act004FloatY') + ' 2.4s ease-in-out infinite'
      }
    },
    isUserFormInputHotspot(hotspot) {
      return !!(hotspot && String(hotspot.type) === '16')
    },
    /**
     * 从接口拉取 yx_user 表单资料（条件跳转前必须调用，以库内数据为准）
     * @returns {Promise<boolean>} 是否成功拿到 profile
     */
    async refreshAct004ProfileForConditionalJump(syncDraft = true) {
      try {
        const res = await getAct004UserFormProfile()
        if (isSignstoreUnauthorizedResponse(res)) {
          await this.recoverFromSignstoreUnauthorized(res)
          return false
        }
        const data = res && res.data != null ? res.data : res
        if (res && Number(res.status) === 200 && data && typeof data === 'object') {
          this.activityUserFormProfile = { ...data }
          if (syncDraft) {
            this.syncUserFormDraftFromProfile()
          }
          return true
        }
        this.activityUserFormProfile = {}
        if (syncDraft) {
          this.syncUserFormDraftFromProfile()
        }
        return false
      } catch (e) {
        console.warn('[activity-multi] refreshAct004ProfileForConditionalJump', e)
        this.activityUserFormProfile = {}
        if (syncDraft) {
          this.syncUserFormDraftFromProfile()
        }
        return false
      }
    },
    async loadAct004UserFormProfile() {
      await this.refreshAct004ProfileForConditionalJump(true)
    },
    syncUserFormDraftFromProfile() {
      const profile = this.activityUserFormProfile || {}
      const draft = { ...(this.activityUserFormDraft || {}) }
      const all = this.collectUserFormInputHotspots('activity')
      all.forEach((h) => {
        const bind = String(h.userFieldBind || 'phone').trim()
        const key = act004UserFormDraftKey(h)
        // 以服务端资料为准覆盖草稿，避免空资料仍残留未提交输入导致条件跳转误判
        draft[key] = profile[bind] != null ? String(profile[bind]) : ''
      })
      this.activityUserFormDraft = draft
    },
    collectUserFormInputHotspots(scope, pageOriginalIndex) {
      const out = []
      const pages = this.pages || []
      if (scope === 'activity') {
        pages.forEach((page, pi) => {
          ;(page && page.hotspots || []).forEach((h) => {
            if (String(h && h.type) === '16') out.push({ ...h, _pageIndex: pi })
          })
        })
        return out
      }
      const pi = pageOriginalIndex != null ? pageOriginalIndex : this.currentPageIndex
      const page = pages[pi]
      ;(page && page.hotspots || []).forEach((h) => {
        if (String(h && h.type) === '16') out.push({ ...h, _pageIndex: pi })
      })
      return out
    },
    getUserFormDraftValue(hotspot) {
      const draft = this.activityUserFormDraft || {}
      const fromDraft = readUserFormDraftByHotspot(draft, hotspot)
      if (fromDraft !== '') return fromDraft
      const bind = String(hotspot && hotspot.userFieldBind || 'phone').trim()
      const profile = this.activityUserFormProfile || {}
      return profile[bind] != null ? String(profile[bind]) : ''
    },
    onUserFormDraftInput(hotspot, e) {
      let val = e && e.target ? e.target.value : ''
      const maxLen = this.userFormInputMaxLength(hotspot)
      if (val.length > maxLen) {
        val = val.slice(0, maxLen)
        if (e && e.target) {
          e.target.value = val
        }
      }
      const key = act004UserFormDraftKey(hotspot)
      this.$set(this.activityUserFormDraft, key, val)
    },
    userFormInputMaxLength(hotspot) {
      const n = parseInt(hotspot && hotspot.formInputMaxLength, 10)
      return Number.isFinite(n) ? Math.max(1, Math.min(500, n)) : 30
    },
    userFormInputLabel(hotspot) {
      const custom = hotspot && String(hotspot.formInputLabel || '').trim()
      if (custom) return custom
      return act004UserFormFieldLabel(hotspot && hotspot.userFieldBind)
    },
    userFormInputPlaceholder(hotspot) {
      const custom = hotspot && String(hotspot.formInputPlaceholder || '').trim()
      if (custom) return custom
      return '请输入' + act004UserFormFieldLabel(hotspot && hotspot.userFieldBind)
    },
    userFormInputType(hotspot) {
      return act004InputTypeForField(hotspot && hotspot.userFieldBind)
    },
    getUserFormInputBgUrl(hotspot) {
      const btn = String(hotspot && hotspot.formInputButtonImg || '').trim()
      return btn ? this.hotspotAssetUrl(btn) : ''
    },
    getHotspotUserFormInputBgStyle(hotspot) {
      return this.getHotspotImageStyle(hotspot)
    },
    userFormInputFontSizeRpx(hotspot) {
      const n = parseInt(hotspot && hotspot.formInputFontSize, 10)
      if (Number.isFinite(n)) return Math.max(12, Math.min(120, n))
      const legacy = parseInt(hotspot && hotspot.dataFontSize, 10)
      if (Number.isFinite(legacy)) return Math.max(12, Math.min(120, legacy))
      return 30
    },
    userFormInputPaddingLeftRpx(hotspot) {
      const n = parseInt(hotspot && hotspot.formInputPaddingLeft, 10)
      return Number.isFinite(n) ? Math.max(0, Math.min(120, n)) : 24
    },
    userFormInputBoxHeightRpx(hotspot) {
      if (!hotspot) return 80
      if (hotspot.shape === 'rectangle') {
        return parseInt(hotspot.sizeHeight || hotspot.size, 10) || 80
      }
      return parseInt(hotspot.radius || hotspot.size, 10) || 80
    },
    getUserFormInputControlStyle(hotspot) {
      const boxH = this.userFormInputBoxHeightRpx(hotspot)
      const fontSize = this.userFormInputFontSizeRpx(hotspot)
      return {
        fontSize: this.rpxPx(fontSize),
        paddingLeft: this.rpxPx(this.userFormInputPaddingLeftRpx(hotspot)),
        paddingRight: this.rpxPx(8),
        lineHeight: this.rpxPx(boxH),
        height: this.rpxPx(boxH)
      }
    },
    buildUserFormFieldsPayload(scope, pageOriginalIndex) {
      const fields = {}
      const list = this.collectUserFormInputHotspots(scope, pageOriginalIndex)
      list.forEach((h) => {
        const bind = String(h.userFieldBind || '').trim()
        if (!bind) return
        let v = this.getUserFormDraftValue(h)
        const maxLen = this.userFormInputMaxLength(h)
        if (v.length > maxLen) v = v.slice(0, maxLen)
        fields[bind] = v
      })
      return fields
    },
    applyAct004AuthPayload(payload, options) {
      if (!payload || typeof window === 'undefined') return
      const opts = options || {}
      const token = payload.token
      const userInfo = payload.userInfo
      if (opts.phoneMemberSwitch && token && userInfo && userInfo.uid != null) {
        persistSignstoreH5LoginPayload(payload, 'member', userInfo.uid)
        this.h5AuthMode = 'member'
        this.signstoreUserUid = userInfo.uid
        return
      }
      if (token) {
        try {
          window.localStorage.setItem('token', token)
        } catch (e) {
          // ignore
        }
      }
      if (userInfo) {
        try {
          window.localStorage.setItem('signstore_h5_user_info', JSON.stringify(userInfo))
        } catch (e) {
          // ignore
        }
        if (userInfo.uid != null) {
          this.signstoreUserUid = userInfo.uid
        }
      }
      if (this.h5AuthMode === 'anonymous' && this.h5Uid) {
        markSignstoreH5Auth('anonymous', this.h5Uid)
      }
    },
    /** 表单提交成功后刷新参与记录与打卡点位（合并手机号账户 / 仅更新匿名账户） */
    async reloadAct004AfterFormSubmit(data) {
      if (data && data.token && data.mergedFromAnonymous) {
        this.applyAct004AuthPayload(data, { phoneMemberSwitch: true })
      }
      if (data && data.profile) {
        this.activityUserFormProfile = { ...data.profile }
        this.syncUserFormDraftFromProfile()
      }
      this.activityUserId = null
      this._lastCheckedPointsRefreshPageIndex = null
      this._lastCheckedPointsRefreshAt = 0
      await this.loadUserRecord()
      await this.loadCheckedPoints()
      if (data && data.mergedFromAnonymous) {
        this.clearAct004LimitDetailCache()
        if (this.activityUserId && Array.isArray(this.pages) && this.pages.length) {
          await this.prefetchAct004CheckinLimitBatch()
        }
      }
    },
    /**
     * 401 / 非法访问：清 token 后重登；匿名优先保留 h5_uid，仅仍失败才换新用户
     * @returns {boolean} 是否已处理（调用方应中止当前流程）
     */
    async recoverFromSignstoreUnauthorized(res) {
      if (!isSignstoreUnauthorizedResponse(res)) return false
      if (this._signstoreUnauthorizedReentryAttempted) return true
      this._signstoreUnauthorizedReentryAttempted = true

      const q = mergeActivityEntryQuery(this.$route)
      const uidRaw = q.uid != null ? String(q.uid).trim() : ''
      const activityCodeStr = String(
        this.activityCode || q.activity_code || q.activityCode || ''
      ).trim()
      const effectiveUid = resolveActivityThirdPartUid(activityCodeStr, uidRaw)

      if (effectiveUid || (this.h5AuthMode === 'thirdPart' && !shouldIgnoreUrlUidForActivity(activityCodeStr))) {
        clearSignstoreH5AuthSession()
        clearSignstoreH5UserInfo()
        if (!activityCodeStr) {
          uni.showToast({
            title: getSignstoreUnauthorizedMessage(res) || '非法访问',
            icon: 'none',
            duration: 2800
          })
          return true
        }
        try {
          const loginRes = await h5ThirdPartSilentLogin({
            uid: effectiveUid || uidRaw,
            activityCode: activityCodeStr
          })
          if (
            isThirdPartUidLoginRejectedResponse(loginRes) ||
            shouldIgnoreUrlUidForActivity(activityCodeStr)
          ) {
            this.h5AuthMode = 'anonymous'
            this.h5Uid = this.h5Uid || ensureSignstoreH5Uid(q.h5_uid)
            const auth = await ensureSignstoreAnonymousLoggedIn(this.h5Uid, h5AnonymousSilentLogin)
            this.h5Uid = auth.h5Uid
            if (auth.loginRes && auth.loginRes.data) {
              this.applyAct004AuthPayload(auth.loginRes.data)
            }
            this.activityUserId = null
            this.activityUserFormProfile = {}
            this.activityUserFormDraft = {}
            await this.loadUserRecord()
            await this.loadCheckedPoints()
            return true
          }
          if (isSignstoreUnauthorizedResponse(loginRes)) {
            throw new Error(getSignstoreUnauthorizedMessage(loginRes) || '非法访问')
          }
          if (!loginRes || loginRes.status !== 200 || !loginRes.data || !loginRes.data.token) {
            throw new Error((loginRes && loginRes.msg) || '静默登录失败')
          }
          this.h5AuthMode = 'thirdPart'
          this.applyAct004AuthPayload(loginRes.data)
          markSignstoreH5Auth('thirdPart', effectiveUid || uidRaw)
          this.activityUserId = null
          this.activityUserFormProfile = {}
          this.activityUserFormDraft = {}
          await this.loadUserRecord()
          await this.loadCheckedPoints()
          return true
        } catch (e) {
          uni.showToast({
            title: (e && e.message) || '登录失败',
            icon: 'none',
            duration: 2800
          })
          return true
        }
      }

      try {
        this.h5AuthMode = 'anonymous'
        this.h5Uid = this.h5Uid || ensureSignstoreH5Uid(q.h5_uid)
        const auth = await ensureSignstoreAnonymousLoggedIn(this.h5Uid, h5AnonymousSilentLogin)
        this.h5Uid = auth.h5Uid
        if (auth.loginRes && auth.loginRes.data) {
          this.applyAct004AuthPayload(auth.loginRes.data)
        }
        this.activityUserId = null
        this.activityUserFormProfile = {}
        this.activityUserFormDraft = {}
        await this.loadUserRecord()
        await this.loadCheckedPoints()
        if (auth.rotated) {
          uni.showToast({ title: '登录已刷新，请继续操作', icon: 'none', duration: 2200 })
        }
        return true
      } catch (e) {
        uni.showToast({
          title: (e && e.message) || '非法访问',
          icon: 'none',
          duration: 2800
        })
        return true
      }
    },
    async ensureH5AnonymousToken(forceRefresh) {
      if (typeof window === 'undefined') return
      if (this.h5AuthMode === 'member') {
        return
      }
      const q = (this.$route && this.$route.query) || {}
      if (!this.h5Uid) {
        this.h5Uid = ensureSignstoreH5Uid(q.h5_uid)
      }
      if (!forceRefresh && canReuseSignstoreAnonymousToken(this.h5Uid)) {
        return
      }
      const auth = await ensureSignstoreAnonymousLoggedIn(
        forceRefresh ? this.h5Uid : (q.h5_uid != null ? q.h5_uid : this.h5Uid),
        h5AnonymousSilentLogin
      )
      this.h5Uid = auth.h5Uid
      if (auth.loginRes && auth.loginRes.data) {
        this.applyAct004AuthPayload(auth.loginRes.data)
      }
    },
    async handleUserFormSubmitHotspot(hotspot, pageOriginalIndex) {
      if (this.activityUserFormSubmitting) return
      if (typeof this.needsProfileAuth === 'function' && this.needsProfileAuth()) {
        this.openAuthPanel()
        return
      }
      if (this.h5AuthMode !== 'thirdPart' && this.h5AuthMode !== 'member' && typeof window !== 'undefined') {
        if (!this.h5Uid) {
          const q = this.$route && this.$route.query ? this.$route.query : {}
          this.h5Uid = ensureSignstoreH5Uid(q.h5_uid)
        }
        if (this.h5AuthMode !== 'anonymous') {
          this.h5AuthMode = 'anonymous'
        }
        clearIncompatibleSignstoreAuthForAnonymous(this.h5Uid)
      }
      try {
        if (this.h5AuthMode !== 'member') {
          await this.ensureH5AnonymousToken()
        }
      } catch (e) {
        uni.showToast({
          title: (e && e.message) || '请先完成登录',
          icon: 'none',
          duration: 2800
        })
        return
      }
      const scope = hotspot && String(hotspot.formSubmitScope || 'page').trim().toLowerCase() === 'activity'
        ? 'activity'
        : 'page'
      const fields = this.buildUserFormFieldsPayload(scope, pageOriginalIndex)
      const keys = Object.keys(fields)
      if (!keys.length) {
        uni.showToast({ title: '当前范围无表单输入热点', icon: 'none' })
        return
      }
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i]
        const v = String(fields[k] || '').trim()
        if (!v) {
          uni.showToast({ title: act004UserFormFieldLabel(k) + '不能为空', icon: 'none' })
          return
        }
      }
      this.activityUserFormSubmitting = true
      try {
        let res = await submitAct004UserForm({
          activityCode: this.activityCode,
          scope,
          pageIndex: pageOriginalIndex != null ? pageOriginalIndex : this.currentPageIndex,
          fields
        })
        if (isSignstoreUnauthorizedResponse(res)) {
          await this.recoverFromSignstoreUnauthorized(res)
          return
        }
        if (!res || res.status !== 200 || res.success === false) {
          uni.showToast({
            title: (res && res.msg) ? String(res.msg) : '保存失败',
            icon: 'none',
            duration: 2800
          })
          return
        }
        const data = res.data != null ? res.data : res
        await this.reloadAct004AfterFormSubmit(data)
        const tip = hotspot && String(hotspot.formSubmitSuccessText || '').trim()
        uni.showToast({
          title: tip || '保存成功',
          icon: 'success'
        })
        this.handleFormSubmitSuccessJump(hotspot)
      } catch (e) {
        const msg = (e && e.message) || (e && e.msg) || '保存失败'
        uni.showToast({ title: String(msg), icon: 'none', duration: 2800 })
      } finally {
        this.activityUserFormSubmitting = false
      }
    },
    isDataHotspot(hotspot) {
      return String(hotspot && hotspot.type) === '9'
    },
    getDataHotspotText(hotspot) {
      if (!hotspot || !this.isDataHotspot(hotspot)) return ''
      const dataType = String(hotspot.dataType || '').trim()
      if (dataType === 'USER_ACTIVITY_POINTS') {
        return `${this.userActivityAvailablePoints || 0}`
      }
      if (dataType === 'USER_LOTTERY_CHANCE') {
        const n = parseInt(this.userLotteryChanceNum, 10)
        return `${Number.isFinite(n) && n > 0 ? n : 0}`
      }
      if (dataType === 'CHECKIN_POINT_RANK') {
        const pi = parseInt(hotspot.dataBindPointIndex, 10)
        if (!Number.isFinite(pi) || pi <= 0) return '-'
        const rank = this.checkinPointRankByPointIndex[pi]
        if (rank == null) return '...'
        if (rank <= 0) return '-'
        return `${rank}`
      }
      return ''
    },
    getDataHotspotTextStyle(hotspot) {
      const size = normalizeAct004DataFontSize(hotspot && hotspot.dataFontSize)
      const bold = normalizeAct004DataFontBold(hotspot && hotspot.dataFontBold) === 1
      const color = normalizeAct004DataFontColor(hotspot && hotspot.dataFontColor)
      const fontFamily = resolveAct004DataHotspotFontFamilyCss(hotspot && hotspot.dataFontFamily)
      return {
        fontSize: this.rpxPx(size),
        fontWeight: bold ? 700 : 400,
        color,
        fontFamily,
        lineHeight: 1.2
      }
    },
    getHotspotVerifyConditionContext() {
      return {
        checkedPointIndexes: this.checkedPointIndexes || [],
        act004LimitDetailByPoint: this.act004LimitDetailByPoint || {},
        moment
      }
    },
    shouldShowHotspotNotUnlockedVisual(hotspot) {
      if (!hotspot || !hotspot.index) return false
      if (this.isPointIndexChecked(hotspot.index)) return false
      return shouldUseHotspotNotUnlockedImg(hotspot, this.getHotspotVerifyConditionContext())
    },
    toastHotspotVerifyConditionLocked() {
      uni.showToast({
        title: HOTSPOT_VERIFY_CONDITION_LOCKED_TOAST,
        icon: 'none',
        duration: 2200
      })
    },
    getHotspotImage(hotspot) {
      if (!hotspot) {
        return ''
      }
      if (this.isDataHotspot(hotspot)) {
        return ''
      }
      if (this.isImageFullHotZoneHotspot(hotspot)) {
        return ''
      }
      if (String(hotspot.type) === '16') {
        return ''
      }
      if (String(hotspot.type) === '17') {
        const btn = String(hotspot.formSubmitButtonImg || '').trim()
        return btn ? this.hotspotAssetUrl(btn) : ''
      }
      if (String(hotspot.type) === '21') {
        const key = this.getShakeHotspotKey(hotspot)
        const verified = key && this.shakeLotteryVerifyStatusByHotspotKey[key] === 1
        if (verified) {
          const vImg = (key && this.shakeVerifiedPrizeImageByHotspotKey[key])
            || hotspot.shakeVerifiedPrizeImage
            || ''
          const img = String(vImg || '').trim()
          return img ? this.hotspotAssetUrl(img) : ''
        }
        const wonRaw = key && this.shakeWonPrizeImageByHotspotKey[key]
          ? this.shakeWonPrizeImageByHotspotKey[key]
          : (hotspot.shakeWonPrizeImage || '')
        const img = String(wonRaw || hotspot.shakeBeforeImage || '').trim()
        return img ? this.hotspotAssetUrl(img) : ''
      }
      if (String(hotspot.type) === '3' || String(hotspot.type) === '15') {
        const use2 = this.act004ImageHotspotUseSecondButtonImg(hotspot)
        if (use2 && String(hotspot.imageButtonImg2 || '').trim()) {
          return this.hotspotAssetUrl(String(hotspot.imageButtonImg2).trim())
        }
        const primary = String(hotspot.imageButtonImg || '').trim()
        if (!primary) return ''
        if (!this.act004ImageHotspotPrimaryButtonUnlocked(hotspot)) return ''
        return this.hotspotAssetUrl(primary)
      }
      // 跳转类型：仅使用「跳转按钮图片」，不沿用打卡未/已打卡图逻辑
      if (String(hotspot.type) === '4') {
        const j = hotspot.jumpButtonImg
        return j ? this.hotspotAssetUrl(j) : ''
      }
      if (!hotspot.index) {
        return this.hotspotAssetUrl(hotspot.notSignImg)
      }

      // 如果该热点的 index 在已打卡列表中，显示已打卡图片
      if (this.isPointIndexChecked(hotspot.index)) {
        const u = hotspot.doSignImg || hotspot.notSignImg || ''
        return this.hotspotAssetUrl(u)
      }

      // 检查是否是兑换类型的热点
      if (hotspot.type === '6') {
        const exchangeState = this.getExchangeHotspotState(hotspot)
        // 优先级：不可核销 > 库存不足 > 未解锁图 > 未兑换图
        if (exchangeState === 'LIMIT_REACHED' && hotspot.notVerifyImg) {
          return this.hotspotAssetUrl(hotspot.notVerifyImg)
        }
        if (exchangeState === 'OUT_OF_STOCK' && hotspot.stockInsufficientImg) {
          return this.hotspotAssetUrl(hotspot.stockInsufficientImg)
        }
      }

      if (this.shouldShowHotspotNotUnlockedVisual(hotspot)) {
        const locked = String(hotspot.notUnlockedImg || '').trim()
        if (locked) return this.hotspotAssetUrl(locked)
      }

      // 默认显示未打卡/未兑换图片
      return this.hotspotAssetUrl(hotspot.notSignImg)
    },
    getExchangeHotspotState(hotspot) {
      if (!hotspot || hotspot.type !== '6') return 'NORMAL'
      const isChecked = !!(hotspot.index && this.isPointIndexChecked(hotspot.index))
      if (isChecked) return 'ALREADY_CHECKED'

      // 1) 优先判断核销量限制（当前页面兑换总成功数）
      if (hotspot.limitVerifyCount) {
        const limit = Number(hotspot.verifyCountLimit || 0)
        if (limit > 0) {
          const currentPageExchangedCount = this.hotspots
            .filter(h => h && h.type === '6' && this.isPointIndexChecked(h.index))
            .length
          if (currentPageExchangedCount >= limit) return 'LIMIT_REACHED'
        }
      }

      // 2) 再判断库存不足
      if (hotspot.hasStock) {
        const total = Number(hotspot.stockQuantity || 0)
        const used = Number(hotspot.usedStockQuantity || 0)
        const availableStock = total - used
        if (availableStock <= 0) return 'OUT_OF_STOCK'
      }

      // 3) 默认可兑换
      return 'NORMAL'
    },
    openCurrentHotspotLocation() {
      const hotspot = this.currentHotspotData
      if (!hotspot) {
        uni.showToast({ title: '未找到打卡点位', icon: 'none' })
        return
      }
      const latitude = parseFloat(hotspot.latitude)
      const longitude = parseFloat(hotspot.longitude)
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        uni.showToast({ title: '该点位未配置经纬度', icon: 'none' })
        return
      }
      const pointName = `打卡点位${hotspot.index || ''}`
      uni.openLocation({
        latitude,
        longitude,
        name: pointName,
        address: hotspot.locationAddress || '',
        scale: 18,
        fail: () => {
          uni.showToast({ title: '拉起导航失败', icon: 'none' })
        }
      })
    },
    /** 从 pageConfigJson 原始热点读取 allowClickCheckin（避免运行时对象缺字段） */
    resolveAct004AllowClickCheckinFromPages(pointIndex) {
      if (pointIndex == null || pointIndex === '') return null
      const pages = this.pages || []
      const parseNestedList = (obj) => {
        if (!obj || typeof obj !== 'object') return []
        if (Array.isArray(obj.imageNestedHotspotsForm)) return obj.imageNestedHotspotsForm
        if (Array.isArray(obj.imageNestedHotspots)) return obj.imageNestedHotspots
        return []
      }
      let foundRaw = null
      for (let pi = 0; pi < pages.length; pi++) {
        const rawList = pages[pi] && pages[pi].hotspots
        if (!Array.isArray(rawList)) continue
        for (let hi = 0; hi < rawList.length; hi++) {
          const parent = rawList[hi]
          if (String(parent && parent.type) === '3') {
            const nestedList = parseNestedList(parent)
            for (let ni = 0; ni < nestedList.length; ni++) {
              const nested = nestedList[ni]
              const checkin = nested && (nested.checkinHotspot || nested.checkInHotspot)
              const pt = checkin && checkin.index != null ? checkin.index : null
              if (String(pt) === String(pointIndex)) {
                foundRaw = checkin
                break
              }
            }
          }
          if (foundRaw) break
          const raw = rawList.find((h) => String(h && h.index) === String(pointIndex))
          if (raw) {
            foundRaw = raw
            break
          }
        }
        if (foundRaw) break
      }
      if (!foundRaw) return null
      return readAllowClickCheckinValue(foundRaw)
    },
    isAct004HotspotClickCheckinEnabled(hotspot) {
      if (!hotspot) return true
      if (String(hotspot.type) !== '1') return true
      if (String(hotspot.checkType || 'CHECKIN').toUpperCase() === 'VERIFY') return true
      const idx = hotspot.index
      if (idx != null && idx !== '') {
        const fromPages = this.resolveAct004AllowClickCheckinFromPages(idx)
        if (fromPages != null) return fromPages === 1
      }
      return readAllowClickCheckinValue(hotspot) === 1
    },
    onAct004HotspotTap(hotspot, index, pageOriginalIndex) {
      if (!hotspot) return
      if (isAct004LinkCheckinZoneHotspot(hotspot)) {
        return
      }
      const pi = pageOriginalIndex != null ? pageOriginalIndex : this.currentPageIndex
      if (this.isCheckinHotspotBlockedByLinkZone(hotspot, pi)) {
        return
      }
      if (String(hotspot.type) === '1') {
        const isChecked = !!(hotspot.index && this.isPointIndexChecked(hotspot.index))
        if (!isChecked && !this.isAct004HotspotClickCheckinEnabled(hotspot)) {
          return
        }
      }
      return this.handleHotspotClick(hotspot, index, pageOriginalIndex)
    },
    // 热点点击：先限次预检 → 再前置 → 再打开盖章；盖章校验通过后由 doValidateSeal 调 hotspotPostCheckInSuccess
    async handleHotspotClick(hotspot, index, pageOriginalIndex) {
      if (hotspot && (String(hotspot.type) === '18' || String(hotspot.type) === '19')) {
        return
      }
      if (isAct004LinkCheckinZoneHotspot(hotspot)) {
        return
      }
      if (hotspot && String(hotspot.type) === '21') {
        if (this.isShakeHotspotAlreadyWon(hotspot)) {
          uni.showToast({
            title: '您已参与过该摇一摇',
            icon: 'none',
            duration: 2000
          })
          return
        }
        if (this.needsShakeMotionPermission()) {
          const granted = await this.requestShakeMotionPermissionIfNeeded()
          if (!granted) {
            uni.showToast({
              title: '需要允许访问运动与方向才能摇一摇',
              icon: 'none',
              duration: 2500
            })
            return
          }
          this._shakeMotionPermissionGranted = true
          uni.showToast({
            title: '授权成功，请摇晃手机',
            icon: 'none',
            duration: 2000
          })
        }
        this.syncShakeLotteryListener()
        return
      }
      if (this.isDataHotspot(hotspot)) {
        return
      }
      if (this.isAct004QuizInteractionActive()) {
        return
      }
      if (pageOriginalIndex != null && pageOriginalIndex !== this.currentPageIndex) {
        this.applyPageAtIndex(pageOriginalIndex)
      }
      console.log('[activity3-1] 热点点击总入口: hotspot, index', hotspot, index)
      const isCheckedAtEntry = !!(hotspot && hotspot.index && this.isPointIndexChecked(hotspot.index))
      // 打卡点不可点击且未打卡：静默结束，跳过限次/前置 Toast
      if (
        hotspot &&
        String(hotspot.type) === '1' &&
        !this.isAct004HotspotClickCheckinEnabled(hotspot) &&
        !isCheckedAtEntry
      ) {
        return
      }
      const limitCheckAtEntry = await this.checkAct004DailyLimitBeforeCheckin(hotspot, isCheckedAtEntry)
      if (limitCheckAtEntry && limitCheckAtEntry.blocked) {
        const preferredLimitHotspotIndex = parseInt(limitCheckAtEntry.limitReachedHotspotIndex, 10)
        if (limitCheckAtEntry.reason && !(Number.isFinite(preferredLimitHotspotIndex) && preferredLimitHotspotIndex > 0)) {
          uni.showToast({
            title: limitCheckAtEntry.reason,
            icon: 'none',
            duration: 2200
          })
          return
        }
        const pointIdx = parseInt(hotspot && hotspot.index, 10)
        const scope = Array.isArray(this.pointCheckinLimitScopePointIndexes) ? this.pointCheckinLimitScopePointIndexes : []
        const pointLimitEnabled = Number.isFinite(this.pointCheckinMaxCountPerPoint) && this.pointCheckinMaxCountPerPoint > 0
        const inPointScope = Number.isFinite(pointIdx) && pointIdx > 0 && (!scope.length || scope.includes(pointIdx))
        if (pointLimitEnabled && inPointScope) {
          uni.showToast({
            title: '当前点位打卡次数已达上限',
            icon: 'none',
            duration: 2000
          })
          return
        }
        await this.showDailyCheckinLimitReachedFeedback(hotspot, limitCheckAtEntry.limitReachedHotspotIndex)
        return
      }
      // ① 前置：拦截分支处理完则不再往下走
      const pre = await this.hotspotPreActions(hotspot, index, pageOriginalIndex)
      if (!pre.continue) {
        return
      }
      // ② 主流程：问答结束后先回到打卡页再弹盖章层
      await this.$nextTick()
      this.hotspotRunCheckInMain(hotspot)
    },

    // ① 打卡前置：不弹盖章层；continue=false 表示本次点击已收尾（收货/预览/Toast）
    async hotspotPreActions(hotspot, index, pageOriginalIndex) {
      // 调试：活动级配置（如是否要求手机号）
      console.log('[activity3-1] 热点点击 · 手机号/昵称等活动配置', {
        activityId: this.activityId,
        activityCode: this.activityCode,
        phoneAuthRaw: this.phoneAuthRaw,
        phoneAuthRequired: this.phoneAuthRequired,
        nicknameAuthRaw: this.nicknameAuthRaw,
        nicknameAuthRequired: this.nicknameAuthRequired,
        hotspotIndex: hotspot && hotspot.index,
        checkType: hotspot && hotspot.checkType,
        hotspotType: hotspot && hotspot.type
      })

      this.currentHotspotIndex = hotspot.index || null
      this.lockCheckInSessionContext(hotspot, pageOriginalIndex)
      const shippingRequired = !!(hotspot && hotspot.needShippingInfo)
      const isChecked = !!(hotspot.index && this.isPointIndexChecked(hotspot.index))

      // 打卡热点「可被点击打卡」= 否：不弹盖章层、不 Toast（已打卡仅保留收货/成就图预览）
      if (String(hotspot.type) === '1' && !this.isAct004HotspotClickCheckinEnabled(hotspot)) {
        if (!isChecked) {
          return { continue: false }
        }
        if (shippingRequired) {
          this.openShippingDialog(hotspot.index)
          return { continue: false }
        }
        if (hotspot.doSignImg) {
          const imageUrl = this.getImageUrl(hotspot.doSignImg)
          this.previewHotspotImage = imageUrl
          this.showHotspotImagePreview = true
          return { continue: false }
        }
        return { continue: false }
      }

      if (!isChecked && this.shouldShowHotspotNotUnlockedVisual(hotspot)) {
        this.toastHotspotVerifyConditionLocked()
        return { continue: false }
      }
      
      // ---------- 特殊类型：跳转热点 ----------
      if (hotspot && hotspot.type === '4') {
        console.log('[activity-multi] 跳转热点点击:', hotspot)
        // 执行跳转逻辑（完善字段条件会先查库再决定目标页）
        await this.handleHotspotJump(hotspot)
        return { continue: false }
      }
      // ---------- 特殊类型：富文本热点 ----------
      if (hotspot && hotspot.type === '2') {
        this.openHotspotRichTextDialog(hotspot)
        return { continue: false }
      }
      // ---------- 特殊类型：图片热点 ----------
      if (hotspot && (String(hotspot.type) === '3' || String(hotspot.type) === '15')) {
        this.handleHotspotImageClick(hotspot)
        return { continue: false }
      }
      // ---------- 特殊类型：显示地点 ----------
      if (hotspot && hotspot.type === '5') {
        this.openHotspotMapLocation(hotspot)
        return { continue: false }
      }
      // ---------- 表单输入热点：点击聚焦输入框 ----------
      if (hotspot && String(hotspot.type) === '16') {
        return { continue: false }
      }
      // ---------- 表单提交热点 ----------
      if (hotspot && String(hotspot.type) === '17') {
        await this.handleUserFormSubmitHotspot(hotspot, pageOriginalIndex)
        return { continue: false }
      }

      // ---------- 问答条件（优先于已打卡分支）----------
      if (isQuizHotspot(hotspot)) {
        const quizCheck = await this.checkVerifyQuiz(hotspot)
        if (!quizCheck.ok) {
          if (quizCheck.message) {
            uni.showToast({
              title: quizCheck.message,
              icon: 'none',
              duration: 2200
            })
          }
          return { continue: false }
        }
      }

      // ---------- 大类一：当前点位已打卡（本点已在 checkedPointIndexes）----------
      if (isChecked) {
        // 刚打卡成功后的首次交互：解除「完成文案 / 角标已打卡图 / 大图预览」的延迟，仅在本次点击中展示
        this.clearDeferPostCheckInVisual(hotspot.index)
        // 配置了需收货：只打开收货弹层（补填/查看），不再进盖章
        if (shippingRequired) {
          this.openShippingDialog(hotspot.index)
          return { continue: false }
        }
        const doneTip = hotspot && String(hotspot.checkInCompleteText || '').trim()
        if (doneTip) {
          uni.showToast({
            title: doneTip,
            icon: 'none',
            duration: 2200
          })
          return { continue: false }
        }
        const checkInBgMode = String(hotspot.checkInBackgroundMode || 'popup').trim().toLowerCase()
        if (checkInBgMode === 'internal_page' && this.isAct004HotspotClickCheckinEnabled(hotspot)) {
          return { continue: true }
        }
        // 配置了已打卡展示图：大图预览，不重复打卡
        if (hotspot.doSignImg) {
          console.log('显示图片预览，原始图片地址:', hotspot.doSignImg)
          console.log('热点信息:', hotspot)
          console.log('已打卡列表:', this.checkedPointIndexes)
          const imageUrl = this.getImageUrl(hotspot.doSignImg)
          console.log('处理后的图片地址:', imageUrl)
          this.previewHotspotImage = imageUrl
          this.showHotspotImagePreview = true
          return { continue: false }
        }
        // 兑换点位：已完成后禁止再次兑换（一个点位只能成功一次）
        if (hotspot && hotspot.type === '6') {
          uni.showToast({
            title: '该兑换点位已完成',
            icon: 'none',
            duration: 1800
          })
          return { continue: false }
        }
        // 以图片为热区的打卡点：已完成后严格禁止再次进入盖章流程
        if (hotspot && (hotspot.imageFullHotZone === 1 || hotspot.imageFullHotZone === '1')) {
          uni.showToast({
            title: '该打卡点位已完成',
            icon: 'none',
            duration: 1800
          })
          return { continue: false }
        }
        // 已打卡、但不要收货、也无成就图：无单独交互，与「未打卡」共用下方前置点位校验（与历史行为一致）
      }

      // ---------- 兑换点位未打卡时：优先校验当前页面核销数量上限 ----------
      if (hotspot && hotspot.type === '6' && !isChecked) {
        const exchangeState = this.getExchangeHotspotState(hotspot)
        if (exchangeState === 'LIMIT_REACHED') {
          uni.showToast({
            title: '当前页面兑换数量已达上限',
            icon: 'none',
            duration: 2000
          })
          return { continue: false }
        }
      }

      // ---------- 大类二：当前点位未打卡（或已打卡但走上一段兜底，需同样校验「前置点」）----------
      // 典型未打卡：首次点该热点；兜底：已打卡无收货/无图时也会走到这里
      const prerequisiteCheck = this.checkVerifyPrerequisite(hotspot)
      if (!prerequisiteCheck.ok) {
        uni.showToast({
          title: prerequisiteCheck.message,
          icon: 'none',
          duration: 2200
        })
        return { continue: false }
      }
      const checkinCountCheck = this.checkVerifyCheckinCount(hotspot)
      if (!checkinCountCheck.ok) {
        uni.showToast({
          title: checkinCountCheck.message,
          icon: 'none',
          duration: 2200
        })
        return { continue: false }
      }

      if (this.needsProfileAuth()) {
        this.openAuthPanel()
        return { continue: false }
      }

      // ---------- 特殊类型：兑换（先走与打卡一致的前置校验，再自动打卡，不弹盖章层） ----------
      if (hotspot && hotspot.type === '6') {
        const confirmed = await this.confirmExchangeHotspot(hotspot)
        if (!confirmed) {
          return { continue: false }
        }
        await this.handleExchangeHotspotAutoCheckin(hotspot)
        return { continue: false }
      }

      if (String(hotspot.type) === '1' && !this.isAct004HotspotClickCheckinEnabled(hotspot)) {
        return { continue: false }
      }

      return { continue: true }
    },
    confirmExchangeHotspot(hotspot) {
      const hotspotName = String((hotspot && hotspot.name) || '').trim() || '当前奖励'
      return new Promise((resolve) => {
        uni.showModal({
          title: '兑换确认',
          content: `您是否确认兑换${hotspotName}？`,
          confirmText: '确认',
          cancelText: '取消',
          success: (res) => {
            resolve(!!(res && res.confirm))
          },
          fail: () => {
            resolve(false)
          }
        })
      })
    },
    
    /** 表单展示：草稿优先，其次服务端资料 */
    getAct004ProfileFieldValue(field) {
      const bind = String(field || '').trim().toLowerCase()
      if (!bind) return ''
      const profile = this.activityUserFormProfile || {}
      const draft = this.activityUserFormDraft || {}
      let fromDraft = ''
      Object.keys(draft).forEach((key) => {
        if (key === bind || key.endsWith(':' + bind)) {
          const v = String(draft[key] || '').trim()
          if (v) fromDraft = v
        }
      })
      if (fromDraft) return fromDraft
      return profile[bind] != null ? String(profile[bind]).trim() : ''
    },
    /** 条件跳转：仅以 yx_user 已保存资料判断（不含未提交草稿） */
    getAct004PersistedProfileFieldValue(field) {
      const bind = String(field || '').trim().toLowerCase()
      if (!bind) return ''
      const profile = this.activityUserFormProfile || {}
      return profile[bind] != null ? String(profile[bind]).trim() : ''
    },
    isCheckinPointIndexDone(pointIndex) {
      const idx = parseInt(pointIndex, 10)
      if (!Number.isFinite(idx) || idx <= 0) return false
      return !!(this.checkedPointIndexes && this.checkedPointIndexes.includes(idx))
    },
    async resolveInternalJumpTargetPageIndex(hotspot) {
      const defaultIdx = Math.max(0, parseInt(hotspot && hotspot.targetPageIndex, 10) || 0)
      if (hotspotNeedsProfileFieldsConditionalVerify(hotspot)) {
        await this.refreshAct004ProfileForConditionalJump(true)
      }
      const targetPageIndex = resolveConditionalTargetPageIndex(
        hotspot,
        {
          getProfileFieldValue: (f) => this.getAct004PersistedProfileFieldValue(f),
          isCheckinPointDone: (idx) => this.isCheckinPointIndexDone(idx)
        },
        defaultIdx
      )
      console.log('[activity-multi] 条件跳转解析', {
        defaultPage: defaultIdx,
        targetPageIndex,
        profileFromApi: { ...(this.activityUserFormProfile || {}) },
        rules: normalizeConditionalTargetRules(hotspot)
      })
      return targetPageIndex
    },
    resolveCheckInSuccessTargetPageIndex(hotspot) {
      const rawDefault = hotspot && hotspot.checkInSuccessTargetPageIndex
      const parsedDefault = parseInt(rawDefault, 10)
      const defaultIdx =
        Number.isFinite(parsedDefault) && parsedDefault >= 0 ? parsedDefault : null
      const resolved = resolveCheckInSuccessTargetPageIndex(
        hotspot,
        {
          getCheckinCount: () => {
            const arr = this.checkedPointIndexes || []
            return Array.isArray(arr) ? arr.length : 0
          },
          isCheckinPointDone: (idx) => this.isCheckinPointIndexDone(idx)
        },
        defaultIdx
      )
      console.log('[activity-multi] 打卡成功条件跳转解析', {
        defaultPage: defaultIdx,
        targetPageIndex: resolved,
        checkedCount: (this.checkedPointIndexes || []).length,
        rules: hotspot && hotspot.checkInSuccessConditionalRules
      })
      return resolved
    },
    /** 打卡成功转圈结束后跳转（配置在热点「打卡后配置 → 成功后跳转」） */
    async handleCheckInSuccessJump(hotspot, fromPageIndex, pointIndexForLookup) {
      let hs = hotspot
      if (pointIndexForLookup != null && pointIndexForLookup !== '') {
        const fresh = this.findHotspotByPointIndex(pointIndexForLookup)
        if (fresh) hs = fresh
      }
      if (!hs) return
      const jumpType = String(hs.checkInSuccessJumpType || 'none').trim().toLowerCase()
      if (!jumpType || jumpType === 'none') return
      const src = parseInt(fromPageIndex, 10)
      const fromPage =
        Number.isFinite(src) && src >= 0 ? src : this.currentPageIndex

      if (jumpType === 'internal') {
        const targetPageIndex = this.resolveCheckInSuccessTargetPageIndex(hs)
        const idx = parseInt(targetPageIndex, 10)
        if (!Number.isFinite(idx) || idx < 0) {
          console.log('[activity-multi] 打卡成功内部跳转：无有效目标页', {
            checkInSuccessTargetPageIndex: hs.checkInSuccessTargetPageIndex,
            rules: hs.checkInSuccessConditionalRules
          })
          return
        }
        this._mpSnapLockUntil = Date.now() + 5000
        await this.applyActivityPageJumpTarget(idx, {
          fromPageIndex: fromPage,
          direct: true
        })
        return
      }
      if (jumpType === 'external') {
        const url = String(hs.checkInSuccessJumpUrl || '').trim()
        if (!url) return
        await this.handleHotspotJump({ jumpType: 'external', url }, { fromPageIndex: fromPage })
        return
      }
      if (jumpType === 'back') {
        await this.handleHotspotJump({ jumpType: 'back' }, { fromPageIndex: fromPage })
      }
    },
    /** 等待成功转圈 DOM 完成绘制后再计时（避免 300ms 内未渲染就关闭） */
    waitSealLoadingPaintReady() {
      return this.$nextTick().then(
        () =>
          new Promise((resolve) => {
            if (typeof requestAnimationFrame === 'function') {
              requestAnimationFrame(() => requestAnimationFrame(resolve))
            } else {
              setTimeout(resolve, 32)
            }
          })
      )
    },
    /** 表单提交热点保存成功后跳转 */
    handleFormSubmitSuccessJump(hotspot) {
      if (!hotspot) return
      const jumpType = String(hotspot.formSubmitSuccessJumpType || 'none').trim().toLowerCase()
      if (!jumpType || jumpType === 'none') return
      this.handleHotspotJump({
        jumpType,
        url: hotspot.formSubmitSuccessJumpUrl || '',
        targetPageIndex: hotspot.formSubmitSuccessTargetPageIndex,
        conditionalTargetRules: []
      })
    },
    isCheckInInternalPageInteractiveHotspot(hotspot) {
      if (!hotspot) return false
      const t = String(hotspot.type)
      return t === '4' || t === '2' || t === '3' || t === '15' || t === '5'
    },
    async onCheckInInternalPageHotspotClick(hotspot) {
      if (!hotspot || this.checkInInternalSealLoading) return
      const pageIdx = this.checkInInternalPageIndexLocked
      const t = String(hotspot.type)
      if (t === '4') {
        const internalIdx = parseInt(this.checkInInternalPageIndexLocked, 10)
        const fromPage =
          Number.isFinite(internalIdx) && internalIdx >= 0 ? internalIdx : this.currentPageIndex
        const jumpType = String(hotspot.jumpType || '').trim().toLowerCase()
        if (jumpType === 'internal') {
          const targetPageIndex = await this.resolveInternalJumpTargetPageIndex(hotspot)
          await this.applyActivityPageJumpTarget(targetPageIndex, {
            fromPageIndex: fromPage,
            direct: true
          })
          await this.finalizeCheckInInternalPageJump(targetPageIndex)
          this.showcheckInBox = false
          this.clearCheckInSessionContext()
        } else {
          await this.handleHotspotJump(hotspot, { fromPageIndex: fromPage })
          await this.$nextTick()
          this.showcheckInBox = false
          this.clearCheckInSessionContext()
        }
        return
      }
      this.showcheckInBox = false
      this.clearCheckInSessionContext()
      const pre = await this.hotspotPreActions(hotspot, null, pageIdx)
      if (pre && pre.continue) {
        await this.$nextTick()
        this.hotspotRunCheckInMain(hotspot)
      }
    },
    // 处理热点跳转；opts.fromPageIndex 用于内部活动页背景上的跳转（非当前 currentPageIndex）
    async handleHotspotJump(hotspot, opts) {
      if (!hotspot) return
      const options = opts && typeof opts === 'object' ? opts : {}
      const fromPageIndex =
        options.fromPageIndex != null && Number.isFinite(parseInt(options.fromPageIndex, 10))
          ? parseInt(options.fromPageIndex, 10)
          : this.currentPageIndex

      console.log('[activity-multi] 处理热点跳转:', hotspot)
      const jumpType = String(hotspot.jumpType || '').trim().toLowerCase()
      const externalUrlRaw = String(hotspot.url || '').trim()
      const externalUrl = /^www\./i.test(externalUrlRaw) ? ('https://' + externalUrlRaw) : externalUrlRaw
      console.log('[activity-multi] 跳转字段诊断:', {
        jumpTypeRaw: hotspot.jumpType,
        jumpTypeNormalized: jumpType,
        externalUrlRaw: hotspot.url,
        externalUrlTrimmed: externalUrl
      })

      if (jumpType === 'internal') {
        // 内部页面跳转：完善字段类条件会先请求 act004UserFormProfile 以 yx_user 为准
        const targetPageIndex = await this.resolveInternalJumpTargetPageIndex(hotspot)
        console.log('[activity-multi] 内部页面跳转，目标页面索引:', targetPageIndex, {
          defaultPage: hotspot.targetPageIndex,
          conditionalTargetRules: hotspot.conditionalTargetRules
        })

        await this.applyActivityPageJumpTarget(targetPageIndex, { fromPageIndex })
      } else if (jumpType === 'back') {
        const prevPageIndex = this.pageJumpHistory.length ? this.pageJumpHistory.pop() : null
        if (Number.isInteger(prevPageIndex) && prevPageIndex >= 0 && prevPageIndex < (this.pages || []).length) {
          const blocks = this.navPageBlocks
          const strict = this.hasStrictDisplayOrder
          const inNav = blocks.some((b) => b.originalIndex === prevPageIndex)
          this.jumpDetachedMode = strict && !inNav
          this.applyPageAtIndex(prevPageIndex)
          this.$nextTick(() => {
            if (!this.jumpDetachedMode && this.navPageBlocks.length > 1) {
              const b = this.navPageBlocks
              const si = b.findIndex((x) => x.originalIndex === prevPageIndex)
              if (si >= 0) {
                this.scrollMpSectionIntoView('mp-' + b[si].originalIndex)
              }
            }
          })
          this.preloadSignPageImages()
        } else if (typeof window !== 'undefined' && window.history && window.history.length > 1) {
          window.history.back()
        } else {
          uni.showToast({
            title: '没有可返回的上一页',
            icon: 'none'
          })
        }
      } else if (jumpType === 'external' && externalUrl) {
        // 外部页面当前页跳转（不新增页面栈）
        console.log('[activity-multi] 外部页面跳转，URL:', externalUrl)
        if (typeof window !== 'undefined') {
          window.location.href = externalUrl
        } else {
          uni.redirectTo({
            url: `/pages/webview/webview?url=${encodeURIComponent(externalUrl)}`
          })
        }
      } else {
        uni.showToast({
          title: '跳转配置不完整',
          icon: 'none'
        })
      }
    },
    handleHotspotImageClick(hotspot) {
      const imageConfig = this.resolveHotspotImageConfig(hotspot)
      const imageUrl = this.getImageUrl((imageConfig && imageConfig.imageUrl) || '')
      if (!imageUrl) {
        return
      }
      this.previewHotspotImage = imageUrl
      const baseNested = this.normalizeNestedHotspots(imageConfig && imageConfig.hotspots)
      let schedIdx = imageConfig && imageConfig.scheduleIndex
      let imageHotzoneNested = this.buildImageFullHotzoneNestedHotspots(hotspot, schedIdx)
      if (!imageHotzoneNested.length && imageUrl) {
        const inferred = this.inferImageScheduleIndexByPreviewUrl(hotspot, imageUrl)
        if (inferred >= 0) {
          imageHotzoneNested = this.buildImageFullHotzoneNestedHotspots(hotspot, inferred)
        }
      }
      this.previewHotspotNestedHotspots = [...baseNested, ...imageHotzoneNested]
      this.showHotspotImagePreview = true
    },
    parseDateTimeSafe(raw) {
      const text = String(raw == null ? '' : raw).trim()
      if (!text) return null
      const normalized = text.replace(/-/g, '/')
      const ts = Date.parse(normalized)
      return Number.isFinite(ts) ? ts : null
    },
    clearImageScheduleAlarmTimers() {
      const list = Array.isArray(this.imageScheduleAlarmTimers) ? this.imageScheduleAlarmTimers : []
      list.forEach((id) => {
        try { clearTimeout(id) } catch (e) {}
      })
      this.imageScheduleAlarmTimers = []
    },
    scheduleImageStartAlarmsForCurrentPage() {
      this.clearImageScheduleAlarmTimers()
      const now = Date.now()
      const hotspots = Array.isArray(this.hotspots) ? this.hotspots : []
      hotspots.forEach((hotspot, hotspotIdx) => {
        if (!hotspot || String(hotspot.type) !== '3') return
        const parentPointIndex = parseInt(hotspot.index, 10)
        if (!Number.isFinite(parentPointIndex)) return
        const schedules = Array.isArray(hotspot.imageScheduleList) ? hotspot.imageScheduleList : []
        schedules.forEach((item, scheduleIdx) => {
          if (!item || typeof item !== 'object') return
          const enabled = item.alarmEnabled === 1 || item.alarmEnabled === true || item.alarmEnabled === '1'
          if (!enabled) return
          const startTs = this.parseDateTimeSafe(item.startTime)
          if (!Number.isFinite(startTs) || startTs <= now) return
          const key = `${this.currentPageIndex}-${hotspot.index || hotspotIdx}-${scheduleIdx}-${item.startTime}`
          const delay = Math.max(0, startTs - now)
          const timerId = setTimeout(() => {
            if (this.imageScheduleAlarmTriggeredKeys.includes(key)) return
            this.imageScheduleAlarmTriggeredKeys.push(key)
            // 勿依赖闭包里的 hotspot：切换页/重算列表后引用可能过时，父级 index 丢失会导致以图热区注入失败
            const freshParent = this.findHotspotByPointIndex(parentPointIndex)
            const parent =
              freshParent && String(freshParent.type) === '3' ? freshParent : hotspot
            const list = Array.isArray(parent.imageScheduleList) ? parent.imageScheduleList : []
            const schedItem = list[scheduleIdx] || item
            this.triggerHotspotScheduleAlarm(parent, schedItem, scheduleIdx)
          }, delay)
          this.imageScheduleAlarmTimers.push(timerId)
        })
      })
    },
    triggerHotspotScheduleAlarm(hotspot, scheduleItem, scheduleIdx) {
      const imageUrl = this.getImageUrl((scheduleItem && scheduleItem.imageUrl) || '')
      if (!imageUrl) return
      this.previewHotspotImage = imageUrl
      const baseNested = this.normalizeNestedHotspots(scheduleItem && scheduleItem.hotspots)
      let imageHotzoneNested = this.buildImageFullHotzoneNestedHotspots(hotspot, scheduleIdx)
      if (!imageHotzoneNested.length && imageUrl) {
        const inferred = this.inferImageScheduleIndexByPreviewUrl(hotspot, imageUrl)
        if (inferred >= 0) {
          imageHotzoneNested = this.buildImageFullHotzoneNestedHotspots(hotspot, inferred)
        }
      }
      this.previewHotspotNestedHotspots = [...baseNested, ...imageHotzoneNested]
      this.showHotspotImagePreview = true
    },
    resolveHotspotImageConfig(hotspot) {
      const fallback = {
        imageUrl: hotspot && hotspot.imageUrl ? hotspot.imageUrl : '',
        hotspots: hotspot && Array.isArray(hotspot.imageNestedHotspots) ? hotspot.imageNestedHotspots : [],
        scheduleIndex: -1
      }
      const schedules = hotspot && Array.isArray(hotspot.imageScheduleList) ? hotspot.imageScheduleList : []
      if (!schedules.length) return fallback
      const hitIndex = this.resolveImageScheduleHitIndexForNow(schedules)
      const hit = hitIndex >= 0 ? schedules[hitIndex] : null
      if (!hit) return fallback
      return {
        imageUrl: hit.imageUrl || fallback.imageUrl,
        hotspots: Array.isArray(hit.hotspots) ? hit.hotspots : fallback.hotspots,
        scheduleIndex: hitIndex
      }
    },
    buildImageFullHotzoneNestedHotspots(parentHotspot, scheduleIndex) {
      const parentIndex = parseInt(parentHotspot && parentHotspot.index, 10)
      if (!Number.isFinite(parentIndex)) return []
      const bindScheduleIndex = parseInt(scheduleIndex, 10)
      if (!Number.isFinite(bindScheduleIndex) || bindScheduleIndex < 0) return []
      const rawMerged = this.collectRawHotspotsForDisplay(
        this.pages &&
          this.pages[this.currentPageIndex] &&
          Array.isArray(this.pages[this.currentPageIndex].hotspots)
          ? this.pages[this.currentPageIndex].hotspots
          : [],
        this.currentPageIndex
      )
      const list = this.mapRawHotspotsToRuntime(rawMerged)
      return list
        .filter((h) => (
          h &&
          (h.imageFullHotZone === 1 || h.imageFullHotZone === '1') &&
          String(h.type || '1') === '1' &&
          parseInt(h.bindImageHotspotIndex, 10) === parentIndex &&
          parseInt(h.bindScheduleIndex, 10) === bindScheduleIndex
        ))
        .map((h) => ({
          left: 0,
          top: 0,
          width: 100,
          height: 100,
          type: 'checkinhotspot',
          targetHotspotIndex: parseInt(h.index, 10)
        }))
    },
    normalizeNestedHotspots(list) {
      const toPercent = (v, fallback) => {
        const n = parseFloat(String(v == null ? '' : v).replace(/%/g, '').trim())
        if (!Number.isFinite(n)) return fallback
        return Math.max(0, Math.min(100, n))
      }
      return (Array.isArray(list) ? list : []).map((item) => ({
        left: toPercent(item && item.left, 0),
        top: toPercent(item && item.top, 0),
        width: toPercent(item && item.width, 20),
        height: toPercent(item && item.height, 20),
        type: String(item && item.type ? item.type : 'url').toLowerCase(),
        url: item && item.url ? String(item.url).trim() : '',
        imageUrl: item && item.imageUrl ? String(item.imageUrl).trim() : '',
        content: item && item.content ? String(item.content).trim() : '',
        targetHotspotIndex: Number.isFinite(parseInt(item && item.targetHotspotIndex, 10))
          ? parseInt(item && item.targetHotspotIndex, 10)
          : null
      }))
    },
    async handlePreviewNestedHotspotClick(nested) {
      const t = String(nested && nested.type ? nested.type : 'url').toLowerCase()
      if (t === 'checkinhotspot') {
        const targetIndex = parseInt(nested && nested.targetHotspotIndex, 10)
        if (!Number.isFinite(targetIndex)) return
        const rawMerged = this.collectRawHotspotsForDisplay(
          this.pages &&
            this.pages[this.currentPageIndex] &&
            Array.isArray(this.pages[this.currentPageIndex].hotspots)
            ? this.pages[this.currentPageIndex].hotspots
            : [],
          this.currentPageIndex
        )
        const all = this.mapRawHotspotsToRuntime(rawMerged)
        const target = all.find((h) => parseInt(h && h.index, 10) === targetIndex)
        if (!target) return
        const normalizedTarget = {
          ...target,
          index: targetIndex
        }
        await this.handleHotspotClick(normalizedTarget, 0)
        return
      }
      if (t === 'image') {
        const u = this.getImageUrl((nested && nested.imageUrl) || '')
        if (!u) return
        this.previewHotspotImage = u
        this.previewHotspotNestedHotspots = []
        return
      }
      if (t === 'richtext') {
        this.hotspotRichTextTitle = '活动详情'
        this.hotspotRichTextContent = nested && nested.content ? String(nested.content) : ''
        this.showHotspotRichTextDialog = true
        return
      }
      const rawUrl = String(nested && nested.url ? nested.url : '').trim()
      if (!rawUrl) return
      const externalUrl = /^www\\./i.test(rawUrl) ? ('https://' + rawUrl) : rawUrl
      if (typeof window !== 'undefined') {
        window.location.href = externalUrl
      } else {
        uni.redirectTo({
          url: `/pages/webview/webview?url=${encodeURIComponent(externalUrl)}`
        })
      }
    },
    openHotspotMapLocation(hotspot) {
      const latitude = parseFloat(hotspot && hotspot.latitude)
      const longitude = parseFloat(hotspot && hotspot.longitude)
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        uni.showToast({ title: '该热点未配置经纬度', icon: 'none' })
        return
      }
      uni.openLocation({
        latitude,
        longitude,
        name: String((hotspot && hotspot.name) || '').trim() || '活动地点',
        address: (hotspot && hotspot.locationAddress) || '',
        scale: 18,
        fail: () => uni.showToast({ title: '拉起导航失败', icon: 'none' })
      })
    },
    async handleExchangeHotspotAutoCheckin(hotspot) {
      if (this.checking) return
      const exchangeState = this.getExchangeHotspotState(hotspot)
      if (exchangeState === 'ALREADY_CHECKED') {
        if (hotspot.doSignImg) {
          this.previewHotspotImage = this.getImageUrl(hotspot.doSignImg)
          this.showHotspotImagePreview = true
        } else {
          uni.showToast({ title: '该兑换点位已完成', icon: 'none', duration: 1800 })
        }
        return
      }
      if (exchangeState === 'LIMIT_REACHED') {
        uni.showToast({ title: '当前页面兑换数量已达上限', icon: 'none', duration: 2000 })
        return
      }
      if (exchangeState === 'OUT_OF_STOCK') {
        uni.showToast({ title: '库存不足，暂不可兑换', icon: 'none', duration: 2000 })
        return
      }
      if (!this.ruleCode) {
        uni.showToast({ title: '活动规则编码不存在，无法兑换', icon: 'none' })
        return
      }
      if (!this.requireAuthStoreCodeForSeal()) {
        return
      }
      if (!this.activityUserId) {
        await this.loadUserRecord()
      }
      if (!this.activityUserId) {
        uni.showToast({ title: '用户活动记录不存在，无法兑换', icon: 'none' })
        return
      }
      this.currentHotspotIndex = hotspot.index || null
      this.lockSealLoadingVisualFromHotspot(hotspot)
      this.checking = true
      if (this.isAct004SealLoadingVisualEnabled(hotspot)) {
        this.load = true
      }
      try {
        const pointsRes = await getFirstTemplatePoints({ ruleCode: this.ruleCode })
        const firstPoints = this.extractFirstTemplatePoints(pointsRes)
        if (!firstPoints) {
          uni.showToast({ title: '未查询到标准点位，无法兑换', icon: 'none' })
          return
        }
        const validateData = {
          ruleCode: this.ruleCode,
          touchPoints: firstPoints,
          activityUserId: this.activityUserId,
          operationType: 'CHECKIN',
          pointIndex: this.currentHotspotIndex || null,
          authStoreCode: String(this.authStoreCode).trim()
        }
        const result = await validateSealForAct004(validateData)
        if (!result || !result.matched) {
          uni.showToast({
            title: (result && (result.errorMessage || result.message)) || '兑换失败，请重试',
            icon: 'none',
            duration: 2000
          })
          return
        }
        await this.hotspotPostCheckInSuccess(result)
      } catch (e) {
        uni.showToast({ title: '兑换失败，请重试', icon: 'none', duration: 2000 })
      } finally {
        this.load = false
        this.checking = false
      }
    },
    extractFirstTemplatePoints(raw) {
      const points = Array.isArray(raw)
        ? raw
        : (raw && Array.isArray(raw.data) ? raw.data : (raw && Array.isArray(raw.templatePoints) ? raw.templatePoints : null))
      if (!Array.isArray(points) || points.length === 0) return null
      const normalized = points.map((p) => {
        if (!Array.isArray(p) || p.length < 2) return null
        const x = Number(p[0])
        const y = Number(p[1])
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return [Math.round(x), Math.round(y)]
      }).filter(Boolean)
      return normalized.length >= 4 ? normalized.slice(0, 4) : null
    },

    /** 与授权组件一致：已绑定手机号（含脱敏展示） */
    hasPhoneValue(userInfo) {
      const u = userInfo || {}
      const p = u.phone || u.mobile || u.tel || ''
      const s = String(p).trim()
      if (!s) return false
      if (/^\d{11}$/.test(s)) return true
      if (/^\d{3}\*{4}\d{4}$/.test(s)) return true
      return false
    },

    /** 有效昵称：非空且非微信占位 */
    hasValidNickname(userInfo) {
      const n = String((userInfo && (userInfo.nickname || userInfo.nickName)) || '').trim()
      if (!n) return false
      if (n === '微信用户') return false
      return true
    },

    /** 活动要求手机号/昵称且当前用户信息不满足时，需先走授权弹窗 */
    needsProfileAuth() {
      const u = (this.$store && this.$store.getters && this.$store.getters.userInfo) || {}
      if (this.phoneAuthRequired && !this.hasPhoneValue(u)) return true
      if (this.nicknameAuthRequired && !this.hasValidNickname(u)) return true
      return false
    },

    openAuthPanel() {
      this.$nextTick(() => {
        const ref = this.$refs.authConfirm
        if (ref && typeof ref.open === 'function') ref.open()
      })
    },

    onAuthConfirmSuccess() {
      if (this.$store && typeof this.$store.dispatch === 'function') {
        this.$store.dispatch('userInfo', true)
      }
    },

    // ② 打卡主流程：只负责打开盖章 overlay（核销 / 普通打卡）
    hotspotRunCheckInMain(hotspot) {
      if (!this.isAct004HotspotClickCheckinEnabled(hotspot)) {
        return
      }
      // 核销：先校验 activityUserId，再进盖章层并置 VERIFY
      if (hotspot.checkType === 'VERIFY') {
        this.handleVerify()
      } else if (hotspot.checkType === 'CHECKIN') {
        // 普通打卡
        this.showcheckInBoxFun()
      } else {
        // 未配置类型时与打卡一致
        this.showcheckInBoxFun()
      }
    },

    // ③ 打卡后置：validateSeal 已通过（matched）；音效、关层、刷新数据、外部回调、核销提示、收货
    // result：接口返回，供 successCallback 组参
    async hotspotPostCheckInSuccess(result, options) {
      const silent = !!(options && options.silent)
      const isInternalPageCheckIn = !!this.checkInInternalPageModeLocked
      const sourcePageIndex = this.checkInSourcePageIndexLocked

      const currentHotspot = this.findHotspotByPointIndex(this.currentHotspotIndex)
      const hotspotForPostJump = currentHotspot ? { ...currentHotspot } : null
      const pointIndexForPost = this.currentHotspotIndex

      // 成功反馈音（联动热区静默打卡不播放）
      if (!silent && this.audioContext) {
        try {
          this.audioContext.play()
        } catch (e) {
          // 播放失败忽略
        }
      }

      if (!silent) {
        if (this.sealLoadingDurationMsLocked == null) {
          this.lockSealLoadingVisualFromHotspot(currentHotspot)
        }
        // 请求阶段结束：先关转圈；背景图模式保持 overlay，刷新完成后再按配置时长展示成功转圈
        this.load = false
        this.checkInInternalSealLoading = false
        if (!isInternalPageCheckIn) {
          this.showcheckInBox = false
          this.clearCheckInSessionContext()
        }
      }
      this.checking = false

      const refreshAfterCheckin = async () => {
        await this.loadUserRecord()
        await this.loadCheckedPoints()
        this._dataHotspotRefreshKey = ''
        await this.loadCheckinPointRanks(undefined, true)
        this._lastCheckedPointsRefreshPageIndex = null
        this._lastCheckedPointsRefreshAt = 0
      }

      // 联动热区：以接口 matched === true 为成功；无音效/转圈，刷新列表后切已打卡图
      if (silent) {
        if (!this.isAct004SealValidationMatched(result)) {
          this.currentHotspotIndex = null
          return false
        }
        await refreshAfterCheckin()
        this.triggerHotspotSuccessCallback(result)
        if (this.isVerifying) {
          this.isVerifying = false
        }
        const needShippingInfo = !!(currentHotspot && currentHotspot.needShippingInfo)
        const shippingIdx = pointIndexForPost
        this.currentHotspotIndex = null
        if (hotspotForPostJump) {
          await this.handleCheckInSuccessJump(hotspotForPostJump, sourcePageIndex, pointIndexForPost)
        }
        if (needShippingInfo && shippingIdx != null) {
          this.openShippingDialog(shippingIdx)
        }
        return true
      }

      // 刷新参与记录、已打卡列表，热点图标与次数与后台一致
      await refreshAfterCheckin()

      // 热点配置的第三方成功回调 URL
      this.triggerHotspotSuccessCallback(result)

      const needShippingInfo = !!(currentHotspot && currentHotspot.needShippingInfo)
      const idx = this.currentHotspotIndex
      if (idx != null && currentHotspot) {
        const hasCompleteTip = !!String(currentHotspot.checkInCompleteText || '').trim()
        const hasCornerBadge = !!String(currentHotspot.checkedBadgeImg || '').trim()
        const hasDonePreviewImg = !!String(currentHotspot.doSignImg || '').trim()
        if (hasCompleteTip || hasCornerBadge || hasDonePreviewImg) {
          this.deferPostCheckInVisual(idx)
        }
      }

      // 本次若是核销：仅立即提示核销成功（「打卡完成后显示文案 / 已打卡图」在用户再次点击该热点时展示）
      if (this.isVerifying) {
        uni.showToast({
          title: '核销成功',
          icon: 'success',
          duration: 2000
        })
        this.isVerifying = false
      }

      // 配置需收货：打卡/核销成功后弹收货（与 shippingRequired 分支不同，那是已打卡后补填）
      if (needShippingInfo) {
        this.openShippingDialog(this.currentHotspotIndex)
      }

      // 本轮热点结束，避免后续逻辑串点
      this.currentHotspotIndex = null

      // 以本次打卡热点配置为准（不用会话初锁定的 2000 兜底覆盖后台已改的 500）
      const sealLoadingDelayMs = this.resolveCheckInLoadingDurationMsForPoint(
        pointIndexForPost,
        hotspotForPostJump || currentHotspot
      )

      const finishSealLoadingAndJump = async () => {
        this.load = false
        this.checkInInternalSealLoading = false
        this.clearSealLoadingVisualLock()
        if (hotspotForPostJump) {
          await this.handleCheckInSuccessJump(hotspotForPostJump, sourcePageIndex, pointIndexForPost)
        }
        this.showcheckInBox = false
        this.clearCheckInSessionContext()
      }

      // 成功转圈：仅计时配置的 ms（刷新已在上方完成，不再叠加进转圈时长）
      if (sealLoadingDelayMs > 0) {
        if (isInternalPageCheckIn) {
          this.checkInInternalSealLoading = true
        } else {
          this.load = true
        }
        await this.waitSealLoadingPaintReady()
        setTimeout(() => {
          void finishSealLoadingAndJump()
        }, sealLoadingDelayMs)
      } else {
        await finishSealLoadingAndJump()
      }
    },
    clearAct004LimitDetailCache() {
      this.act004LimitDetailByPoint = {}
    },
    getDefaultLimitMessageByPeriodType(periodType) {
      const t = String(periodType || '').trim().toUpperCase()
      if (t === 'ALL') return '当前活动打卡次数已达上限'
      if (t === 'MONTHLY') return '本月打卡次数已达上限'
      return '今日打卡次数已达上限'
    },
    /** 与单点限次接口 data 解析一致，供批量预取缓存与单点降级共用 */
    buildAct004DailyLimitCheckResult(raw) {
      if (!raw || typeof raw !== 'object') {
        return { blocked: true, reason: '打卡次数校验失败，请稍后重试', limitReachedHotspotIndex: null }
      }
      const allowed = !(
        raw.allowed === false ||
        String(raw.allowed).toLowerCase() === 'false' ||
        raw.allowed === 0 ||
        String(raw.allowed) === '0'
      )
      const hotspotIndex = raw.limitReachedHotspotIndex != null ? parseInt(raw.limitReachedHotspotIndex, 10) : NaN
      return {
        blocked: !allowed,
        reason: raw.blockedReason ? String(raw.blockedReason) : '',
        limitReachedHotspotIndex: Number.isFinite(hotspotIndex) && hotspotIndex > 0 ? hotspotIndex : null
      }
    },
    async checkAct004DailyLimitBeforeCheckin(hotspot, isChecked) {
      const t = this.normalizeHotspotTypeForLimit(hotspot && hotspot.type)
      const checkType = String(hotspot && hotspot.checkType || 'CHECKIN').toUpperCase()
      if (t !== '1' || checkType !== 'CHECKIN') return { blocked: false }
      if (!this.activityUserId) {
        return { blocked: true, reason: '' }
      }
      const pointIndex = parseInt(hotspot && hotspot.index, 10)
      if (!Number.isFinite(pointIndex) || pointIndex <= 0) {
        return { blocked: false }
      }
      const cachedRaw = this.act004LimitDetailByPoint && this.act004LimitDetailByPoint[pointIndex]
      if (cachedRaw != null && typeof cachedRaw === 'object') {
        return this.buildAct004DailyLimitCheckResult(cachedRaw)
      }
      try {
        const res = await getAct004CheckinLimitStatusDetail({
          activityUserId: this.activityUserId,
          pointIndex
        })
        const raw = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
        if (raw && typeof raw === 'object') {
          this.$set(this.act004LimitDetailByPoint, pointIndex, raw)
        }
        return this.buildAct004DailyLimitCheckResult(raw)
      } catch (e) {
        return { blocked: true, reason: '打卡次数校验失败，请稍后重试' }
      }
    },
    async showDailyCheckinLimitReachedFeedback(sourceHotspot, preferredHotspotIndex) {
      const targetIndex = (() => {
        const preferred = parseInt(preferredHotspotIndex, 10)
        if (Number.isFinite(preferred) && preferred > 0) return preferred
        const n = parseInt(this.dailyCheckinLimitReachedHotspotIndex, 10)
        if (Number.isFinite(n) && n > 0) return n
        const dailyActivity = parseInt(this.dailyActivityTotalRoundsLimitReachedHotspotIndex, 10)
        return Number.isFinite(dailyActivity) && dailyActivity > 0 ? dailyActivity : null
      })()
      if (targetIndex != null) {
        const sourceIdx = parseInt(sourceHotspot && sourceHotspot.index, 10)
        if (!Number.isFinite(sourceIdx) || sourceIdx !== targetIndex) {
          const target = this.findHotspotWithPageByPointIndex(targetIndex)
          if (target && target.hotspot) {
            if (target.pageOriginalIndex != null && target.pageOriginalIndex !== this.currentPageIndex) {
              this.applyPageAtIndex(target.pageOriginalIndex)
              await this.$nextTick()
            }
            const latest = this.findHotspotWithPageByPointIndex(targetIndex)
            const hs = latest && latest.hotspot ? latest.hotspot : target.hotspot
            const handled = await this.dispatchHotspotTypeAction(hs)
            if (handled) return
            await this.handleHotspotClick(
              hs,
              0,
              latest && latest.pageOriginalIndex != null ? latest.pageOriginalIndex : target.pageOriginalIndex
            )
            return
          }
        }
      }
      uni.showToast({
        title: this.getDefaultLimitMessageByPeriodType(this.checkinLimitPeriodType),
        icon: 'none',
        duration: 2200
      })
    },
    async dispatchHotspotTypeAction(hotspot) {
      if (!hotspot) return false
      const t = String(hotspot.type || '')
      if (t === '9') {
        return true
      }
      if (t === '4') {
        await this.handleHotspotJump(hotspot)
        return true
      }
      if (t === '2') {
        this.openHotspotRichTextDialog(hotspot)
        return true
      }
      if (t === '3' || t === '15') {
        this.handleHotspotImageClick(hotspot)
        return true
      }
      if (t === '5' || t === '8') {
        this.openHotspotMapLocation(hotspot)
        return true
      }
      if (t === '1' || t === '6') {
        return false
      }
      return false
    },
    findHotspotWithPageByPointIndex(pointIndex) {
      if (pointIndex == null || pointIndex === '') return null
      const pages = this.pages || []
      for (let pi = 0; pi < pages.length; pi++) {
        const rawList = pages[pi] && pages[pi].hotspots
        if (!Array.isArray(rawList)) continue
        const raw = rawList.find((h) => String(h && h.index) === String(pointIndex))
        if (raw) {
          const mapped = this.mapRawHotspotsToRuntime([raw])
          return { hotspot: mapped[0] || null, pageOriginalIndex: pi }
        }
      }
      if (Array.isArray(this.hotspots)) {
        const local = this.hotspots.find((item) => String(item && item.index) === String(pointIndex)) || null
        if (local) return { hotspot: local, pageOriginalIndex: this.currentPageIndex }
      }
      return null
    },
    normalizeHotspotTypeForLimit(raw) {
      const s = String(raw || '').toLowerCase()
      if (s === '1' || s === 'checkinhotspot') return '1'
      return s
    },
    collectAct004CheckinLimitPointIndexes() {
      const set = new Set()
      const parseMaybeJsonArray = (v) => {
        if (Array.isArray(v)) return v
        if (typeof v === 'string') {
          try {
            const parsed = JSON.parse(v)
            return Array.isArray(parsed) ? parsed : []
          } catch (e) {
            return []
          }
        }
        return []
      }
      const addFromHotspotRaw = (h) => {
        if (!h || typeof h !== 'object') return
        const t = this.normalizeHotspotTypeForLimit(h.type)
        const ct = String(h.checkType || 'CHECKIN').toUpperCase()
        if (t !== '1' || ct !== 'CHECKIN') return
        const pi = parseInt(h.index, 10)
        if (Number.isFinite(pi) && pi > 0) set.add(pi)
      }
      const walkNestedRows = (rows) => {
        parseMaybeJsonArray(rows).forEach((row) => {
          const ch = row && (row.checkinHotspot || row.checkInHotspot || row)
          if (ch && typeof ch === 'object') addFromHotspotRaw(ch)
        })
      }
      const pages = Array.isArray(this.pages) ? this.pages : []
      pages.forEach((page) => {
        const hs = Array.isArray(page && page.hotspots) ? page.hotspots : []
        hs.forEach((h) => {
          addFromHotspotRaw(h)
          if (String(h && h.type) !== '3') return
          walkNestedRows(h && h.imageNestedHotspotsForm)
          walkNestedRows(h && h.imageNestedHotspots)
          walkNestedRows(h && h.hotspotsForm)
          walkNestedRows(h && h.hotspots)
          const schedules = parseMaybeJsonArray(h && h.imageScheduleListForm).concat(
            parseMaybeJsonArray(h && h.imageScheduleList)
          )
          schedules.forEach((sch) => {
            if (!sch || typeof sch !== 'object') return
            walkNestedRows(sch.hotspotsForm)
            walkNestedRows(sch.imageNestedHotspotsForm)
            walkNestedRows(sch.hotspots)
            walkNestedRows(sch.imageNestedHotspots)
          })
        })
      })
      return Array.from(set).sort((a, b) => a - b)
    },
    async prefetchAct004CheckinLimitBatch() {
      if (!this.activityUserId) {
        this.clearAct004LimitDetailCache()
        return
      }
      if (!Array.isArray(this.pages) || !this.pages.length) {
        return
      }
      const all = this.collectAct004CheckinLimitPointIndexes()
      if (!all.length) {
        this.clearAct004LimitDetailCache()
        return
      }
      const MAX = 99
      try {
        const merged = {}
        for (let i = 0; i < all.length; i += MAX) {
          const chunk = all.slice(i, i + MAX)
          const res = await getAct004CheckinLimitStatusDetailBatch({
            activityUserId: this.activityUserId,
            pointIndexes: chunk
          })
          let rawBy = null
          if (res && res.byPoint && typeof res.byPoint === 'object') {
            rawBy = res.byPoint
          } else if (res && res.data && res.data.byPoint && typeof res.data.byPoint === 'object') {
            rawBy = res.data.byPoint
          } else if (res && res.status === 200 && res.data && res.data.byPoint) {
            rawBy = res.data.byPoint
          }
          if (rawBy && typeof rawBy === 'object') {
            Object.keys(rawBy).forEach((k) => {
              const n = parseInt(k, 10)
              if (Number.isFinite(n) && n > 0) merged[n] = rawBy[k]
            })
          }
        }
        this.act004LimitDetailByPoint = merged
      } catch (e) {
        console.warn('ACT004 checkin limit batch prefetch failed', e)
      }
    },
    normalizeAct004QuizSessionQuestions(raw) {
      if (!Array.isArray(raw)) return []
      return raw.map((row) => {
        if (!row || typeof row !== 'object') return null
        const opts = Array.isArray(row.options) ? row.options : []
        return {
          questionId: row.questionId,
          questionType: row.questionType,
          title: row.title,
          displaySort: row.displaySort,
          activityPageIndex:
            row.activityPageIndex != null && row.activityPageIndex !== ''
              ? parseInt(row.activityPageIndex, 10)
              : null,
          layoutConfigJson: row.layoutConfigJson != null ? row.layoutConfigJson : '',
          options: opts.map((o) => ({
            optionId: o && o.optionId != null ? o.optionId : null,
            label: o && o.label != null ? String(o.label) : ''
          }))
        }
      }).filter(Boolean)
    },
    async ensureAct004QuizDetailForPoint(pointIndex) {
      const pi = parseInt(pointIndex, 10)
      if (!Number.isFinite(pi) || pi <= 0 || !this.activityUserId) {
        return null
      }
      const cached = this.act004LimitDetailByPoint && this.act004LimitDetailByPoint[pi]
      const cachedQuiz = cached && cached.quiz
      if (cachedQuiz && isQuizPassedFromPrefetch(cachedQuiz)) {
        return cachedQuiz
      }
      if (
        cachedQuiz &&
        (cachedQuiz.needQuiz === true || cachedQuiz.needQuiz === 1 || String(cachedQuiz.needQuiz) === 'true') &&
        cachedQuiz.quizPaperId != null &&
        ((Array.isArray(cachedQuiz.questions) && cachedQuiz.questions.length) ||
          isRandomQuestionOrder(cachedQuiz) ||
          (cachedQuiz.paperRules && typeof cachedQuiz.paperRules === 'object'))
      ) {
        return cachedQuiz
      }
      try {
        const res = await getAct004CheckinLimitStatusDetail({
          activityUserId: this.activityUserId,
          pointIndex: pi
        })
        const raw = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res
        if (raw && typeof raw === 'object') {
          this.$set(this.act004LimitDetailByPoint, pi, raw)
          return raw.quiz && typeof raw.quiz === 'object' ? raw.quiz : null
        }
      } catch (e) {
        console.warn('ACT004 quiz detail fallback failed', pi, e)
      }
      return cachedQuiz || null
    },
    async checkVerifyQuiz(hotspot) {
      if (!isQuizHotspot(hotspot)) {
        return { ok: true }
      }
      const pointIndex = parseInt(hotspot && hotspot.index, 10)
      if (isQuizPassedForPoint(this.act004LimitDetailByPoint, pointIndex)) {
        return { ok: true }
      }
      await this.ensureAct004QuizDetailForPoint(pointIndex)
      if (isQuizPassedForPoint(this.act004LimitDetailByPoint, pointIndex)) {
        return { ok: true }
      }
      const gate = shouldOpenQuizDialog(hotspot, this.act004LimitDetailByPoint)
      if (!gate.open) {
        if (gate.passed) {
          return { ok: true }
        }
        if (gate.reason) {
          return { ok: false, message: gate.reason }
        }
        return { ok: true }
      }
      const displayMode = gate.displayMode || getQuizDisplayMode(
        this.getQuizFromLimitCache(pointIndex) || {}
      )
      const quizCached = this.getQuizFromLimitCache(pointIndex) || {}
      const studyGate = resolveQuizStudyGateFromSources(quizCached, gate.payload)
      if (studyGate) {
        return this.runAct004QuizStudyGate(gate, studyGate, displayMode)
      }
      return this.continueAct004QuizAfterStudy(gate, displayMode)
    },
    async runAct004QuizStudyGate(gate, studyGate, displayMode) {
      return new Promise((resolve) => {
        this.act004QuizPendingResolve = resolve
        this.act004QuizStudyGatePending = {
          gate,
          displayMode
        }
        this.act004QuizStudyPrevJumpDetached = this.jumpDetachedMode
        this.act004QuizStudyStartHotspot = studyGate.startHotspot
        this.act004QuizStudyPageIndex = studyGate.studyPageIndex
        if (this.act004QuizPageReturnPageIndex == null) {
          this.act004QuizPageReturnPageIndex = this.currentPageIndex
        }
        const pi = studyGate.studyPageIndex
        if (pi != null && Number.isFinite(pi) && pi >= 0 && pi < this.pages.length) {
          const blocks = this.navPageBlocks
          const strict = this.hasStrictDisplayOrder
          const inNav = blocks.some((b) => b.originalIndex === pi)
          this.jumpDetachedMode = strict && !inNav
          this.applyPageAtIndex(pi)
          this.act004QuizStudyOverlayVisible = true
          this.$nextTick(() => {
            if (!this.jumpDetachedMode && this.navPageBlocks.length > 1) {
              this.scrollMpSectionIntoView('mp-' + pi)
            }
          })
        } else {
          this.act004QuizStudyOverlayVisible = true
        }
      })
    },
    getQuizStudyHotspotStyle() {
      const h = this.act004QuizStudyStartHotspot
      if (!h) return { display: 'none' }
      let style = quizHotspotBoxStyle(h, (n) => this.rpxPx(n))
      style = {
        ...style,
        pointerEvents: 'auto',
        cursor: 'pointer',
        zIndex: 200,
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        color: 'transparent',
        fontSize: 0,
        lineHeight: 0,
        paddingLeft: 0,
        border: 'none'
      }
      return isHotspotDebugVisible() ? mergeHotspotDebugStyle(style) : style
    },
    shouldRenderQuizStudyHotspot(pageOriginalIndex) {
      if (!this.act004QuizStudyOverlayVisible || !this.act004QuizStudyStartHotspot) return false
      const expect = parseInt(this.act004QuizStudyPageIndex, 10)
      const cur = parseInt(pageOriginalIndex, 10)
      return Number.isFinite(expect) && expect === cur
    },
    shouldShowQuizPageOverlayOnPage(pageOriginalIndex) {
      if (!this.act004QuizPageOverlayVisible) return false
      if (this.act004QuizPageDisplayMode === 'IMAGE_OPTION_HOTSPOT') return false
      const cur = parseInt(this.currentPageIndex, 10)
      const pi = parseInt(pageOriginalIndex, 10)
      return Number.isFinite(cur) && cur === pi
    },
    shouldShowQuizImageOverlayOnPage(pageOriginalIndex) {
      if (!this.act004QuizPageOverlayVisible) return false
      if (this.act004QuizPageDisplayMode !== 'IMAGE_OPTION_HOTSPOT') return false
      const cur = parseInt(this.currentPageIndex, 10)
      const pi = parseInt(pageOriginalIndex, 10)
      return Number.isFinite(cur) && cur === pi
    },
    onAct004QuizStudyStart() {
      this.act004QuizStudyOverlayVisible = false
      this.act004QuizStudyPageIndex = null
      const pending = this.act004QuizStudyGatePending
      this.act004QuizStudyGatePending = null
      this.act004QuizStudyStartHotspot = null
      if (!pending) {
        this.finishAct004QuizStudyFlow(false, '参数异常')
        return
      }
      this.continueAct004QuizAfterStudy(pending.gate, pending.displayMode)
    },
    finishAct004QuizStudyFlow(success, message) {
      this.act004QuizStudyOverlayVisible = false
      this.act004QuizStudyPageIndex = null
      this.act004QuizStudyStartHotspot = null
      this.act004QuizStudyGatePending = null
      if (!success) {
        this.restoreAct004QuizReturnPageIfNeeded()
      }
      const fn = this.act004QuizPendingResolve
      this.act004QuizPendingResolve = null
      if (fn) fn({ ok: !!success, message: message || '' })
    },
    async continueAct004QuizAfterStudy(gate, displayMode) {
      if (displayMode === 'PAGE_HOTSPOT' || displayMode === 'IMAGE_OPTION_HOTSPOT') {
        return this.runAct004QuizPageFlow(gate, displayMode)
      }
      return new Promise((resolve) => {
        this.bindAct004QuizPendingResolve(resolve)
        this.act004QuizPointIndex = gate.pointIndex
        this.act004QuizPayload = gate.payload
        this.act004QuizDialogVisible = true
      })
    },
    bindAct004QuizPendingResolve(resolve) {
      if (typeof resolve === 'function' && !this.act004QuizPendingResolve) {
        this.act004QuizPendingResolve = resolve
      }
    },
    getQuizFromLimitCache(pointIndex) {
      const n = parseInt(pointIndex, 10)
      if (!Number.isFinite(n) || n <= 0) return null
      const raw = this.act004LimitDetailByPoint && this.act004LimitDetailByPoint[n]
      if (!raw || typeof raw !== 'object') return null
      return raw.quiz && typeof raw.quiz === 'object' ? raw.quiz : null
    },
    resetAct004QuizPageState() {
      this.act004QuizPageDisplayMode = 'PAGE_HOTSPOT'
      this.act004QuizPageCurrentQuestionCorrect = false
      this.act004QuizPageQuizPassedPending = false
      this.act004QuizPageOverlayVisible = false
      this.act004QuizPageSessionId = ''
      this.act004QuizPageQuestions = []
      this.act004QuizPageCurrentIndex = 0
      this.act004QuizPageSelectedIds = []
      this.act004QuizPageTextValue = ''
      this.act004QuizPageSubmitting = false
      this.act004QuizPageBootstrapping = false
      this._act004QuizBootstrapPromise = null
      this.act004QuizPageWrongRetryMode = 'RESTART_FROM_FIRST'
      this.restoreAct004QuizReturnPageIfNeeded()
    },
    restoreAct004QuizReturnPageIfNeeded() {
      if (this.act004QuizPageReturnPageIndex != null) {
        this.applyPageAtIndex(this.act004QuizPageReturnPageIndex)
        this.act004QuizPageReturnPageIndex = null
      }
      if (this.act004QuizStudyPrevJumpDetached != null) {
        this.jumpDetachedMode = this.act004QuizStudyPrevJumpDetached
        this.act004QuizStudyPrevJumpDetached = null
      }
    },
    applyAct004QuizPageForQuestionIndex(qIndex) {
      this.act004QuizPageCurrentQuestionCorrect = false
      this.act004QuizPageQuizPassedPending = false
      const q = this.act004QuizPageQuestions[qIndex]
      const pi = q && q.activityPageIndex
      if (pi != null && Number.isFinite(pi) && pi >= 0 && pi < this.pages.length) {
        this.applyPageAtIndex(pi)
        this.markAct004QuizPageBackgroundReady(pi)
      }
    },
    /** 进入问答页后不再等待背景 @load，避免 showBgHold 全屏转圈挡住答题层 */
    markAct004QuizPageBackgroundReady(pageIndex) {
      const idx = parseInt(pageIndex, 10)
      if (!Number.isFinite(idx) || idx < 0) {
        this.signSingleBgLoaded = true
        return
      }
      const pages = this.pages || []
      const page = pages[idx] || {}
      const signCtx = { originalIndex: idx, page }
      if (this.resolvePageBackgroundSignMode(signCtx) === 'composite') {
        const banner = this.resolvePageSignCompositeBanner(signCtx)
        this.signCompositeBannerLoaded = !banner
      } else {
        const bg = page.background != null ? String(page.background).trim() : ''
        this.signSingleBgLoaded = !bg
      }
      if (!this.signSingleBgLoaded || !this.signCompositeBannerLoaded) {
        this.preloadPageBackgroundAtIndex(idx).then(() => {
          this.signSingleBgLoaded = true
          this.signCompositeBannerLoaded = true
        })
      }
    },
    isAct004QuizInteractionActive() {
      return !!(
        this.act004QuizPageOverlayVisible ||
        this.act004QuizDialogVisible ||
        this.act004QuizStudyOverlayVisible
      )
    },
    async runAct004QuizPageFlow(gate, displayMode) {
      const payload = gate.payload
      if (!this.activityUserId || !payload || !payload.quizPaperId) {
        return { ok: false, message: '问答参数异常' }
      }
      const mode = displayMode || getQuizDisplayMode(this.getQuizFromLimitCache(gate.pointIndex) || {})
      const firstQ = (payload.questions && payload.questions[0]) || null
      if (mode !== 'IMAGE_OPTION_HOTSPOT' && firstQ && String(firstQ.questionType || '').toUpperCase() === 'TEXT') {
        return new Promise((resolve) => {
          this.bindAct004QuizPendingResolve(resolve)
          this.act004QuizPointIndex = gate.pointIndex
          this.act004QuizPayload = payload
          this.act004QuizDialogVisible = true
        })
      }
      return new Promise((resolve) => {
        this.bindAct004QuizPendingResolve(resolve)
        this.act004QuizPointIndex = gate.pointIndex
        this.act004QuizPayload = payload
        this.act004QuizPageDisplayMode = mode
        if (this.act004QuizPageReturnPageIndex == null) {
          this.act004QuizPageReturnPageIndex = this.currentPageIndex
        }
        this.bootstrapAct004QuizPageSession()
      })
    },
    bootstrapAct004QuizPageSession(options) {
      const forceReset = !!(options && options.forceReset)
      if (this._act004QuizBootstrapPromise) {
        return this._act004QuizBootstrapPromise
      }
      this._act004QuizBootstrapPromise = this._runBootstrapAct004QuizPageSession(forceReset).finally(() => {
        this._act004QuizBootstrapPromise = null
      })
      return this._act004QuizBootstrapPromise
    },
    async _runBootstrapAct004QuizPageSession(forceReset) {
      const payload = this.act004QuizPayload
      if (!payload || !payload.quizPaperId) {
        this.finishAct004QuizPageFlow(false, '问答参数异常')
        return
      }
      const pendingQuestions = this.normalizeAct004QuizSessionQuestions(payload.questions)
      if (pendingQuestions.length) {
        this.act004QuizPageQuestions = pendingQuestions
        if (forceReset || !this.act004QuizPageSessionId) {
          this.act004QuizPageCurrentIndex = 0
          this.act004QuizPageSelectedIds = []
          this.act004QuizPageTextValue = ''
          this.act004QuizPageCurrentQuestionCorrect = false
          this.act004QuizPageQuizPassedPending = false
        }
        this.applyAct004QuizPageForQuestionIndex(this.act004QuizPageCurrentIndex)
      }
      this.act004QuizPageOverlayVisible = true
      this.act004QuizPageBootstrapping = true
      await this.$nextTick()
      try {
        const res = await startAct004QuizSession({
          activityUserId: this.activityUserId,
          activityCode: this.activityCode,
          pageIndex: this.act004QuizSessionPageIndex,
          pointIndex: this.act004QuizPointIndex,
          quizPaperId: payload.quizPaperId
        })
        const data = res && res.data ? res.data : res
        this.act004QuizPageSessionId = data && data.sessionId ? data.sessionId : ''
        if (!this.act004QuizPageSessionId) {
          throw new Error('创建答题会话失败')
        }
        this.act004QuizPageWrongRetryMode = String((data && data.wrongRetryMode) || 'RESTART_FROM_FIRST').toUpperCase()
        const fromSession = this.normalizeAct004QuizSessionQuestions(data && data.questions)
        if (fromSession.length) {
          this.act004QuizPageQuestions = fromSession
        } else {
          this.act004QuizPageQuestions = this.normalizeAct004QuizSessionQuestions(payload.questions)
        }
        if (!this.act004QuizPageQuestions.length) {
          throw new Error('问答卷暂无题目')
        }
        const cur = this.act004QuizPageQuestions[0]
        const mode = this.act004QuizPageDisplayMode || 'PAGE_HOTSPOT'
        if (!cur || !cur.layoutConfigJson) {
          throw new Error('请配置题目热点布局')
        }
        if (mode === 'IMAGE_OPTION_HOTSPOT') {
          if (String(cur.questionType || '').toUpperCase() === 'TEXT') {
            throw new Error('背景图选项热点模式不支持问答题')
          }
          if (!parseImageOptionLayoutConfig(cur.layoutConfigJson)) {
            throw new Error('请配置选项热点布局')
          }
        }
        if (forceReset) {
          this.act004QuizPageCurrentIndex = 0
          this.act004QuizPageSelectedIds = []
          this.act004QuizPageTextValue = ''
          this.act004QuizPageCurrentQuestionCorrect = false
          this.act004QuizPageQuizPassedPending = false
        }
        this.applyAct004QuizPageForQuestionIndex(this.act004QuizPageCurrentIndex)
        await this.$nextTick()
      } catch (e) {
        this.finishAct004QuizPageFlow(false, (e && e.message) || '无法开始答题')
      } finally {
        this.act004QuizPageBootstrapping = false
      }
    },
    isAct004QuizImageOptionClickToSubmit() {
      if (this.act004QuizPageDisplayMode !== 'IMAGE_OPTION_HOTSPOT') return false
      const layout = parseImageOptionLayoutConfig(this.act004QuizPageLayoutConfig)
      return String((layout && layout.imageOptionSubmitMode) || 'SELECT_THEN_SUBMIT').toUpperCase() === 'CLICK_TO_SUBMIT'
    },
    onAct004QuizPageToggleOption(evt) {
      const optionId = evt && evt.optionId
      const type = String((evt && evt.questionType) || 'SINGLE').toUpperCase()
      if (optionId == null) return
      if (
        this.isAct004QuizImageOptionClickToSubmit() &&
        (this.act004QuizPageCurrentQuestionCorrect || this.act004QuizPageQuizPassedPending)
      ) {
        showAct004QuizAlreadyCorrectToast()
        return
      }
      if (type === 'MULTI') {
        const idx = this.act004QuizPageSelectedIds.findIndex((x) => String(x) === String(optionId))
        if (idx >= 0) {
          this.act004QuizPageSelectedIds.splice(idx, 1)
        } else {
          this.act004QuizPageSelectedIds.push(optionId)
        }
      } else {
        this.act004QuizPageSelectedIds = [optionId]
      }
      if (this.isAct004QuizImageOptionClickToSubmit() && type === 'SINGLE') {
        void this.onAct004QuizPageSubmit({ skipPageNavigation: true })
      }
    },
    act004QuizImageOptionHotspotCount() {
      if (this.act004QuizPageDisplayMode !== 'IMAGE_OPTION_HOTSPOT') return -1
      const layout = parseImageOptionLayoutConfig(this.act004QuizPageLayoutConfig)
      return layout && Array.isArray(layout.options) ? layout.options.length : 0
    },
    /** 背景图选项热点且布局中无选项热点：仅提交按钮，点提交即本题答对 */
    isAct004QuizImageOptionSubmitOnly() {
      return (
        this.act004QuizPageDisplayMode === 'IMAGE_OPTION_HOTSPOT' &&
        this.act004QuizImageOptionHotspotCount() === 0
      )
    },
    buildAct004QuizPageAnswerPayload() {
      const q = this.act004QuizPageCurrentQuestion
      if (!q) return null
      const type = String(q.questionType || 'SINGLE').toUpperCase()
      if (type === 'TEXT') {
        return { text: this.act004QuizPageTextValue }
      }
      if (
        this.act004QuizPageDisplayMode === 'IMAGE_OPTION_HOTSPOT' &&
        this.isAct004QuizImageOptionSubmitOnly()
      ) {
        if (type === 'MULTI') return { optionIds: [] }
        return { optionIds: [], submitOnly: true }
      }
      if (type === 'MULTI') {
        return { optionIds: this.act004QuizPageSelectedIds.slice() }
      }
      return { optionId: this.act004QuizPageSelectedIds[0] != null ? this.act004QuizPageSelectedIds[0] : null }
    },
    validateAct004QuizPageAnswer() {
      const q = this.act004QuizPageCurrentQuestion
      if (!q) return false
      if (
        this.act004QuizPageDisplayMode === 'IMAGE_OPTION_HOTSPOT' &&
        this.isAct004QuizImageOptionSubmitOnly()
      ) {
        return true
      }
      const payload = this.buildAct004QuizPageAnswerPayload()
      const type = String(q.questionType || 'SINGLE').toUpperCase()
      if (type === 'TEXT') {
        return !!(payload && String(payload.text || '').trim())
      }
      if (type === 'MULTI') {
        return !!(payload && Array.isArray(payload.optionIds) && payload.optionIds.length)
      }
      return !!(payload && payload.optionId != null)
    },
    onAct004QuizPageNavigateAfterAnswer() {
      if (this.act004QuizPageQuizPassedPending) {
        this.act004QuizPageQuizPassedPending = false
        this.act004QuizPageCurrentQuestionCorrect = false
        this.finishAct004QuizPageFlow(true)
        return
      }
      if (!this.act004QuizPageCurrentQuestionCorrect) {
        uni.showToast({ title: '请先答对本题', icon: 'none' })
        return
      }
      this.act004QuizPageCurrentQuestionCorrect = false
      this.act004QuizPageCurrentIndex += 1
      this.act004QuizPageSelectedIds = []
      if (this.act004QuizPageCurrentIndex >= this.act004QuizPageQuestions.length) {
        this.finishAct004QuizPageFlow(true)
        return
      }
      this.applyAct004QuizPageForQuestionIndex(this.act004QuizPageCurrentIndex)
    },
    async onAct004QuizPageSubmit(opts) {
      const skipPageNavigation = !!(opts && opts.skipPageNavigation)
      const submitOnlyQuestion = this.isAct004QuizImageOptionSubmitOnly()
      if (
        !skipPageNavigation &&
        this.isAct004QuizImageOptionClickToSubmit() &&
        !submitOnlyQuestion
      ) {
        const q = this.act004QuizPageCurrentQuestion
        const type = String((q && q.questionType) || 'SINGLE').toUpperCase()
        if (type === 'SINGLE') {
          this.onAct004QuizPageNavigateAfterAnswer()
          return
        }
      }
      if (this.act004QuizPageSubmitting) return
      if (
        skipPageNavigation &&
        this.isAct004QuizImageOptionClickToSubmit() &&
        (this.act004QuizPageCurrentQuestionCorrect || this.act004QuizPageQuizPassedPending)
      ) {
        showAct004QuizAlreadyCorrectToast()
        return
      }
      if (!this.validateAct004QuizPageAnswer()) {
        uni.showToast({ title: '请先完成本题', icon: 'none' })
        return
      }
      if (!this.act004QuizPageSessionId || this.act004QuizPageBootstrapping) {
        try {
          await this.bootstrapAct004QuizPageSession()
        } catch (e) {
          uni.showToast({ title: (e && e.message) || '无法开始答题', icon: 'none' })
          return
        }
      }
      const q = this.act004QuizPageCurrentQuestion
      if (!q) {
        uni.showToast({ title: '题目加载中，请稍候', icon: 'none' })
        return
      }
      if (!this.act004QuizPageSessionId) {
        uni.showToast({ title: '题目加载中，请稍候', icon: 'none' })
        return
      }
      this.act004QuizPageSubmitting = true
      try {
        const res = await submitAct004QuizAnswer({
          sessionId: this.act004QuizPageSessionId,
          questionId: q.questionId,
          answerPayload: this.buildAct004QuizPageAnswerPayload()
        })
        if (isSignstoreUnauthorizedResponse(res)) {
          await this.recoverFromSignstoreUnauthorized(res)
          return
        }
        if (!res || Number(res.status) !== 200) {
          uni.showToast({
            title: (res && res.msg) ? String(res.msg) : '提交失败',
            icon: 'none'
          })
          return
        }
        const data = res.data != null ? res.data : res
        if (data && data.passed) {
          this.act004QuizPageSubmitting = false
          if (skipPageNavigation) {
            this.act004QuizPageCurrentQuestionCorrect = true
            this.act004QuizPageQuizPassedPending = true
            showAct004QuizCorrectAnswerModal()
            return
          }
          await showAct004QuizCorrectAnswerModal()
          this.finishAct004QuizPageFlow(true)
          return
        }
        if (data && data.needRetry) {
          this.act004QuizPageSubmitting = false
          this.act004QuizPageCurrentQuestionCorrect = false
          this.act004QuizPageQuizPassedPending = false
          showAct004QuizWrongAnswerModal()
          if (
            String(this.act004QuizPageWrongRetryMode || '').toUpperCase() === 'RETRY_CURRENT' &&
            data.finished === false
          ) {
            this.act004QuizPageSelectedIds = []
            return
          }
          void this.bootstrapAct004QuizPageSession({ forceReset: true })
          return
        }
        if (data && data.isCorrect) {
          this.act004QuizPageSubmitting = false
          if (skipPageNavigation) {
            this.act004QuizPageCurrentQuestionCorrect = true
            showAct004QuizCorrectAnswerModal()
            return
          }
          await showAct004QuizCorrectAnswerModal()
          this.act004QuizPageCurrentIndex += 1
          this.act004QuizPageSelectedIds = []
          if (this.act004QuizPageCurrentIndex >= this.act004QuizPageQuestions.length) {
            this.finishAct004QuizPageFlow(true)
            return
          }
          this.applyAct004QuizPageForQuestionIndex(this.act004QuizPageCurrentIndex)
          return
        }
        this.act004QuizPageSubmitting = false
        this.act004QuizPageCurrentQuestionCorrect = false
        showAct004QuizWrongAnswerModal()
      } catch (e) {
        uni.showToast({ title: (e && e.message) || '提交失败', icon: 'none' })
      } finally {
        this.act004QuizPageSubmitting = false
      }
    },
    finishAct004QuizPageFlow(success, message) {
      this.resetAct004QuizPageState()
      if (!success && message) {
        if (this.act004QuizPendingResolve) {
          this.act004QuizPendingResolve({ ok: false, message })
          this.act004QuizPendingResolve = null
        }
        uni.showToast({ title: message, icon: 'none' })
        return
      }
      if (success) {
        this.onAct004QuizSuccess({ pointIndex: this.act004QuizPointIndex })
      } else if (this.act004QuizPendingResolve) {
        this.act004QuizPendingResolve({ ok: false, message: message || '' })
        this.act004QuizPendingResolve = null
      }
    },
    onAct004QuizSuccess(evt) {
      const pointIndex = parseInt((evt && evt.pointIndex) || this.act004QuizPointIndex, 10)
      if (Number.isFinite(pointIndex) && pointIndex > 0) {
        const raw = this.act004LimitDetailByPoint && this.act004LimitDetailByPoint[pointIndex]
        if (raw && typeof raw === 'object') {
          if (!raw.quiz || typeof raw.quiz !== 'object') {
            this.$set(raw, 'quiz', { needQuiz: true, quizPassed: true })
          } else {
            this.$set(raw.quiz, 'quizPassed', true)
          }
          this.$set(this.act004LimitDetailByPoint, pointIndex, raw)
        }
      }
      this.act004QuizDialogVisible = false
      this.act004QuizPayload = null
      if (this.act004QuizPendingResolve) {
        this.act004QuizPendingResolve({ ok: true })
        this.act004QuizPendingResolve = null
      }
    },
    onAct004QuizClose() {
      this.act004QuizDialogVisible = false
      this.act004QuizPayload = null
      if (this.act004QuizPendingResolve) {
        this.act004QuizPendingResolve({ ok: false, message: '' })
        this.act004QuizPendingResolve = null
      }
    },
    // 校验核销点位前置打卡条件（仅处理 PREREQUISITE）
    checkVerifyPrerequisite(hotspot) {
      const cond = hotspot && hotspot.verifyCondition
      if (!cond || !cond.needValidate || cond.conditionType !== 'PREREQUISITE') {
        return { ok: true, message: '' }
      }
      const prerequisiteIndexes = Array.isArray(cond.prerequisiteIndexes) ? cond.prerequisiteIndexes : []
      if (prerequisiteIndexes.length === 0) {
        return { ok: true, message: '' }
      }

      const isPreDone = (idx) =>
        this.checkedPointIndexes.some((c) => String(c) === String(idx))
      const isOr =
        cond.prerequisiteRelation === 'OR' || String(cond.prerequisiteRelation || '').toUpperCase() === 'OR'

      if (isOr) {
        if (!prerequisiteIndexes.some((n) => isPreDone(n))) {
          return {
            ok: false,
            message: '请先完成至少一个前置打卡点位'
          }
        }
        return { ok: true, message: '' }
      }

      const missing = prerequisiteIndexes.filter((n) => !isPreDone(n))
      if (missing.length > 0) {
        return {
          ok: false,
          message: '请先完成前置打卡点位'
        }
      }
      return { ok: true, message: '' }
    },
    checkVerifyCheckinCount(hotspot) {
      const cond = hotspot && hotspot.verifyCondition
      if (!cond || !cond.needValidate || cond.conditionType !== 'CHECKIN_COUNT') {
        return { ok: true, message: '' }
      }
      const min = normalizeVerifyMinCheckinCount(cond.minCheckinCount)
      if (min <= 0) {
        return { ok: true, message: '' }
      }
      const passed = evaluateVerifyCheckinCountCondition({
        minCheckinCount: min,
        getCompletedPointCount: () => (this.checkedPointIndexes || []).length
      })
      if (passed) {
        return { ok: true, message: '' }
      }
      const count = (this.checkedPointIndexes || []).length
      return {
        ok: false,
        message: `请先完成至少 ${min} 个打卡点位（当前 ${count} 个）`
      }
    },
    findHotspotByPointIndex(pointIndex) {
      const hit = this.findHotspotWithPageByPointIndex(pointIndex)
      return hit ? hit.hotspot : null
    },
    /** 解析热点在 pages 配置中的 pageIndex（与后端 Act004QuizPageConfigUtil 一致） */
    resolveHotspotPageIndexByPointIndex(pointIndex) {
      const hit = this.findHotspotWithPageByPointIndex(pointIndex)
      return hit ? hit.pageOriginalIndex : null
    },
    findHotspotWithPageByPointIndex(pointIndex) {
      if (pointIndex == null || pointIndex === '') return null
      const pages = this.pages || []
      const parseNestedList = (obj) => {
        if (!obj || typeof obj !== 'object') return []
        if (Array.isArray(obj.imageNestedHotspotsForm)) return obj.imageNestedHotspotsForm
        if (Array.isArray(obj.imageNestedHotspots)) return obj.imageNestedHotspots
        return []
      }
      for (let pi = 0; pi < pages.length; pi++) {
        const rawList = pages[pi] && pages[pi].hotspots
        if (!Array.isArray(rawList)) continue
        for (let hi = 0; hi < rawList.length; hi++) {
          const parent = rawList[hi]
          if (String(parent && parent.type) !== '3') continue
          const nestedList = parseNestedList(parent)
          for (let ni = 0; ni < nestedList.length; ni++) {
            const nested = nestedList[ni]
            const checkin = nested && (nested.checkinHotspot || nested.checkInHotspot)
            const pt = checkin && checkin.index != null ? checkin.index : null
            if (String(pt) !== String(pointIndex)) continue
            const mapped = this.mapRawHotspotsToRuntime([checkin])
            return { hotspot: mapped[0] || checkin, pageOriginalIndex: pi }
          }
        }
        const raw = rawList.find((h) => String(h && h.index) === String(pointIndex))
        if (raw) {
          const mapped = this.mapRawHotspotsToRuntime([raw])
          return { hotspot: mapped[0] || null, pageOriginalIndex: pi }
        }
      }
      if (Array.isArray(this.hotspots)) {
        const local = this.hotspots.find((item) => String(item.index) === String(pointIndex)) || null
        if (local) return { hotspot: local, pageOriginalIndex: this.currentPageIndex }
      }
      return null
    },
    normalizeLotteryConfigId(raw) {
      const n = parseInt(raw, 10)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    getShakeHotspotKey(hotspot) {
      if (!hotspot) return ''
      const idx = parseInt(hotspot.index, 10)
      if (Number.isFinite(idx) && idx > 0) return `idx-${idx}`
      const cfgId = this.normalizeLotteryConfigId(hotspot.lotteryConfigId)
      return cfgId ? `cfg-${cfgId}` : ''
    },
    isShakeHotspotAlreadyWon(hotspot) {
      const key = this.getShakeHotspotKey(hotspot)
      return !!(key && this.shakeLotteryCompletedKeys[key])
    },
    mergeUidIntoH5UserInfo(uid) {
      if (uid == null || typeof window === 'undefined') return
      try {
        const raw = window.localStorage.getItem('signstore_h5_user_info')
        const parsed = raw ? JSON.parse(raw) : {}
        if (!parsed || typeof parsed !== 'object') return
        if (!parsed.uid) {
          parsed.uid = uid
          window.localStorage.setItem('signstore_h5_user_info', JSON.stringify(parsed))
        }
      } catch (e) {
        /* ignore */
      }
    },
    resolveShakeLotteryPrizeImage(prize) {
      if (!prize || typeof prize !== 'object') return ''
      return String(
        prize.lotteryShowImage ||
        prize.lottery_show_image ||
        prize.productImage ||
        prize.product_image ||
        ''
      ).trim()
    },
    resolveShakeVerifiedPrizeImage(prize) {
      if (!prize || typeof prize !== 'object') return ''
      return String(
        prize.lotteryVerifiedImage ||
        prize.lottery_verified_image ||
        ''
      ).trim()
    },
    isShakeHotspotVerifyRequired(hotspot) {
      if (!hotspot) return false
      const mode = String(hotspot.shakeVerifyMode || hotspot.shake_verify_mode || '').trim().toLowerCase()
      const ruleCode = String(
        hotspot.shakeVerifyRuleCode ||
        hotspot.shake_verify_rule_code ||
        hotspot.hotspotRuleCode ||
        hotspot.sealRuleCode ||
        ''
      ).trim()
      if (mode === 'checkin' && ruleCode) return true
      if (mode === 'checkin') return true
      return !!ruleCode
    },
    isShakeHotspotPendingVerify(hotspot) {
      if (!hotspot || String(hotspot.type) !== '21') return false
      if (!this.isShakeHotspotAlreadyWon(hotspot)) return false
      const key = this.getShakeHotspotKey(hotspot)
      if (!key) return false
      const status = this.shakeLotteryVerifyStatusByHotspotKey[key]
      if (status === 1) return false
      // 接口已恢复为待核销时，直接展示盖章区（避免 pageConfig 未带核销字段导致不渲染）
      if (status === 0) return true
      if (!this.isShakeHotspotVerifyRequired(hotspot)) return false
      return status !== 1
    },
    getShakeLotteryPendingVerifyHotspotsForPage(pageHotspots, pageIndex) {
      const raw = Array.isArray(pageHotspots) ? pageHotspots : []
      const pi = Number.isFinite(parseInt(pageIndex, 10)) ? parseInt(pageIndex, 10) : this.currentPageIndex
      const list = this.sortedHotspotsForAct004Render(raw, pi)
      return list.filter((h) => this.isShakeHotspotPendingVerify(h))
    },
    linkCheckinZoneKey(zone, pageIndex, listIndex) {
      return getAct004LinkCheckinZoneKey(zone, pageIndex, listIndex)
    },
    getAct004LinkCheckinZoneKey(zone, pageIndex, listIndex) {
      return this.linkCheckinZoneKey(zone, pageIndex, listIndex)
    },
    /** 联动热区层级：高于普通打卡热点(10)、摇一摇核销层(24)，低于问答门禁(60) */
    act004LinkCheckinZoneZIndex() {
      return 55
    },
    invalidateLinkZoneBlockedPointIndexesCache() {
      this._linkZoneBlockedPointIndexesCachePage = null
      this._linkZoneBlockedPointIndexesCache = null
    },
    getAct004HotspotHitRectPx(hotspot, ww) {
      if (!hotspot) return null
      const rpxNum = (n) => {
        const w =
          ww != null && Number.isFinite(Number(ww))
            ? Number(ww)
            : this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
        const v = parseFloat(String(rpxToPx(n, w)).replace(/px$/i, '').trim())
        return Number.isFinite(v) ? v : 0
      }
      const leftPct = this.parseHotspotNumber(hotspot.left, 50)
      const topPct = this.parseHotspotNumber(hotspot.top, 50)
      const isRect = hotspot.shape === 'rectangle'
      const wRpx = isRect
        ? (parseInt(hotspot.sizeWidth, 10) || parseInt(hotspot.size, 10) || 80)
        : (parseInt(hotspot.radius, 10) || parseInt(hotspot.size, 10) || 80)
      const hRpx = isRect
        ? (parseInt(hotspot.sizeHeight, 10) || parseInt(hotspot.size, 10) || 80)
        : wRpx
      const cx = rpxNum((leftPct / 100) * 750)
      const cy = rpxNum((topPct / 100) * 1334)
      const hw = rpxNum(wRpx) / 2
      const hh = rpxNum(hRpx) / 2
      return { left: cx - hw, top: cy - hh, right: cx + hw, bottom: cy + hh }
    },
    act004HotspotRectsOverlap(a, b) {
      if (!a || !b) return false
      return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom)
    },
    getLinkZoneBlockedPointIndexesForPage(pageIndex) {
      const pi = Number.isFinite(parseInt(pageIndex, 10)) ? parseInt(pageIndex, 10) : this.currentPageIndex
      if (
        this._linkZoneBlockedPointIndexesCachePage === pi &&
        this._linkZoneBlockedPointIndexesCache
      ) {
        return this._linkZoneBlockedPointIndexesCache
      }
      const page = (this.pages || [])[pi]
      const zones = this.sortedLinkCheckinZonesForPage(page && page.hotspots, pi)
      const blocked = new Set()
      if (zones.length) {
        const ww = this.windowWidthPx || (typeof window !== 'undefined' ? window.innerWidth : 375)
        const zoneRects = zones.map((z) => this.getAct004HotspotHitRectPx(z, ww)).filter(Boolean)
        const hotspots = this.sortedHotspotsForAct004Render(page && page.hotspots, pi)
        hotspots.forEach((h) => {
          if (String(h.type) !== '1') return
          const pointIndex = parseInt(h.index, 10)
          if (!Number.isFinite(pointIndex) || pointIndex <= 0) return
          const hr = this.getAct004HotspotHitRectPx(h, ww)
          if (!hr) return
          if (zoneRects.some((zr) => this.act004HotspotRectsOverlap(hr, zr))) {
            blocked.add(pointIndex)
          }
        })
      }
      this._linkZoneBlockedPointIndexesCachePage = pi
      this._linkZoneBlockedPointIndexesCache = blocked
      return blocked
    },
    isCheckinHotspotBlockedByLinkZone(hotspot, pageIndex) {
      if (!hotspot || String(hotspot.type) !== '1') return false
      const pointIndex = parseInt(hotspot.index, 10)
      if (!Number.isFinite(pointIndex) || pointIndex <= 0) return false
      return this.getLinkZoneBlockedPointIndexesForPage(pageIndex).has(pointIndex)
    },
    onLinkCheckinZonePointerBlock() {},
    getLinkCheckinZoneLayerStyle(zone) {
      return this.getAct004CenterHotspotBoxStyle(
        zone,
        zone && zone.sizeWidth,
        zone && zone.sizeHeight,
        {
          zIndex: this.act004LinkCheckinZoneZIndex(),
          pointerEvents: 'auto',
          touchAction: 'none'
        }
      )
    },
    resolveAct004CheckinRuleCode(hotspot) {
      const perHotspot = hotspot && String(hotspot.sealRuleCode || '').trim()
      if (perHotspot) return perHotspot
      return String(this.ruleCode || '').trim()
    },
    canTryLinkCheckinForPoint(hotspot) {
      return !this.getLinkCheckinPointSkipReason(hotspot)
    },
    getLinkCheckinPointSkipReason(hotspot) {
      if (!hotspot || String(hotspot.type) !== '1') return '非打卡点或未找到配置'
      if (String(hotspot.checkType || 'CHECKIN').toUpperCase() === 'VERIFY') return '核销点'
      const pointIndex = parseInt(hotspot.index, 10)
      if (!Number.isFinite(pointIndex) || pointIndex <= 0) return '点位 index 无效'
      if (this.isPointIndexChecked(pointIndex)) return '已打卡'
      if (this.shouldShowHotspotNotUnlockedVisual(hotspot)) return '未解锁'
      const prerequisiteCheck = this.checkVerifyPrerequisite(hotspot)
      if (!prerequisiteCheck.ok) return prerequisiteCheck.message || '前置点位未满足'
      const checkinCountCheck = this.checkVerifyCheckinCount(hotspot)
      if (!checkinCountCheck.ok) return checkinCountCheck.message || '打卡次数条件未满足'
      if (isQuizHotspot(hotspot)) return '问答点不可联动盖章'
      if (!this.resolveAct004CheckinRuleCode(hotspot)) return '无 sealRuleCode/活动 ruleCode'
      return ''
    },
    detectActivityHasLinkCheckinZone() {
      const pages = this.pages || []
      for (let pi = 0; pi < pages.length; pi++) {
        const raw = pages[pi] && pages[pi].hotspots
        if (!Array.isArray(raw)) continue
        if (raw.some((h) => isAct004LinkCheckinZoneHotspot(h))) return true
      }
      return false
    },
    scheduleSyncLinkCheckinZoneHitTargets() {
      if (this._linkZoneSyncScrollTimer) {
        clearTimeout(this._linkZoneSyncScrollTimer)
      }
      this._linkZoneSyncScrollTimer = setTimeout(() => {
        this._linkZoneSyncScrollTimer = null
        this.syncLinkCheckinZoneHitTargets()
      }, 100)
    },
    /** 每次触摸前取「当前屏可见」热区矩形（避免多页纵向滚动时 getElementById 命中屏外节点） */
    collectLinkCheckinZoneHitTargetsFresh() {
      const pi = this.currentPageIndex
      const page = (this.pages || [])[pi]
      const zones = this.sortedLinkCheckinZonesForPage(page && page.hotspots, pi)
      if (!zones.length || typeof document === 'undefined') return []

      const vh = (typeof window !== 'undefined' && window.innerHeight) || 667
      const vw = (typeof window !== 'undefined' && window.innerWidth) || 375
      const targets = []
      const seenEl = new Set()

      const pushVisibleTarget = (el, zone, zi) => {
        if (!el || !zone || seenEl.has(el)) return
        const r = el.getBoundingClientRect()
        if (!r || r.width <= 0 || r.height <= 0) return
        const inViewport = r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw
        if (!inViewport) return
        seenEl.add(el)
        targets.push({
          zone,
          pageIndex: pi,
          zi,
          el,
          rect: {
            left: r.left,
            top: r.top,
            right: r.right,
            bottom: r.bottom,
            width: r.width,
            height: r.height
          }
        })
      }

      zones.forEach((zone, zi) => {
        const id = 'linkCheckinZoneTouchArea-' + getAct004LinkCheckinZoneKey(zone, pi, zi)
        const nodes = document.querySelectorAll('#' + id)
        if (nodes && nodes.length) {
          nodes.forEach((el) => pushVisibleTarget(el, zone, zi))
        }
      })

      if (!targets.length) {
        const captures = document.querySelectorAll(
          '.link-checkin-zone-touch-capture[data-page-index="' + String(pi) + '"]'
        )
        captures.forEach((el) => {
          const zi = parseInt(el.getAttribute('data-zone-zi'), 10)
          const zone = zones[Number.isFinite(zi) ? zi : 0]
          if (zone) pushVisibleTarget(el, zone, Number.isFinite(zi) ? zi : 0)
        })
      }

      return targets
    },
    syncLinkCheckinZoneHitTargets() {
      this._linkZoneHitTargets = this.collectLinkCheckinZoneHitTargetsFresh()
    },
    normalizeLinkCheckinClientPoint(touch) {
      if (!touch) return { x: 0, y: 0 }
      const x =
        touch.clientX != null
          ? touch.clientX
          : touch.pageX != null
            ? touch.pageX
            : 0
      const y =
        touch.clientY != null
          ? touch.clientY
          : touch.pageY != null
            ? touch.pageY
            : 0
      return { x, y }
    },
    isPointInLinkCheckinRect(x, y, rect) {
      if (!rect) return false
      const pad = 4
      return (
        x >= rect.left - pad &&
        x <= rect.right + pad &&
        y >= rect.top - pad &&
        y <= rect.bottom + pad
      )
    },
    ensureLinkCheckinZoneDocumentTouch() {
      if (typeof document === 'undefined') return
      if (!this._activityHasLinkCheckinZone && !this.detectActivityHasLinkCheckinZone()) return
      this._activityHasLinkCheckinZone = true
      if (this._linkZoneDocTouchInstalled) return
      this._linkZoneDocTouchBound = (e) => this.onLinkCheckinZoneDocumentTouch(e)
      const opts = { capture: true, passive: false }
      document.addEventListener('touchstart', this._linkZoneDocTouchBound, opts)
      document.addEventListener('touchmove', this._linkZoneDocTouchBound, opts)
      document.addEventListener('touchend', this._linkZoneDocTouchBound, opts)
      document.addEventListener('touchcancel', this._linkZoneDocTouchBound, opts)
      this._linkZoneDocTouchInstalled = true
    },
    teardownLinkCheckinZoneDocumentTouch() {
      if (typeof document === 'undefined' || !this._linkZoneDocTouchBound) return
      document.removeEventListener('touchstart', this._linkZoneDocTouchBound, true)
      document.removeEventListener('touchmove', this._linkZoneDocTouchBound, true)
      document.removeEventListener('touchend', this._linkZoneDocTouchBound, true)
      document.removeEventListener('touchcancel', this._linkZoneDocTouchBound, true)
      this._linkZoneDocTouchBound = null
      this._linkZoneDocTouchInstalled = false
      if (this._linkZoneSyncScrollTimer) {
        clearTimeout(this._linkZoneSyncScrollTimer)
        this._linkZoneSyncScrollTimer = null
      }
    },
    findLinkCheckinZoneHitTarget(clientX, clientY) {
      const targets = this.collectLinkCheckinZoneHitTargetsFresh()
      this._linkZoneHitTargets = targets
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i]
        if (this.isPointInLinkCheckinRect(clientX, clientY, t.rect)) {
          return t
        }
      }
      return null
    },
    onLinkCheckinZoneDocumentTouch(event) {
      if (!this._activityHasLinkCheckinZone && !this.hasLinkCheckinZoneOnCurrentPage) return
      const phase = event && event.type ? event.type : ''
      const extracted = this.extractTouchesFromPointerEvent(event)
      if (!this.hasLinkCheckinZoneOnCurrentPage) return
      if (!extracted.touchCount) return
      if (phase !== 'touchstart' && phase !== 'touchmove') return
      const t0 = extracted.touches[0]
      const pt = this.normalizeLinkCheckinClientPoint(t0)
      const hit = this.findLinkCheckinZoneHitTarget(pt.x, pt.y)
      if (!hit) return
      const synthetic = Object.assign({}, event, { currentTarget: hit.el, type: phase })
      this.processLinkCheckinZoneTouch(synthetic, hit.zone, hit.pageIndex, hit.zi)
    },
    /**
     * 解析触摸点（与盖章弹框 touch-capture-layer 一致：兼容 H5 / uni 包装事件）
     * touchstart/move 用 touches；touchend 时 touches 常为空，用 changedTouches。
     */
    extractTouchesFromPointerEvent(event) {
      const raw = (event && (event.originalEvent || event.nativeEvent)) || event || {}
      const phase = String((event && event.type) || raw.type || '').toLowerCase()
      let list = raw.touches && raw.touches.length ? raw.touches : null
      if ((!list || !list.length) && phase.indexOf('end') >= 0 && raw.changedTouches && raw.changedTouches.length) {
        list = raw.changedTouches
      }
      if ((!list || !list.length) && event && event.detail) {
        const d = event.detail
        if (d.touches && d.touches.length) list = d.touches
        else if (d.changedTouches && d.changedTouches.length) list = d.changedTouches
      }
      if (!list || !list.length) {
        return { touchCount: 0, touches: [], source: 'none' }
      }
      const touches = []
      for (let i = 0; i < list.length; i++) {
        const touch = list[i]
        touches.push({
          clientX: touch.clientX !== undefined ? touch.clientX : (touch.pageX !== undefined ? touch.pageX : 0),
          clientY: touch.clientY !== undefined ? touch.clientY : (touch.pageY !== undefined ? touch.pageY : 0)
        })
      }
      const source =
        raw.touches && raw.touches.length
          ? 'touches'
          : raw.changedTouches && raw.changedTouches.length
            ? 'changedTouches'
            : 'detail'
      return { touchCount: touches.length, touches, source }
    },
    buildLinkCheckinZoneTouchMeta(event, captureEl) {
      const phase = event && event.type ? event.type : 'touch'
      const extracted = this.extractTouchesFromPointerEvent(event)
      const el = captureEl || (event && event.currentTarget)
      let rectOk = false
      let rect = null
      let standardPoints = null
      if (el && typeof el.getBoundingClientRect === 'function') {
        const r = el.getBoundingClientRect()
        rectOk = !!(r && r.width > 0 && r.height > 0)
        if (rectOk) {
          rect = { left: r.left, top: r.top, width: r.width, height: r.height }
          if (extracted.touchCount > 0) {
            standardPoints = []
            for (let i = 0; i < extracted.touches.length; i++) {
              const touch = extracted.touches[i]
              const x = touch.clientX - r.left
              const y = touch.clientY - r.top
              const standardX = Math.round((x / r.width) * this.standardWidth)
              const standardY = Math.round((y / r.height) * this.standardHeight)
              standardPoints.push([standardX, standardY])
            }
          }
        }
      }
      const domId =
        el && el.id
          ? el.id
          : 'linkCheckinZoneTouchArea-' + this.linkCheckinZoneKey(null, -1, -1)
      return {
        phase,
        touchCount: extracted.touchCount,
        touchSource: extracted.source,
        rectOk,
        rect,
        standardPoints,
        rawTouches: extracted.touches,
        selector: domId ? `#${domId}` : ''
      }
    },
    processLinkCheckinZoneTouch(event, zone, pageIndex, listIndex) {
      if (!isAct004LinkCheckinZoneHotspot(zone)) return
      const captureEl = event && event.currentTarget
      const meta = this.buildLinkCheckinZoneTouchMeta(event, captureEl)
      if (!meta.rectOk) return
      if (meta.touchCount !== 4) return
      if (!meta.standardPoints || meta.standardPoints.length !== 4) return
      if (this.linkCheckinZoneChecking) return
      if (this._linkCheckinZoneGestureSubmitted) return
      this._linkCheckinZoneGestureSubmitted = true
      this.doValidateLinkCheckinZoneSeal(zone, meta.standardPoints, pageIndex)
    },
    handleLinkCheckinZoneTouchStart(event, zone, pageIndex, listIndex) {
      this.processLinkCheckinZoneTouch(event, zone, pageIndex, listIndex)
    },
    handleLinkCheckinZoneTouchMove(event, zone, pageIndex, listIndex) {
      this.processLinkCheckinZoneTouch(event, zone, pageIndex, listIndex)
    },
    handleLinkCheckinZoneTouchEnd(event, zone, pageIndex, listIndex) {
      const meta = this.buildLinkCheckinZoneTouchMeta(event, event && event.currentTarget)
      if (meta.touchCount === 0) {
        this._linkCheckinZoneGestureSubmitted = false
      }
    },
    async doValidateLinkCheckinZoneSeal(zoneHotspot, touchPoints, pageOriginalIndex) {
      if (!zoneHotspot || this.linkCheckinZoneChecking) return
      const pointIndexes = normalizeLinkCheckinPointIndexes(zoneHotspot.linkCheckinPointIndexes)
      if (!pointIndexes.length) return

      if (this.needsProfileAuth()) {
        this.openAuthPanel()
        this._linkCheckinZoneGestureSubmitted = false
        return
      }
      const authStoreCode = (this.authStoreCode && String(this.authStoreCode).trim()) || ''
      if (!authStoreCode) {
        this._linkCheckinZoneGestureSubmitted = false
        return
      }
      if (!this.activityUserId) {
        await this.loadUserRecord()
      }
      if (!this.activityUserId) {
        this._linkCheckinZoneGestureSubmitted = false
        return
      }

      this.linkCheckinZoneChecking = true

      try {
        for (let i = 0; i < pointIndexes.length; i++) {
          const pointIndex = pointIndexes[i]
          const hit = this.findHotspotWithPageByPointIndex(pointIndex)
          const target = hit && hit.hotspot
          if (!target) continue
          if (this.getLinkCheckinPointSkipReason(target)) continue

          const limitCheck = await this.checkAct004DailyLimitBeforeCheckin(target, false)
          if (limitCheck && limitCheck.blocked) continue

          const ruleCode = this.resolveAct004CheckinRuleCode(target)
          if (!ruleCode) continue

          this.currentHotspotIndex = pointIndex

          let validateRes = null
          try {
            validateRes = await validateSealForAct004({
              ruleCode,
              touchPoints,
              activityUserId: this.activityUserId,
              operationType: 'CHECKIN',
              pointIndex,
              authStoreCode
            })
          } catch (e) {
            continue
          }

          if (isSignstoreUnauthorizedResponse(validateRes)) {
            if (await this.recoverFromSignstoreUnauthorized(validateRes)) {
              this._linkCheckinZoneGestureSubmitted = false
              this.linkCheckinZoneChecking = false
              return this.doValidateLinkCheckinZoneSeal(zoneHotspot, touchPoints, pageOriginalIndex)
            }
            return
          }

          if (!this.isAct004SealValidationMatched(validateRes)) {
            continue
          }
          await this.hotspotPostCheckInSuccess(validateRes, { silent: true })
          this.showLinkCheckinZoneSuccessPopup(zoneHotspot)
          break
        }
      } finally {
        this.linkCheckinZoneChecking = false
        this._linkCheckinZoneGestureSubmitted = false
      }
    },
    clearLinkCheckinSuccessPopupTimer() {
      if (this._linkCheckinSuccessPopupTimer) {
        clearTimeout(this._linkCheckinSuccessPopupTimer)
        this._linkCheckinSuccessPopupTimer = null
      }
    },
    buildLinkCheckinSuccessPopupBoxStyle(zoneHotspot) {
      const ww =
        this.windowWidthPx != null
          ? this.windowWidthPx
          : typeof window !== 'undefined'
            ? window.innerWidth || 375
            : 375
      const wRpx = normalizeLinkCheckinSuccessWidthRpx(zoneHotspot && zoneHotspot.linkCheckinSuccessWidthRpx)
      const hRpx = normalizeLinkCheckinSuccessHeightRpx(zoneHotspot && zoneHotspot.linkCheckinSuccessHeightRpx)
      return {
        width: rpxToPx(wRpx, ww),
        height: rpxToPx(hRpx, ww),
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100vh - 40px)',
        flexShrink: '0',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }
    },
    showLinkCheckinZoneSuccessPopup(zoneHotspot) {
      if (!zoneHotspot) return
      const img = String(zoneHotspot.linkCheckinSuccessImg || '').trim()
      if (!img) return
      const ms = normalizeLinkCheckinSuccessDurationMs(zoneHotspot.linkCheckinSuccessDurationMs)
      this.clearLinkCheckinSuccessPopupTimer()
      this.previewHotspotNestedHotspots = []
      this.linkCheckinSuccessPopupActive = true
      this.linkCheckinSuccessPopupBoxStyle = this.buildLinkCheckinSuccessPopupBoxStyle(zoneHotspot)
      this.previewHotspotImage = this.getImageUrl(img)
      this.showHotspotImagePreview = true
      this._linkCheckinSuccessPopupTimer = setTimeout(() => {
        this._linkCheckinSuccessPopupTimer = null
        this.hideHotspotImagePreview()
      }, ms)
    },
    getShakeLotteryVerifySealLayerStyle(hotspot) {
      return this.getAct004CenterHotspotBoxStyle(hotspot, 600, 600, {
        zIndex: 24,
        pointerEvents: 'auto',
        touchAction: 'none'
      })
    },
    applyShakeLotteryVerifyStateToHotspot(hotspot, prize, drawRecordId) {
      const key = hotspot ? this.getShakeHotspotKey(hotspot) : ''
      if (!key) return
      let verifyStatus = prize && prize.verifyStatus != null
        ? parseInt(prize.verifyStatus, 10)
        : NaN
      if (!Number.isFinite(verifyStatus)) {
        verifyStatus = this.isShakeHotspotVerifyRequired(hotspot) ? 0 : 1
      }
      const normalizedStatus = verifyStatus === 1 ? 1 : 0
      this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, normalizedStatus)
      const recordId = drawRecordId != null
        ? drawRecordId
        : (prize && (prize.drawRecordId != null ? prize.drawRecordId : prize.draw_record_id))
      if (recordId != null) {
        this.$set(this.shakeLotteryDrawRecordIdByHotspotKey, key, recordId)
      }
      const verifiedImg = this.resolveShakeVerifiedPrizeImage(prize)
      if (verifiedImg) {
        this.$set(this.shakeVerifiedPrizeImageByHotspotKey, key, verifiedImg)
        if (normalizedStatus === 1 && hotspot) {
          this.$set(hotspot, 'shakeVerifiedPrizeImage', verifiedImg)
        }
      }
    },
    applyShakeLotteryPrizeToHotspot(hotspot, prize) {
      const rawImg = this.resolveShakeLotteryPrizeImage(prize)
      const key = hotspot ? this.getShakeHotspotKey(hotspot) : ''
      if (key) {
        this.$set(this.shakeLotteryCompletedKeys, key, true)
        if (rawImg) {
          this.$set(this.shakeWonPrizeImageByHotspotKey, key, rawImg)
        }
        this.applyShakeLotteryVerifyStateToHotspot(hotspot, prize)
        if (hotspot && hotspot.index != null) {
          this.cacheShakeLotteryVerifyRuleCodeForIndex(hotspot.index)
        }
      }
      if (!hotspot) return !!rawImg
      if (rawImg) {
        this.$set(hotspot, 'shakeWonPrizeImage', rawImg)
      }
      return !!rawImg
    },
    markShakeHotspotCompletedByIndex(hotspotIndex, prizeImage, extra) {
      const idx = parseInt(hotspotIndex, 10)
      if (!Number.isFinite(idx) || idx <= 0) return
      const key = `idx-${idx}`
      this.$set(this.shakeLotteryCompletedKeys, key, true)
      const meta = extra && typeof extra === 'object' ? extra : {}
      const verifyStatus = meta.verifyStatus != null ? parseInt(meta.verifyStatus, 10) : null
      const verifiedImg = String(meta.verifiedImage || '').trim()
      const showImg = String(prizeImage || '').trim()
      if (verifyStatus === 1 && verifiedImg) {
        this.$set(this.shakeVerifiedPrizeImageByHotspotKey, key, verifiedImg)
        this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, 1)
      } else if (verifyStatus === 0) {
        this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, 0)
      } else if (this.isShakeHotspotVerifyRequired(this.findShakeHotspotByIndex(idx))) {
        this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, 0)
      } else {
        this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, 1)
      }
      if (meta.drawRecordId != null) {
        this.$set(this.shakeLotteryDrawRecordIdByHotspotKey, key, meta.drawRecordId)
      }
      if (showImg) {
        this.$set(this.shakeWonPrizeImageByHotspotKey, key, showImg)
      }
      const patchHotspot = (h) => {
        if (!h || parseInt(h.index, 10) !== idx) return
        if (showImg) this.$set(h, 'shakeWonPrizeImage', showImg)
        if (verifyStatus === 1 && verifiedImg) this.$set(h, 'shakeVerifiedPrizeImage', verifiedImg)
      }
      if (Array.isArray(this.pages)) {
        this.pages.forEach((page) => {
          if (!page || !Array.isArray(page.hotspots)) return
          page.hotspots.forEach(patchHotspot)
        })
      }
      if (Array.isArray(this.hotspots)) {
        this.hotspots.forEach(patchHotspot)
      }
    },
    findShakeHotspotByIndex(hotspotIndex) {
      const idx = parseInt(hotspotIndex, 10)
      if (!Number.isFinite(idx) || idx <= 0) return null
      if (Array.isArray(this.pages)) {
        for (let pi = 0; pi < this.pages.length; pi++) {
          const page = this.pages[pi]
          if (!page || !Array.isArray(page.hotspots)) continue
          const merged = this.sortedHotspotsForAct004Render(page.hotspots, pi)
          const hs = merged.find((h) => parseInt(h && h.index, 10) === idx && String(h.type) === '21')
          if (hs) return hs
        }
      }
      if (Array.isArray(this.hotspots)) {
        const merged = this.sortedHotspotsForAct004Render(this.hotspots, this.currentPageIndex)
        return merged.find((h) => parseInt(h && h.index, 10) === idx && String(h.type) === '21') || null
      }
      return null
    },
    cacheShakeLotteryVerifyRuleCodeForIndex(hotspotIndex) {
      const hs = this.findShakeHotspotByIndex(hotspotIndex)
      if (!hs) return
      const key = this.getShakeHotspotKey(hs)
      const code = String(
        hs.shakeVerifyRuleCode ||
        this.shakeLotteryVerifyRuleCodeByHotspotKey[key] ||
        ''
      ).trim()
      if (key && code) {
        this.$set(this.shakeLotteryVerifyRuleCodeByHotspotKey, key, code)
        this.$set(hs, 'shakeVerifyRuleCode', code)
      }
    },
    restoreShakeLotteryDrawRecord(record) {
      if (!record || typeof record !== 'object') return
      const hotspotIndex = record.hotspotIndex != null ? record.hotspotIndex : record.hotspot_index
      const showImg = this.resolveShakeLotteryPrizeImage(record)
      const verifiedImg = this.resolveShakeVerifiedPrizeImage(record)
      const verifyStatus = record.verifyStatus != null ? record.verifyStatus : record.verify_status
      this.markShakeHotspotCompletedByIndex(hotspotIndex, showImg, {
        drawRecordId: record.drawRecordId != null ? record.drawRecordId : record.draw_record_id,
        verifyStatus,
        verifiedImage: verifiedImg
      })
      this.cacheShakeLotteryVerifyRuleCodeForIndex(hotspotIndex)
    },
    async loadShakeLotteryDrawRecords() {
      if (!this.activityUserId) return
      try {
        const res = await listSignStoreLotteryDrawRecords({
          activityUserId: this.activityUserId
        })
        if (isSignstoreUnauthorizedResponse(res)) {
          await this.recoverFromSignstoreUnauthorized(res)
          return
        }
        if (!res || res.status !== 200 || !Array.isArray(res.data)) return
        const seenHotspotIndexes = new Set()
        res.data.forEach((item) => {
          const hotspotIndex = item && (item.hotspotIndex != null ? item.hotspotIndex : item.hotspot_index)
          const idx = parseInt(hotspotIndex, 10)
          if (!Number.isFinite(idx) || idx <= 0 || seenHotspotIndexes.has(idx)) return
          seenHotspotIndexes.add(idx)
          this.restoreShakeLotteryDrawRecord(item)
        })
      } catch (e) {
        /* ignore */
      }
    },
    hasUnwonShakeHotspotOnCurrentPage() {
      return this.getCurrentPageShakeHotspots().some((h) => !this.isShakeHotspotAlreadyWon(h))
    },
    getCurrentPageShakeHotspots() {
      const list = Array.isArray(this.hotspots) ? this.hotspots : []
      return list.filter((h) => {
        if (!h || String(h.type) !== '21') return false
        if (String(h.shakeAfterAction || 'lottery') !== 'lottery') return false
        return !!this.normalizeLotteryConfigId(h.lotteryConfigId)
      })
    },
    destroyShakeLotteryListener() {
      if (typeof window === 'undefined') return
      if (this._shakeMotionHandler) {
        window.removeEventListener('devicemotion', this._shakeMotionHandler, false)
        this._shakeMotionHandler = null
      }
      if (this._shakeUseUni && typeof uni !== 'undefined') {
        try {
          if (this._shakeUniHandler && typeof uni.offAccelerometerChange === 'function') {
            uni.offAccelerometerChange(this._shakeUniHandler)
          }
          if (typeof uni.stopAccelerometer === 'function') {
            uni.stopAccelerometer()
          }
        } catch (e) {
          /* ignore */
        }
        this._shakeUniHandler = null
        this._shakeUseUni = false
      }
      this._shakeMotionLastX = null
      this._shakeMotionLastY = null
      this._shakeMotionLastZ = null
      this._shakeMotionLastTime = 0
    },
    isShakeSecureContext() {
      if (typeof window === 'undefined') return false
      return window.isSecureContext === true || (window.location && window.location.protocol === 'https:')
    },
    needsShakeMotionPermission() {
      if (typeof window === 'undefined') return false
      const DM = window.DeviceMotionEvent
      return !!(DM && typeof DM.requestPermission === 'function')
    },
    getShakeDeltaThreshold() {
      return this._shakeUseUni ? 1.0 : 8
    },
    delayMs(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    async requestShakeMotionPermissionIfNeeded() {
      if (typeof window === 'undefined') return false
      const DM = window.DeviceMotionEvent
      if (!DM) {
        return !!(typeof uni !== 'undefined' && typeof uni.startAccelerometer === 'function')
      }
      if (typeof DM.requestPermission !== 'function') {
        return true
      }
      try {
        const state = await DM.requestPermission()
        return state === 'granted'
      } catch (e) {
        return false
      }
    },
    tryStartDeviceMotionListener() {
      if (!window.DeviceMotionEvent) {
        return false
      }
      this._shakeUseUni = false
      this._shakeMotionHandler = (event) => this.onShakeDeviceMotion(event)
      window.addEventListener('devicemotion', this._shakeMotionHandler, false)
      return true
    },
    tryStartUniAccelerometerListener() {
      if (typeof uni === 'undefined' || typeof uni.startAccelerometer !== 'function') {
        return false
      }
      this._shakeUniHandler = (res) => {
        const x = res && res.x != null ? res.x : 0
        const y = res && res.y != null ? res.y : 0
        const z = res && res.z != null ? res.z : 0
        this.processShakeMotionSample(x, y, z)
      }
      uni.startAccelerometer({
        interval: 'game',
        success: () => {
          uni.onAccelerometerChange(this._shakeUniHandler)
          this._shakeUseUni = true
        },
        fail: () => {
          this.tryStartDeviceMotionListener()
        }
      })
      return false
    },
    syncShakeLotteryListener() {
      this.destroyShakeLotteryListener()
      if (typeof window === 'undefined') return
      const shakeHotspots = this.getCurrentPageShakeHotspots()
      if (!shakeHotspots.length) return
      if (!this.hasUnwonShakeHotspotOnCurrentPage()) return
      if (this.showcheckInBox || this.showLoading) return
      if (this.needsShakeMotionPermission() && !this._shakeMotionPermissionGranted) {
        return
      }
      if (this.isShakeSecureContext() && window.DeviceMotionEvent) {
        this.tryStartDeviceMotionListener()
        return
      }
      if (this.tryStartUniAccelerometerListener()) {
        return
      }
      this.tryStartDeviceMotionListener()
    },
    processShakeMotionSample(x, y, z) {
      if (this.showcheckInBox || this.showLoading) return
      const shakeHotspots = this.getCurrentPageShakeHotspots()
      if (!shakeHotspots.length) return
      const target = shakeHotspots.find((h) => !this.isShakeHotspotAlreadyWon(h)) || null
      const now = Date.now()
      if (now - this._shakeMotionLastTime < 80) return
      const threshold = this.getShakeDeltaThreshold()
      if (this._shakeMotionLastX != null) {
        const delta =
          Math.abs(x - this._shakeMotionLastX) +
          Math.abs(y - this._shakeMotionLastY) +
          Math.abs(z - this._shakeMotionLastZ)
        if (delta > threshold) {
          if (!target || this.shakeLotteryDrawing) {
            this._shakeMotionLastX = x
            this._shakeMotionLastY = y
            this._shakeMotionLastZ = z
            this._shakeMotionLastTime = now
            return
          }
          this.triggerShakeLotteryDraw(target, false, true)
        }
      }
      this._shakeMotionLastX = x
      this._shakeMotionLastY = y
      this._shakeMotionLastZ = z
      this._shakeMotionLastTime = now
    },
    onShakeDeviceMotion(event) {
      const acc = event && (event.accelerationIncludingGravity || event.acceleration)
      if (!acc) return
      const x = acc.x == null ? 0 : acc.x
      const y = acc.y == null ? 0 : acc.y
      const z = acc.z == null ? 0 : acc.z
      this.processShakeMotionSample(x, y, z)
    },
    extractShakeLotteryErrorMessage(err, res) {
      if (res && typeof res === 'object') {
        const msg = String(res.msg || res.message || '').trim()
        if (msg) return msg
      }
      if (err && err.response && err.response.data) {
        const d = err.response.data
        const msg = String((d && (d.msg || d.message)) || '').trim()
        if (msg) return msg
      }
      if (err && err.message) return String(err.message)
      return '抽奖失败'
    },
    async ensureShakeLotteryAuthReady() {
      if (typeof window !== 'undefined' && this.h5AuthMode !== 'thirdPart' && this.h5AuthMode !== 'member') {
        const q = (this.$route && this.$route.query) || {}
        if (!this.h5Uid) {
          this.h5Uid = ensureSignstoreH5Uid(q.h5_uid)
        }
        this.h5AuthMode = 'anonymous'
        clearIncompatibleSignstoreAuthForAnonymous(this.h5Uid)
        await this.ensureH5AnonymousToken()
      }
      let user = this.getCurrentUserCallbackInfo()
      let uid = user && user.uid != null ? parseInt(user.uid, 10) : NaN
      if (!Number.isFinite(uid) || uid <= 0) {
        await this.loadUserRecord()
        user = this.getCurrentUserCallbackInfo()
        uid = user && user.uid != null ? parseInt(user.uid, 10) : NaN
      }
      if (!Number.isFinite(uid) || uid <= 0) {
        const cached = parseInt(this.signstoreUserUid, 10)
        if (Number.isFinite(cached) && cached > 0) {
          uid = cached
        }
      }
      if (!Number.isFinite(uid) || uid <= 0) {
        throw new Error('请先登录后再抽奖')
      }
      return uid
    },
    async triggerShakeLotteryDraw(hotspot, retryAfterAuth, fromShake) {
      if (!hotspot) return
      if (this.isShakeHotspotAlreadyWon(hotspot)) return
      if (this.shakeLotteryDrawing) return
      const lotteryConfigId = this.normalizeLotteryConfigId(hotspot.lotteryConfigId)
      if (!lotteryConfigId) {
        if (typeof uni !== 'undefined' && typeof uni.showToast === 'function') {
          uni.showToast({ title: '未配置抽奖规则', icon: 'none', duration: 2200 })
        }
        return
      }
      const drawStartAt = Date.now()
      this.shakeLotteryDrawing = true
      this._shakeLastDrawAt = drawStartAt
      this.showShakeLotteryDrawingHint = true
      let res = null
      try {
        const uid = await this.ensureShakeLotteryAuthReady()
        const payload = {
          userId: uid,
          lotteryConfigId
        }
        if (this.activityUserId) payload.activityUserId = this.activityUserId
        if (this.isShakeHotspotVerifyRequired(hotspot)) {
          payload.shakeVerifyRequired = true
        }
        const hi = parseInt(hotspot.index, 10)
        if (Number.isFinite(hi) && hi > 0) payload.hotspotIndex = hi
        res = await drawSignStoreLottery(payload)
        if (isSignstoreUnauthorizedResponse(res)) {
          if (!retryAfterAuth && (await this.recoverFromSignstoreUnauthorized(res))) {
            this.showShakeLotteryDrawingHint = false
            this.shakeLotteryDrawing = false
            return this.triggerShakeLotteryDraw(hotspot, true, fromShake)
          }
          const msg = getSignstoreUnauthorizedMessage(res) || '登录失效，请重试'
          if (typeof uni !== 'undefined' && typeof uni.showToast === 'function') {
            uni.showToast({ title: msg, icon: 'none', duration: 2800 })
          }
          return
        }
        if (!res || res.status !== 200 || !res.data) {
          const errMsg = this.extractShakeLotteryErrorMessage(null, res)
          if (errMsg && errMsg.indexOf('已参与过') >= 0) {
            await this.loadShakeLotteryDrawRecords()
          }
          if (typeof uni !== 'undefined' && typeof uni.showToast === 'function') {
            uni.showToast({ title: errMsg, icon: 'none', duration: 2800 })
          }
          return
        }
        const remain = Math.max(0, 2000 - (Date.now() - drawStartAt))
        if (remain > 0) {
          await this.delayMs(remain)
        }
        const prize = res.data
        this.applyShakeLotteryPrizeToHotspot(hotspot, prize)
        if (typeof uni !== 'undefined' && typeof uni.showToast === 'function') {
          if (this.isShakeHotspotPendingVerify(hotspot)) {
            uni.showToast({ title: '恭喜中奖，请在奖品区域盖章核销', icon: 'none', duration: 2600 })
          } else {
            const title = prize.productName ? `恭喜获得 ${prize.productName}` : '恭喜中奖'
            uni.showToast({ title, icon: 'none', duration: 2200 })
          }
        }
      } catch (e) {
        const errMsg = this.extractShakeLotteryErrorMessage(e, res)
        if (typeof uni !== 'undefined' && typeof uni.showToast === 'function') {
          uni.showToast({ title: errMsg, icon: 'none', duration: 2800 })
        }
      } finally {
        this.showShakeLotteryDrawingHint = false
        this.shakeLotteryDrawing = false
        this.syncShakeLotteryListener()
      }
    },
    getCurrentUserCallbackInfo() {
      let storeUser =
        this.$store && this.$store.getters && this.$store.getters.userInfo ? this.$store.getters.userInfo : {}
      try {
        const raw = window.localStorage.getItem('signstore_h5_user_info')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed && typeof parsed === 'object') {
            storeUser = { ...parsed, ...storeUser }
          }
        }
      } catch (e) {
        /* ignore */
      }
      const uid = storeUser.uid || storeUser.userId || storeUser.id || null
      const openid = storeUser.openid || storeUser.openId || storeUser.username || storeUser.wechatOpenid || storeUser.wxOpenid || ''
      const thirdPartUidStored =
        storeUser.thirdPartUid ||
        storeUser.third_part_uid ||
        ''
      const thirdPartUid =
        storeUser.externalThirdPartUid ||
        storeUser.external_third_part_uid ||
        thirdPartUidStored
      return { uid, openid, third_part_uid: thirdPartUid, third_part_uid_stored: thirdPartUidStored }
    },
    buildHotspotCallbackPayload(result, hotspot) {
      const user = this.getCurrentUserCallbackInfo()
      const now = moment().format('YYYY-MM-DD HH:mm:ss')
      const hs = hotspot || this.findHotspotByPointIndex(this.currentHotspotIndex)
      const hotspotName = hs && hs.name != null ? String(hs.name) : ''
      return {
        openid: user.openid || '',
        uid: user.uid || null,
        third_part_uid: user.third_part_uid || '',
        pointIndex: this.currentHotspotIndex || null,
        hotspotName,
        signTime: now,
        matched: !!(result && result.matched)
      }
    },
    triggerHotspotSuccessCallback(result) {
      try {
        const hotspot = this.findHotspotByPointIndex(this.currentHotspotIndex)
        const callbackUrl = hotspot && hotspot.successCallbackUrl ? String(hotspot.successCallbackUrl).trim() : ''
        if (!callbackUrl) return
        const method =
          String(hotspot && hotspot.successCallbackMethod == null ? '' : hotspot.successCallbackMethod)
            .trim()
            .toUpperCase() === 'GET'
            ? 'GET'
            : 'POST'
        const payload = this.buildHotspotCallbackPayload(result, hotspot)

        // 仅白名单 activityCode + 外部 https 回调走「图片打点」；其余活动始终 uni.request（GET/POST）
        if (
          shouldUseHotspotCallbackBeacon(this.activityCode) &&
          isExternalHttpSuccessCallbackUrl(callbackUrl)
        ) {
          fireHotspotCallbackBeaconGet(callbackUrl, payload, {
            retainImg: (img) => {
              this._successCallbackBeaconImg = img
            },
            timeout: 8000
          })
          return
        }

        const query = Object.keys(payload)
          .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key] == null ? '' : payload[key])}`)
          .join('&')
        const baseJoined = callbackUrl.includes('?') ? `${callbackUrl}&${query}` : `${callbackUrl}?${query}`

        if (method === 'GET') {
          uni.request({ url: baseJoined, method: 'GET', timeout: 8000 })
        } else {
          uni.request({
            url: callbackUrl,
            method: 'POST',
            data: payload,
            header: { 'content-type': 'application/json' },
            timeout: 8000
          })
        }
      } catch (e) {
        // 回调失败不影响打卡主流程
      }
    },
    getShippingDraftStorageKey(pointIndex) {
      const code = this.activityCode || this.activityId || 'unknown'
      const userId = this.activityUserId || 'guest'
      return `sign_shipping_draft_${code}_${userId}_${pointIndex || 'none'}`
    },
    loadShippingDraft(pointIndex) {
      try {
        const raw = uni.getStorageSync(this.getShippingDraftStorageKey(pointIndex))
        if (!raw) return null
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
        if (!parsed || typeof parsed !== 'object') return null
        return {
          receiverName: parsed.receiverName || '',
          receiverPhone: parsed.receiverPhone || '',
          province: parsed.province || '',
          city: parsed.city || '',
          district: parsed.district || '',
          address: parsed.address || '',
          saved: false
        }
      } catch (e) {
        return null
      }
    },
    persistShippingDraft() {
      if (!this.shippingHotspotIndex || this.shippingReadonly) return
      const payload = {
        receiverName: (this.shippingForm.receiverName || '').trim(),
        receiverPhone: (this.shippingForm.receiverPhone || '').trim(),
        province: (this.shippingForm.province || '').trim(),
        city: (this.shippingForm.city || '').trim(),
        district: (this.shippingForm.district || '').trim(),
        address: (this.shippingForm.address || '').trim()
      }
      uni.setStorageSync(this.getShippingDraftStorageKey(this.shippingHotspotIndex), JSON.stringify(payload))
    },
    clearShippingDraft(pointIndex) {
      uni.removeStorageSync(this.getShippingDraftStorageKey(pointIndex))
    },
    syncShippingRegionFromForm() {
      this.shippingRegion = [
        this.shippingForm.province || '',
        this.shippingForm.city || '',
        this.shippingForm.district || ''
      ]
    },
    async openShippingDialog(pointIndex) {
      if (!this.activityUserId || !pointIndex) {
        uni.showToast({ title: '活动记录或点位信息缺失', icon: 'none' })
        return
      }
      this.shippingHotspotIndex = pointIndex || null
      this.shippingReadonly = false
      this.shippingSaving = false
      this.shippingForm = {
        receiverName: '',
        receiverPhone: '',
        province: '',
        city: '',
        district: '',
        address: '',
        saved: false
      }
      try {
        const res = await getShippingInfo({
          activityUserId: this.activityUserId,
          pointIndex
        })
        const info = res && res.data ? res.data : null
        if (info) {
          this.shippingForm = {
            receiverName: info.receiverName || '',
            receiverPhone: info.receiverPhone || '',
            province: info.province || '',
            city: info.city || '',
            district: info.district || '',
            address: info.address || '',
            saved: true
          }
          this.shippingReadonly = true
          this.clearShippingDraft(pointIndex)
        } else {
          const draft = this.loadShippingDraft(pointIndex)
          if (draft) {
            this.shippingForm = draft
          }
        }
      } catch (e) {
        this.shippingReadonly = false
        const draft = this.loadShippingDraft(pointIndex)
        if (draft) {
          this.shippingForm = draft
        }
      }
      this.syncShippingRegionFromForm()
      this.showShippingDialog = true
    },
    closeShippingDialog() {
      this.persistShippingDraft()
      this.showShippingDialog = false
    },
    async submitShippingInfo() {
      if (this.shippingReadonly) {
        uni.showToast({ title: '已保存，不能修改', icon: 'none' })
        return
      }
      const phone = (this.shippingForm.receiverPhone || '').trim()
      if (!(this.shippingForm.receiverName || '').trim()) {
        uni.showToast({ title: '请填写姓名', icon: 'none' })
        return
      }
      if (!/^1\d{10}$/.test(phone)) {
        uni.showToast({ title: '请填写正确手机号', icon: 'none' })
        return
      }
      if (!(this.shippingForm.province || '').trim() || !(this.shippingForm.city || '').trim() || !(this.shippingForm.district || '').trim()) {
        uni.showToast({ title: '请填写省市区', icon: 'none' })
        return
      }
      if (!(this.shippingForm.address || '').trim()) {
        uni.showToast({ title: '请填写详细地址', icon: 'none' })
        return
      }
      if (!this.activityUserId || !this.shippingHotspotIndex) {
        uni.showToast({ title: '活动记录或点位信息缺失', icon: 'none' })
        return
      }
      this.shippingSaving = true
      try {
        const res = await saveShippingInfo({
          activityUserId: this.activityUserId,
          pointIndex: this.shippingHotspotIndex,
          receiverName: (this.shippingForm.receiverName || '').trim(),
          receiverPhone: phone,
          province: (this.shippingForm.province || '').trim(),
          city: (this.shippingForm.city || '').trim(),
          district: (this.shippingForm.district || '').trim(),
          address: (this.shippingForm.address || '').trim()
        })
        if (res && res.status === 200) {
          this.shippingForm.saved = true
          this.shippingReadonly = true
          this.clearShippingDraft(this.shippingHotspotIndex)
          this.showShippingDialog = false
          uni.showToast({ title: '收货信息已保存', icon: 'success' })
          return
        }
        uni.showToast({ title: (res && res.msg) || '保存失败', icon: 'none' })
      } catch (e) {
        uni.showToast({ title: '保存失败，请重试', icon: 'none' })
      } finally {
        this.shippingSaving = false
      }
    },
    // 检查所有 checkType = CHECKIN 的热点是否均已成功打卡
    checkAllCheckinCompleted() {
      // 找出所有 checkType = 'CHECKIN' 且有 index 的热点
      const checkinHotspots = this.hotspots.filter(h => 
        h.checkType === 'CHECKIN' && h.index !== null && h.index !== undefined
      ) 
      // 如果没有打卡任务，返回 true（允许核销）
      if (checkinHotspots.length === 0) {
        return true
      }
      
      // 检查所有打卡热点的 index 是否都在已打卡列表中
      const allCompleted = checkinHotspots.every(hotspot =>
        this.isPointIndexChecked(hotspot.index)
      )
      
      console.log('打卡热点列表:', checkinHotspots.map(h => h.index))
      console.log('已打卡列表:', this.checkedPointIndexes)
      console.log('所有打卡任务是否完成:', allCompleted)
      
      return allCompleted
    },
    // 隐藏热点图片预览
    hideHotspotImagePreview() {
      this.clearLinkCheckinSuccessPopupTimer()
      this.linkCheckinSuccessPopupActive = false
      this.linkCheckinSuccessPopupBoxStyle = null
      this.showHotspotImagePreview = false
      this.previewHotspotImage = ''
      this.previewHotspotNestedHotspots = []
    },
    openHotspotRichTextDialog(hotspot) {
      const content = String((hotspot && hotspot.content) || '').trim()
      if (!content) {
        uni.showToast({ title: '该热点未配置富文本内容', icon: 'none' })
        return
      }
      this.hotspotRichTextTitle = String((hotspot && hotspot.name) || '').trim() || '活动详情'
      this.hotspotRichTextContent = content
      this.showHotspotRichTextDialog = true
    },
    closeHotspotRichTextDialog() {
      this.showHotspotRichTextDialog = false
      this.hotspotRichTextContent = ''
      this.hotspotRichTextTitle = '活动详情'
    },
    // 处理图片加载成功
    handleImageLoad(e) {
      console.log('图片加载成功:', e)
      console.log('图片元素:', e.target)
      const d = e && e.detail
      console.log('图片尺寸:', d && d.width, d && d.height)
    },
    // 处理图片加载错误
    handleImageError(e) {
      console.error('图片加载失败:', e)
      console.error('图片地址:', this.previewHotspotImage)
      uni.showToast({
        title: '图片加载失败',
        icon: 'none',
        duration: 2000
      })
    },
    resolveCheckInBackgroundImgForHotspot(hotspot) {
      if (!hotspot) return ''
      const mode = String(hotspot.checkInBackgroundMode || 'popup').trim().toLowerCase()
      if (mode === 'internal_page') {
        const idx = parseInt(hotspot.checkInBackgroundPageIndex, 10)
        const pages = Array.isArray(this.pages) ? this.pages : []
        const page = Number.isFinite(idx) && idx >= 0 && pages[idx] ? pages[idx] : null
        const pageBg = page && page.background ? String(page.background).trim() : ''
        if (pageBg) return this.getImageUrl(pageBg)
      }
      const hotspotBg = String(hotspot.checkInBackgroundImg || '').trim()
      if (hotspotBg) return this.getImageUrl(hotspotBg)
      const fallback = String(this.checkInBackgroundImgDefault || '').trim()
      return fallback ? this.getImageUrl(fallback) : ''
    },
    lockCheckInSessionContext(hotspot, pageOriginalIndex) {
      this.lockSealLoadingVisualFromHotspot(hotspot)
      const mode = String(hotspot && hotspot.checkInBackgroundMode || 'popup').trim().toLowerCase()
      this.checkInInternalPageModeLocked = mode === 'internal_page'
      const src = parseInt(pageOriginalIndex, 10)
      this.checkInSourcePageIndexLocked = Number.isFinite(src) && src >= 0
        ? src
        : this.currentPageIndex
      const areaOffset = parseInt(hotspot && hotspot.checkInAreaOffsetY, 10)
      this.checkInAreaOffsetYLocked = Number.isFinite(areaOffset) ? areaOffset : 0
      if (this.checkInInternalPageModeLocked) {
        const idx = parseInt(hotspot && hotspot.checkInBackgroundPageIndex, 10)
        this.checkInInternalPageIndexLocked =
          Number.isFinite(idx) && idx >= 0 ? idx : null
        this.checkInInternalBackgroundImgLocked = this.resolveCheckInBackgroundImgForHotspot(hotspot)
      } else {
        this.checkInInternalPageIndexLocked = null
        this.checkInInternalBackgroundImgLocked = ''
      }
      this.checkInInternalSealLoading = false
    },
    clearSealLoadingVisualLock() {
      this.sealLoadingCenterImgLocked = ''
      this.sealLoadingDurationMsLocked = null
    },
    clearCheckInSessionContext() {
      this.checkInInternalPageModeLocked = false
      this.checkInInternalBackgroundImgLocked = ''
      this.checkInInternalPageIndexLocked = null
      this.checkInSourcePageIndexLocked = null
      this.checkInInternalSealLoading = false
      this.checkInAreaOffsetYLocked = null
    },
    showcheckInBoxFun() {
      const idx = this.currentHotspotIndex
      if (idx != null && idx !== '') {
        const hs = this.findHotspotByPointIndex(idx)
        if (hs && !this.isAct004HotspotClickCheckinEnabled(hs)) {
          return
        }
      }
      // 打开打卡弹框前，重置手势缓存，避免复用上一次成功坐标
      this.lastTouchPoints = null
      this.lastValidFourPoints = null
      this.hasSubmittedThisGesture = false
      this.touchDebugCount = -1
      this.showcheckInBox = true
      this.isVerifying = false // 普通打卡
    },
    // 处理核销操作
    handleVerify() {
      // 核销必须要有 activityUserId
      if (!this.activityUserId) {
        uni.showToast({
          title: '用户活动记录不存在，无法核销',
          icon: 'none',
          duration: 2000
        })
        return
      }
      
      // 设置为核销模式，打开打卡弹框
      this.isVerifying = true
      // 打开核销弹框前，重置手势缓存，避免复用上一次成功坐标
      this.lastTouchPoints = null
      this.lastValidFourPoints = null
      this.hasSubmittedThisGesture = false
      this.touchDebugCount = -1
      this.showcheckInBox = true
    },
    // 处理overlay点击（只有点击卡片外的区域才关闭）
    handleOverlayClick() {
      // 只有点击overlay背景区域才关闭，点击卡片区域不关闭
      this.showcheckInBox = false
      this.clearCheckInSessionContext()
      this.clearSealLoadingVisualLock()
      // 关闭弹框时清理手势状态
      this.lastTouchPoints = null
      this.lastValidFourPoints = null
      this.hasSubmittedThisGesture = false
      this.touchDebugCount = -1
    },
    handleTouchStart(event) {
      const touchCount = event.touches ? event.touches.length : 0
      this.touchDebugCount = touchCount
      if (touchCount === 0 || this.checking) return

      const touches = []
      for (let i = 0; i < event.touches.length; i++) {
        const touch = event.touches[i]
        touches.push({
          clientX: touch.clientX !== undefined ? touch.clientX : (touch.pageX !== undefined ? touch.pageX : 0),
          clientY: touch.clientY !== undefined ? touch.clientY : (touch.pageY !== undefined ? touch.pageY : 0)
        })
      }

      const query = uni.createSelectorQuery().in(this)
      query.select('#checkinTouchArea').boundingClientRect((rect) => {
        if (!rect || rect.width === 0 || rect.height === 0) {
          if (touchCount === 4) {
            uni.showToast({ title: '无法获取打卡区域位置，请重试', icon: 'none', duration: 2000 })
          }
          return
        }

        const touchPoints = []
        for (let i = 0; i < touches.length; i++) {
          const touch = touches[i]
          const x = touch.clientX - rect.left
          const y = touch.clientY - rect.top
          const standardX = Math.round((x / rect.width) * this.standardWidth)
          const standardY = Math.round((y / rect.height) * this.standardHeight)
          touchPoints.push([standardX, standardY])
        }

        this.lastTouchPoints = touchPoints
        if (touchCount === 4) this.lastValidFourPoints = touchPoints

        if (touchCount === 4) {
          if (this.checking || this.hasSubmittedThisGesture) return
          this.hasSubmittedThisGesture = true
          this.doValidateSeal(touchPoints)
        }
      }).exec()
    },
    handleTouchMove(event) {
      const touchCount = event.touches ? event.touches.length : 0
      this.touchDebugCount = touchCount
      if (touchCount === 0 || this.checking) return

      const touches = []
      for (let i = 0; i < event.touches.length; i++) {
        const touch = event.touches[i]
        touches.push({
          clientX: touch.clientX !== undefined ? touch.clientX : (touch.pageX !== undefined ? touch.pageX : 0),
          clientY: touch.clientY !== undefined ? touch.clientY : (touch.pageY !== undefined ? touch.pageY : 0)
        })
      }
      const query = uni.createSelectorQuery().in(this)
      query.select('#checkinTouchArea').boundingClientRect((rect) => {
        if (!rect || rect.width === 0 || rect.height === 0) return
        const touchPoints = []
        for (let i = 0; i < touches.length; i++) {
          const touch = touches[i]
          const x = touch.clientX - rect.left
          const y = touch.clientY - rect.top
          const standardX = Math.round((x / rect.width) * this.standardWidth)
          const standardY = Math.round((y / rect.height) * this.standardHeight)
          touchPoints.push([standardX, standardY])
        }
        this.lastTouchPoints = touchPoints
        if (touchCount === 4) this.lastValidFourPoints = touchPoints

        if (touchCount === 4) {
          if (this.checking || this.hasSubmittedThisGesture) return
          this.hasSubmittedThisGesture = true
          this.doValidateSeal(touchPoints)
        }
      }).exec()
    },
    handleTouchEnd(event) {
      const touchCount = event.touches ? event.touches.length : 0
      if (touchCount === 0) {
        this.touchDebugCount = -1
        this.hasSubmittedThisGesture = false
        this._shakeLotteryVerifyGestureSubmitted = false
        // 注意：此处不再兜底提交上一次四点坐标，避免“点一下就成功”的误判
      }
    },
    collectTouchPointsFromSelector(event, selector, callback) {
      const touchCount = event.touches ? event.touches.length : 0
      if (touchCount === 0) return
      const touches = []
      for (let i = 0; i < event.touches.length; i++) {
        const touch = event.touches[i]
        touches.push({
          clientX: touch.clientX !== undefined ? touch.clientX : (touch.pageX !== undefined ? touch.pageX : 0),
          clientY: touch.clientY !== undefined ? touch.clientY : (touch.pageY !== undefined ? touch.pageY : 0)
        })
      }
      const query = uni.createSelectorQuery().in(this)
      query.select(selector).boundingClientRect((rect) => {
        if (!rect || rect.width === 0 || rect.height === 0) {
          if (touchCount === 4 && typeof callback === 'function') {
            callback(null, touchCount)
          }
          return
        }
        const touchPoints = []
        for (let i = 0; i < touches.length; i++) {
          const touch = touches[i]
          const x = touch.clientX - rect.left
          const y = touch.clientY - rect.top
          const standardX = Math.round((x / rect.width) * this.standardWidth)
          const standardY = Math.round((y / rect.height) * this.standardHeight)
          touchPoints.push([standardX, standardY])
        }
        if (typeof callback === 'function') {
          callback(touchPoints, touchCount)
        }
      }).exec()
    },
    handleShakeLotteryVerifyTouchStart(event, hotspot) {
      if (!this.isShakeHotspotPendingVerify(hotspot)) return
      const selector = '#shakeLotteryVerifyTouchArea-' + this.getShakeHotspotKey(hotspot)
      this.collectTouchPointsFromSelector(event, selector, (touchPoints, touchCount) => {
        if (touchCount !== 4 || !touchPoints) return
        if (this.shakeLotteryVerifyChecking || this._shakeLotteryVerifyGestureSubmitted) return
        this._shakeLotteryVerifyGestureSubmitted = true
        this.doVerifyShakeLotterySeal(hotspot, touchPoints)
      })
    },
    handleShakeLotteryVerifyTouchMove(event, hotspot) {
      this.handleShakeLotteryVerifyTouchStart(event, hotspot)
    },
    handleShakeLotteryVerifyTouchEnd(event) {
      const touchCount = event.touches ? event.touches.length : 0
      if (touchCount === 0) {
        this._shakeLotteryVerifyGestureSubmitted = false
      }
    },
    async doVerifyShakeLotterySeal(hotspot, touchPoints) {
      if (!hotspot || this.shakeLotteryVerifyChecking) return
      const ruleCode = String(
        hotspot.shakeVerifyRuleCode ||
        this.shakeLotteryVerifyRuleCodeByHotspotKey[this.getShakeHotspotKey(hotspot)] ||
        ''
      ).trim()
      if (!ruleCode) {
        uni.showToast({ title: '未配置核销规则', icon: 'none', duration: 2200 })
        return
      }
      if (!this.requireAuthStoreCodeForSeal()) return
      const key = this.getShakeHotspotKey(hotspot)
      const drawRecordId = parseInt(this.shakeLotteryDrawRecordIdByHotspotKey[key], 10)
      if (!Number.isFinite(drawRecordId) || drawRecordId <= 0) {
        uni.showToast({ title: '抽奖记录不存在，请刷新重试', icon: 'none', duration: 2200 })
        return
      }
      this.shakeLotteryVerifyChecking = true
      let res = null
      try {
        const uid = await this.ensureShakeLotteryAuthReady()
        if (!this.activityUserId) {
          await this.loadUserRecord()
        }
        res = await verifySignStoreLotterySeal({
          drawRecordId,
          userId: uid,
          ruleCode,
          touchPoints,
          activityUserId: this.activityUserId,
          authStoreCode: String(this.authStoreCode).trim()
        })
        if (isSignstoreUnauthorizedResponse(res)) {
          if (await this.recoverFromSignstoreUnauthorized(res)) {
            this._shakeLotteryVerifyGestureSubmitted = false
            return this.doVerifyShakeLotterySeal(hotspot, touchPoints)
          }
          const msg = getSignstoreUnauthorizedMessage(res) || '登录失效，请重试'
          uni.showToast({ title: msg, icon: 'none', duration: 2800 })
          return
        }
        if (!res || res.status !== 200 || !res.data) {
          const errMsg = this.extractShakeLotteryErrorMessage(null, res)
          uni.showToast({ title: errMsg, icon: 'none', duration: 2800 })
          return
        }
        this.applyShakeLotteryPrizeToHotspot(hotspot, res.data)
        this.$set(this.shakeLotteryVerifyStatusByHotspotKey, key, 1)
        uni.showToast({ title: '核销成功', icon: 'success', duration: 2000 })
      } catch (e) {
        const errMsg = this.extractShakeLotteryErrorMessage(e, res)
        uni.showToast({ title: errMsg, icon: 'none', duration: 2800 })
      } finally {
        this.shakeLotteryVerifyChecking = false
        this._shakeLotteryVerifyGestureSubmitted = false
      }
    },
    async doValidateSeal(touchArr) {
      if (this.checking) {
        return
      }

      let successHandled = false
      this.checking = true
      if (!this.checkInInternalPageModeLocked && this.isAct004SealLoadingVisualEnabled()) {
        this.load = true
      }

      try {
        // 检查 ruleCode 是否存在
        if (!this.ruleCode) {
          uni.showToast({
            title: '活动规则编码不存在，无法打卡',
            icon: 'none',
            duration: 2000
          })
          return
        }

        if (!this.requireAuthStoreCodeForSeal()) {
          return
        }

        // 如果 activityUserId 为空，尝试重新加载
        if (!this.activityUserId) {
          await this.loadUserRecord()
        }

        // 确保 activityUserId 存在，如果还是为空则提示错误
        if (!this.activityUserId) {
          uni.showToast({
            title: '用户活动记录不存在，无法打卡',
            icon: 'none',
            duration: 2000
          })
          return
        }

        // 调用打卡接口（ACT004专用入口）
        const validateData = {
          ruleCode: this.ruleCode,
          touchPoints: touchArr,
          activityUserId: this.activityUserId, // 传入用户活动参与记录ID（必填）
          operationType: this.isVerifying ? 'VERIFY' : 'CHECKIN', // 操作类型：CHECKIN-打卡，VERIFY-核销
          pointIndex: this.currentHotspotIndex || null, // 打卡点位序号
          authStoreCode: String(this.authStoreCode).trim()
        }
        const validateRes = await validateSealForAct004(validateData)

        const result = validateRes

        if (!result.matched) {
          // 打卡失败（验证不通过）- 不关闭弹框，让用户重试
          const message = result.errorMessage || result.message || '印章验证失败，请重试'
          uni.showToast({
            title: message,
            icon: 'none',
            duration: 2000
          })
          // 失败时不关闭弹框
          return
        }

        // ③ 校验通过：走打卡后置（音效/刷新/回调/收货；load 在 hotspotPostCheckInSuccess 里延时关）
        successHandled = true
        await this.hotspotPostCheckInSuccess(result)
      } catch (error) {
        uni.showToast({
          title: '打卡失败，请重试',
          icon: 'none',
          duration: 2000
        })
        // 失败时不关闭弹框，让用户重试
      } finally {
        if (!successHandled) {
          if (this.checkInInternalPageModeLocked) {
            this.checkInInternalSealLoading = false
          } else {
            this.load = false
          }
          this.clearSealLoadingVisualLock()
        }
        this.checking = false
      }
    },
	},
	beforeDestroy() {
		this.clearLinkCheckinSuccessPopupTimer()
		this.clearBgHoldSafetyTimer()
		this.clearMultiPageSnapTimer()
      this.clearImageScheduleAlarmTimers()
		this.teardownLinkCheckinZoneDocumentTouch()
		if (this._viewportResizeHandler && typeof window !== 'undefined') {
			window.removeEventListener('resize', this._viewportResizeHandler)
			this._viewportResizeHandler = null
		}
		// 销毁音频播放器，避免内存泄漏
		if (this.audioContext) {
			this.audioContext.destroy()
			this.audioContext = null
		}
	}

}
</script>

<style scoped="scoped" lang="less">
// 页面容器
.page-container {
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: #F5F5F5;
  padding: 0;
  margin: 0;
}

/* 多页纵向：整页占满可视高度，滚动区 flex 填满，避免固定 height(px) 露底 + 节与节之间断层 */
.page-container--act004-mp-scroll {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100vh;
  height: 100dvh;
}

.page-container--act004-mp-scroll > .act004-mp-scroll.act004-mp-scroll--h5 {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

/* 原内联样式：屏外挂载 audio，避免占布局 */
.activity-multi-audio-offscreen {
  position: fixed;
  left: 110%;
  top: 110%;
  width: 0;
  height: 0;
  overflow: hidden;
}

.act004-mp-scroll {
  width: 100%;
  box-sizing: border-box;
}

/* 原生 div 多页滚动：必须显式纵向滚动，否则内容在盒外不可见（白屏） */
.act004-mp-scroll.act004-mp-scroll--h5 {
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background-color: #f5f5f5;
  overscroll-behavior-y: contain;
  /* 纵向整页吸附：松手/惯性结束后对齐各节顶部，减少停在两节之间 */
  scroll-snap-type: y mandatory;
  scroll-padding-top: 0;
}

.act004-mp-section {
  width: 100%;
  box-sizing: border-box;
  position: relative;
  margin: 0;
  padding: 0;
  /* 与 page-container / 单页底图 letterbox 同色，节与节上下连贯 */
  background-color: #f5f5f5;
  scroll-snap-align: start;
  /* normal：单节高于一屏时允许在节内连续滚动；节与节之间仍由 mandatory + start 吸附 */
  scroll-snap-stop: normal;
}

// 组合背景整页
.composite-page-root {
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: #f5f5f5;
}

.composite-banner-img {
  width: 100%;
  height: auto;
  display: block;
  /* H5：无 uni rpx 运行时，勿依赖 opacity 渐显，否则易误判为白屏 */
  opacity: 1;
}

.composite-banner-img.hero-image-visible {
  opacity: 1;
}

.composite-rules-wrap {
  padding: 24rpx 32rpx 28rpx;
  background: #ffffff;
  font-size: 28rpx;
  color: #333333;
  line-height: 1.6;
}

/* u-parse 根在子组件内，须穿透 scoped，避免规则区富文本与外层样式脱节、大图撑版 */
.composite-rules-wrap /deep/ .u-parse-h5 {
  color: inherit;
  font-size: inherit;
  line-height: inherit;
}

.composite-rules-wrap /deep/ .u-parse-h5 img {
  max-width: 100%;
  height: auto;
  display: block;
}

.composite-rules-wrap /deep/ .u-parse-h5 table {
  max-width: 100%;
  display: block;
  overflow-x: auto;
}

.composite-gap-before-checkin {
  width: 100%;
  height: 20px;
  box-sizing: border-box;
}

// 打卡区整体：白底（与规则区间隔由上方 composite-gap-before-checkin 承担）
.composite-checkin-module {
  margin-top: 0;
  background: #ffffff;
  border-radius: 24rpx 24rpx 0 0;
  padding-bottom: 32rpx;
  overflow: hidden;
}

// 规则与打卡区之间的分隔：灰虚线 + 中间文案
.composite-divider-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12rpx 32rpx 16rpx;
  gap: 20rpx;
  box-sizing: border-box;
}

.composite-divider-line {
  flex: 1;
  height: 0;
  border-top: 1rpx dashed #c4c4c4;
}

.composite-divider-text {
  font-size: 24rpx;
  color: #9ca3af;
  letter-spacing: 2rpx;
  flex-shrink: 0;
}

// 组合模式下打卡层：与单背景共用坐标系；高度由内联 minHeight（compositeCheckinLayerHeightRpx）控制。
// 勿写 min-height:0!important，否则会压过内联 minHeight，层高塌陷，多行热点会被 composite-checkin-module 的 overflow 裁掉。
.composite-sign-layer.background-wrapper {
  position: relative;
  margin-top: 0;
  background: #ffffff;
  width: 100%;
  box-sizing: border-box;
}

.composite-sign-layer__bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  display: block;
  pointer-events: none;
  z-index: 0;
}

.act004-progress-bar-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}

.act004-progress-bar-host {
  position: absolute;
  overflow: hidden;
  /* 与 .hotspot-circle / .act004-bg-overlay-img / 控制台 .preview-hotspot 一致：left/top 为热点中心 */
  transform: translate(-50%, -50%);
}

.act004-bg-overlay-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.act004-bg-overlay-img {
  position: absolute;
  display: block;
  pointer-events: none;
  object-fit: fill;
  /* 与 .hotspot-circle 一致：left/top 为中心点 */
  transform: translate(-50%, -50%);
}

.act004-quiz-study-overlay-host {
  z-index: 60;
}

.act004-quiz-study-hit {
  -webkit-tap-highlight-color: transparent;
}

// 背景图容器（单背景整页打卡）
.background-wrapper:not(.composite-sign-layer) {
  position: relative;
  width: 100%;
  min-height: 1334rpx;
  /* 纯 Vue H5 未转换 rpx 时上一行无效，用视口高度兜底避免节高度塌成 0 */
  min-height: 100vh;
  background-color: #f5f5f5;
}

.background-image {
  width: 100%;
  display: block;
  opacity: 1;
}

.background-image.hero-image-visible {
  opacity: 1;
}

// 热点区域样式
.hotspot-circle--data {
  pointer-events: none !important;
  z-index: 6 !important;
  cursor: default;
}

.hotspot-data-text {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
  box-sizing: border-box;
  pointer-events: none;
}

.hotspot-data-text__inner {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hotspot-circle--full-image-hotzone {
  z-index: 2;
}

.hotspot-user-form-input {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  overflow: hidden;
  z-index: 2;
}
.hotspot-user-form-input__bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: fill;
  pointer-events: none;
  z-index: 0;
}
.hotspot-user-form-input__control {
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  min-width: 0;
  margin: 0;
  border: none;
  outline: none;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  background: transparent;
  color: #333;
  text-align: left;
  vertical-align: middle;
}
.hotspot-user-form-input__control::placeholder {
  color: rgba(0, 0, 0, 0.35);
}
.hotspot-user-form-input__control:focus {
  outline: none;
}
.hotspot-circle--act004-img-float {
  overflow: visible;
}

.hotspot-full-image-hit {
  width: 100%;
  height: 100%;
  opacity: 0;
}

/* 问答热点层：与 background-wrapper 同坐标系 */
.act004-quiz-overlay-host {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  overflow: visible;
  pointer-events: none;
}

.hotspot-circle {
  position: absolute;
  cursor: pointer;
  z-index: 10;
  transform: translate(-50%, -50%); // 居中定位
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  // 禁用点击高亮效果（去除蓝色背景闪烁）
  -webkit-tap-highlight-color: transparent;
  
  // 点击反馈效果
  &:active {
    transform: translate(-50%, -50%) scale(0.95);
    opacity: 0.8;
  }
}

/* 热点调试：config/app.js HOTSPOT_DEBUG_VISIBLE = true 时生效 */
.page-container--hotspot-debug {
  .hotspot-circle {
    background-color: rgba(255, 105, 135, 0.35) !important;
    border: 1px dashed rgba(255, 82, 122, 0.75) !important;
    box-sizing: border-box;
  }
  .act004-quiz-page-overlay__stem,
  .act004-quiz-page-overlay__option,
  .act004-quiz-page-overlay__next,
  .act004-quiz-img-opt-overlay__hit {
    background-color: rgba(255, 105, 135, 0.35) !important;
    border: 1px dashed rgba(255, 82, 122, 0.75) !important;
    box-sizing: border-box;
  }
  .act004-quiz-study-hit {
    background-color: rgba(255, 105, 135, 0.35) !important;
    border: 1px dashed rgba(255, 82, 122, 0.75) !important;
    box-sizing: border-box;
  }
  .shake-lottery-verify-seal-zone,
  .link-checkin-zone-touch-capture {
    opacity: 1 !important;
    background-color: rgba(255, 105, 135, 0.2) !important;
    border: 2px dashed rgba(255, 82, 122, 0.85) !important;
    box-sizing: border-box;
  }
}

.hotspot-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.hotspot-checked-badge-wrap {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  box-sizing: border-box;
  pointer-events: none;
  overflow: hidden;
}

.hotspot-checked-badge-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

// 标题区域
.title-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .h1 {
    font-size: 48rpx;
    font-weight: 600;
    color: #333333;
    text-align: center;
    margin-bottom: 16rpx;
    line-height: 1.4;
  }
  
  .sub {
    font-size: 28rpx;
    color: #666666;
    text-align: center;
    margin-bottom: 30rpx;
    line-height: 1.5;
  }
  
  .line {
    width: 60rpx;
    height: 4rpx;
    background: #4A90E2;
    margin-bottom: 30rpx;
    border-radius: 2rpx;
  }
  
  .des {
    font-size: 28rpx;
    color: #666666;
    line-height: 1.8;
    text-align: left;
    width: 100%;
    padding: 0 20rpx;
    white-space: pre-wrap;
    word-break: break-all;
  }
}

// 勋章列表
.medal-list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20rpx;
  padding: 40rpx 30rpx;
  background: #FFFFFF;
  margin-top: 20rpx;
  
  .item {
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
      width: 100rpx;
      height: 100rpx;
      object-fit: contain;
      display: block;
    }
  }
}

// 底部进度卡片
.dk-activity-total {
  display: flex;
  align-items: center;
  background: #F8F8F8;
  border-radius: 20rpx;
  padding: 30rpx;
  margin: 20rpx 30rpx 0;
  
  .icon {
    width: 100rpx;
    height: 100rpx;
    margin-right: 24rpx;
    flex-shrink: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 12rpx;
    }
  }
  
  .txt {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8rpx;
    
    div {
      font-size: 28rpx;
      color: #333333;
      line-height: 1.6;
      
      &:first-child {
        margin-bottom: 8rpx;
        font-weight: 500;
      }
      
      &:last-child {
        font-size: 26rpx;
        color: #666666;
        
        .b {
          font-weight: 600;
          color: #4A90E2;
          padding-left: 4rpx;
          padding-right: 4rpx;
        }
      }
    }
  }
  
  .link-arrow {
    width: 24rpx;
    height: 24rpx;
    border-right: 2rpx solid #CCCCCC;
    border-top: 2rpx solid #CCCCCC;
    transform: rotate(45deg);
    flex-shrink: 0;
  }
  
  .verify-action {
    margin-left: 20rpx;
  }
  
  .verify-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    padding: 16rpx 32rpx;
    border-radius: 40rpx;
    font-size: 28rpx;
    font-weight: 500;
    box-shadow: 0 4rpx 12rpx rgba(102, 126, 234, 0.3);
    transition: all 0.3s ease;
  }
  
  .verify-btn:active {
    transform: scale(0.95);
    box-shadow: 0 2rpx 8rpx rgba(102, 126, 234, 0.2);
  }
}

// 工具类
.fcc-h {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.fcc {
  display: flex;
  align-items: center;
  justify-content: center;
}

.fc-bet {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.flex1 {
  flex: 1;
}

// 页面加载完成后的动画
.page-loaded {
  .banne-img,
  .p030,
  .medal-list,
  .dk-activity-total {
    opacity: 0;
    transform: translateY(30rpx);
    transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
}

.fade-in-up {
  opacity: 0;
  transform: translateY(30rpx);
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
  
  &.delay-1 {
    transition-delay: 0.1s;
  }
  
  &.delay-2 {
    transition-delay: 0.2s;
  }
  
  &.delay-3 {
    transition-delay: 0.3s;
  }
}

// 勋章动画
.medal-list {
  .item {
    opacity: 0;
    transform: scale(0.8);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    
    .medal-img {
      transition: transform 0.3s ease;
    }
    
    &:active .medal-img {
      transform: scale(0.95);
    }
  }
  
  &.animate-in .item {
    opacity: 1;
    transform: scale(1);
  }
}

// 加载中动画 - 优化版
.loading-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  position: relative;
  width: 200rpx;
  height: 200rpx;
  margin-bottom: 40rpx;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 6rpx solid transparent;
  border-top-color: rgba(255, 255, 255, 0.8);
  border-right-color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  animation: spinner-rotate 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  
  &:nth-child(1) {
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 80%;
    height: 80%;
    top: 10%;
    left: 10%;
    border-top-color: rgba(255, 255, 255, 0.6);
    border-right-color: rgba(255, 255, 255, 0.3);
    animation-delay: 0.15s;
    animation-duration: 1s;
  }
  
  &:nth-child(3) {
    width: 60%;
    height: 60%;
    top: 20%;
    left: 20%;
    border-top-color: rgba(255, 255, 255, 0.4);
    border-right-color: rgba(255, 255, 255, 0.2);
    animation-delay: 0.3s;
    animation-duration: 0.8s;
  }
}

.spinner-logo {
  position: absolute;
  width: 100rpx;
  height: 100rpx;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: logo-pulse 2s ease-in-out infinite;
  
  image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
}

@keyframes spinner-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes logo-pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.8;
  }
}

// 打卡加载动画 - 优化版
.seal-loading {
  width: 400rpx;
  height: 400rpx;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.seal-circle-outer {
  position: absolute;
  width: 100%;
  height: 100%;
  animation: seal-rotate 3s linear infinite;
  
  .circle {
    width: 100%;
    height: 100%;
    animation: seal-scale 2s ease-in-out infinite;
  }
}

.seal-logo-inner {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  padding: 3%;
  box-sizing: border-box;
  z-index: 2;
  overflow: visible;
  animation: logo-bounce 1.5s ease-in-out infinite;
  
  img {
    width: 100%;
    height: 100%;
    display: block;
    overflow: hidden;
    border-radius: 50%;
    object-fit: cover;
  }
}

@keyframes seal-rotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes seal-scale {
  0%, 100% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
}

@keyframes logo-bounce {
  0%, 100% {
    transform: scale(1) translateY(0);
  }
  50% {
    transform: scale(1.05) translateY(-10rpx);
  }
}

// 打卡盖章提示 overlay 样式
.checkin-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10030;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkin-pop {
  width: 680rpx;
  max-width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

/* Canvas容器，完全按照H5的方式，等比例放大 */
.canvas-container {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 32rpx;
  padding: 20rpx;
  box-shadow: 0 8rpx 32rpx rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: card-fade-in 0.3s ease-out;
  width: 100%;
}

.canvas-container--with-bg {
  padding: 0;
  background: transparent;
  border-radius: 24rpx;
}

.checkin-popup-bg {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 24rpx;
}

.canvas-container--with-bg .checkin-popup-bg {
  position: relative;
  z-index: 1;
}

.canvas-container--with-bg .canvas-wrapper {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}

.checkin-overlay--internal-page {
  align-items: stretch;
  justify-content: stretch;
  padding: 0;
}

.checkin-internal-page {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.checkin-internal-page-body {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.checkin-internal-page-bg {
  width: 100%;
  height: auto;
  display: block;
}

.checkin-internal-page-hotspots {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.checkin-internal-page-hotspot {
  position: absolute;
  pointer-events: auto;
  cursor: pointer;
  z-index: 1;
}

.checkin-internal-page-stage {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
}

.checkin-internal-page-stage .canvas-wrapper {
  pointer-events: auto;
}

.checkin-internal-page-stage .checkin-hint {
  pointer-events: auto;
}

.checkin-internal-seal-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
}

.checkin-hint--internal-page {
  color: #ffffff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}

.canvas-container--with-bg .checkin-hint {
  position: relative;
  z-index: 3;
  margin-top: 16rpx;
}

.canvas-wrapper {
  position: relative;
  width: 600rpx;
  height: 600rpx;
  z-index: 2;
}

.canvas-wrapper--with-bg {
  border: 2rpx dashed rgba(255, 255, 255, 0.95);
  border-radius: 20rpx;
  box-shadow: 0 0 0 2rpx rgba(0, 0, 0, 0.08) inset;
}

.canvas-wrapper--with-bg .checkin-canvas {
  border: none;
  background: transparent;
  box-shadow: none;
}

.checkin-canvas {
  border: 2px solid #ddd;
  border-radius: 16rpx;
  background: #f5f5f5;
  box-shadow: 0 4rpx 16rpx rgba(0, 0, 0, 0.1);
  display: block;
}

.touch-capture-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  display: block;
  -webkit-tap-highlight-color: transparent;
}

.checkin-hint {
  font-size: 28rpx;
  color: #666666;
  margin-top: 20rpx;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 18rpx;
  
  .checkin-error {
    color: #ff4444;
    font-size: 24rpx;
    margin-left: 8rpx;
    font-weight: 500;
  }
}

.checkin-nav-link {
  font-size: 26rpx;
  color: #2563eb;
  font-weight: 500;
  padding: 2rpx 10rpx;
  border: 1rpx solid rgba(37, 99, 235, 0.45);
  border-radius: 12rpx;
}

@keyframes card-fade-in {
  0% {
    opacity: 0;
    transform: translateY(40rpx) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.ljsy{
  padding-left: 6rpx;
  color: #317bcf;
}

// 加载中 overlay 样式
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.loading-text {
  color: #FFFFFF;
  font-size: 28rpx;
  font-weight: 300;
  letter-spacing: 2rpx;
  animation: text-fade 1.5s ease-in-out infinite;
}

@keyframes text-fade {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.shake-lottery-drawing-hint {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 10005;
  padding: 16rpx 36rpx;
  border-radius: 999rpx;
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  font-size: 28rpx;
  pointer-events: none;
}

.shake-lottery-drawing-hint__text {
  display: block;
  white-space: nowrap;
}

.act004-shake-lottery-verify-layer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 24;
  overflow: visible;
}

.shake-lottery-verify-seal-host {
  pointer-events: auto;
}

.shake-lottery-verify-seal-zone {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  opacity: 0;
  touch-action: none;
  border-radius: 10px;
}

.hotspot-circle--blocked-by-link-zone {
  pointer-events: none !important;
  cursor: default;
}

.act004-link-checkin-zone-layer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 55;
  overflow: visible;
  touch-action: none;
}

.link-checkin-zone-seal-host {
  pointer-events: auto;
  z-index: 55;
  touch-action: none;
}

/* 联动打卡热区：透明触摸层，四指盖章触发 */
.link-checkin-zone-touch-capture {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-width: 40px;
  min-height: 40px;
  box-sizing: border-box;
  z-index: 1;
  display: block;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
  background: transparent;
  border: none;
}

// 热点图片预览 overlay 样式
.hotspot-image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fade-in 0.3s ease-out;
}

.hotspot-image-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 24rpx;
  box-sizing: border-box;
  position: relative;
}

.hotspot-preview-image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 680rpx;
  max-width: calc(100vw - 48rpx);
  position: relative;
  z-index: 10002;
}

.hotspot-preview-image-wrapper--link-success {
  /* 不设 width/height，由内联 style（rpx→px）决定矩形尺寸 */
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 40px);
  box-sizing: border-box;
  overflow: hidden;
}

.hotspot-preview-image {
  width: 100%;
  max-width: calc(100vw - 48rpx);
  max-height: calc(100vh - 80rpx);
  // 无背景和边框
  background: transparent;
  border: none;
  // 确保图片可见
  opacity: 1 !important;
  visibility: visible !important;
  position: relative;
  z-index: 10003;
  // 确保图片能正确显示
  display: block;
}

.hotspot-preview-image--link-success {
  width: 100% !important;
  height: 100% !important;
  max-width: 100% !important;
  max-height: 100% !important;
  object-fit: contain;
}

.hotspot-preview-nested {
  position: absolute;
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.12);
  z-index: 10004;
}

.hotspot-preview-nested--checkinhotspot {
  border: 0;
  background: transparent;
}

.preview-placeholder-text {
  color: #fff;
  font-size: 28rpx;
  text-align: center;
}

.hotspot-richtext-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10015;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32rpx;
  box-sizing: border-box;
}

.hotspot-richtext-panel {
  width: 100%;
  max-height: 80vh;
  background: #fff;
  border-radius: 20rpx;
  padding: 24rpx;
  box-sizing: border-box;
}

.hotspot-richtext-title {
  font-size: 32rpx;
  color: #111827;
  font-weight: 600;
}

.hotspot-richtext-content-wrap {
  margin-top: 16rpx;
  max-height: 56vh;
}

/* 纯 H5：scroll-view 无效，用 div + overflow 与 activity3-1 的「可滚动区域」思路一致 */
.hotspot-richtext-content-wrap--h5 {
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
}

.hotspot-richtext-content-wrap--h5 /deep/ .u-parse-h5 img {
  max-width: 100%;
  height: auto;
  display: block;
}

.hotspot-richtext-actions {
  margin-top: 20rpx;
  display: flex;
  justify-content: flex-end;
}

.hotspot-richtext-btn {
  min-width: 180rpx;
  height: 72rpx;
  line-height: 72rpx;
  border-radius: 10rpx;
  background: #2563eb;
  color: #fff;
  font-size: 28rpx;
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.shipping-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32rpx;
  box-sizing: border-box;
}

.shipping-dialog-panel {
  width: 100%;
  background: #fff;
  border-radius: 24rpx;
  padding: 28rpx;
  box-sizing: border-box;
}

.shipping-dialog-title {
  font-size: 34rpx;
  color: #222;
  font-weight: 600;
}

.shipping-dialog-desc {
  margin-top: 10rpx;
  margin-bottom: 18rpx;
  color: #666;
  font-size: 24rpx;
}

.shipping-input {
  width: 100%;
  height: 78rpx;
  border: 1px solid #e5e7eb;
  border-radius: 12rpx;
  padding: 0 20rpx;
  box-sizing: border-box;
  font-size: 28rpx;
  margin-top: 14rpx;
}

.shipping-region-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 14rpx;
}

.shipping-region-part {
  flex: 1;
  min-width: 0;
  margin-top: 0;
}

.shipping-picker {
  width: 100%;
  height: 78rpx;
  border: 1px solid #e5e7eb;
  border-radius: 12rpx;
  padding: 0 20rpx;
  box-sizing: border-box;
  font-size: 28rpx;
  margin-top: 14rpx;
  line-height: 78rpx;
  color: #111827;
  background: #fff;
}

.shipping-picker-disabled {
  background: #f3f4f6;
  color: #6b7280;
}

.shipping-textarea {
  width: 100%;
  min-height: 150rpx;
  border: 1px solid #e5e7eb;
  border-radius: 12rpx;
  padding: 16rpx 20rpx;
  box-sizing: border-box;
  font-size: 28rpx;
  margin-top: 14rpx;
}

.shipping-actions {
  display: flex;
  gap: 16rpx;
  margin-top: 24rpx;
}

.shipping-readonly-tip {
  margin-top: 14rpx;
  color: #b45309;
  font-size: 24rpx;
  background: #fffbeb;
  border-radius: 10rpx;
  padding: 12rpx 14rpx;
}

.shipping-btn {
  flex: 1;
  font-size: 28rpx;
  border-radius: 12rpx;
  height: 80rpx;
  line-height: 80rpx;
}

.shipping-btn-cancel {
  background: #f3f4f6;
  color: #374151;
}

.shipping-btn-confirm {
  background: #2563eb;
  color: #fff;
}

.shipping-btn-disabled {
  background: #9ca3af !important;
  color: #fff !important;
}
</style>

<style lang="less">
/* 非 scoped：与内联 animation 名称一致 */
@keyframes act004FloatY {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(0, calc(-1 * var(--act004-fr, 0px)), 0);
  }
}
@keyframes act004FloatX {
  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }
  50% {
    transform: translate3d(calc(-1 * var(--act004-fr, 0px)), 0, 0);
  }
}
.act004-img-float-host {
  flex-shrink: 0;
}
</style>
